/**
 * Terrain surface shading.
 *
 * OWNER: piece:terrain-shading
 *
 * The clipmap supplies position, an exact analytic normal, and a per-vertex base
 * colour keyed to the surface type. Everything that makes the ground read as ground —
 * detail at 3 m, landform at 300 m, no visible tiling at either — happens here.
 *
 * Approach: MeshStandardMaterial patched through onBeforeCompile rather than a raw
 * ShaderMaterial, so we keep three's shadow receiving, IBL/hemisphere ambient, fog and
 * ACES tone mapping for free and only replace the four chunks we actually care about:
 * albedo, roughness, normal, and the varyings that feed them.
 *
 * ---------------------------------------------------------------------------
 * ROUND 3: why the ground read as brushed suede, and what replaced it.
 *
 * R2 fixed the "smooth putty" problem by making the detail screen-locked — pick the
 * noise octave whose period lands on a few pixels and cross-fade like a mip chain, so
 * grain is present at every distance. That worked, and it produced a *new* failure:
 * the whole near and middle field became one continuous painted mottle. Screen-locked
 * noise has no fixed world scale, so it contains no objects; it is a field of value,
 * and a field of value seen at a grazing angle — which is every angle you actually ride
 * at — is filtered hard along the view axis and not at all across it. The result is a
 * coherent directional smear. Fur. Suede. Never ground.
 *
 * The fix is not more noise. It is that ground is made of *things*:
 *
 *   DISCRETE CLASTS. Four fixed-world-scale layers of individual objects — 20-95 cm
 *   buried knobs, 8-26 cm stones, 3-9 cm clods, 1-3 cm grains — each one a squashed,
 *   rotated dome with its own radius, proudness, albedo and jittered position. Not a
 *   level set of a noise field: an object, found with an exact 2x2 cell sweep so it is
 *   seam free and costs four hashes. Their Nyquist ranges overlap and hand off, so
 *   there is no distance at which the ground has nothing in it: grains to ~10 m, clods
 *   to ~45 m, stones to ~45-135 m depending on the view angle, knobs to ~150 m, and
 *   only past that does the macro field take over as pure landform.
 *
 *   FOUR CUES PER OBJECT, not one. A pattern flattens under foreshortening; an object
 *   with its own lighting does not. Every clast gets
 *     - a real dome normal, so the sun-facing flank is lit and the far flank is dark,
 *     - a lit crown, from per-object albedo and sky visibility at the top of the dome,
 *     - a dark contact crease where soil banks against its base — light-independent, so
 *       it holds up in flat overcast where a normal map gives you nothing,
 *     - a cast contact shadow, traced one tap up-sun through the clast height field.
 *   The last one is the cue a normal map fundamentally cannot fake, and it is what makes
 *   a 4-pixel ellipse at 40 m still read as a stone lying on dirt.
 *
 *   THE MOTTLE DEMOTED. The screen-locked layers survive, at roughly half their R2
 *   contrast, doing the job they are actually good at: keeping a grain at the pixel
 *   scale between the objects, and turning into landform on its own as distance grows.
 * ---------------------------------------------------------------------------
 * WAVE 2: the mid distance is now read from SHAPE, not from noise amplitude.
 *
 * R3 fixed 0-15 m and left everything past it as a low-contrast brown wash: once the
 * clast layers die at Nyquist the only thing out there was edge-less isotropic value
 * noise, further flattened by the graze clamp. The art direction changed at the same
 * time — stylized-grounded, form and light carrying the image — and under that target
 * "add another octave" is explicitly the wrong move. So the fix is not smaller detail,
 * it is bigger *form*, at 3-30 m, hard-edged, and anchored to the hill:
 *
 *   CLIFF BANDS. Bedding is roughly iso-elevation, so a fract() of a warped altitude
 *   gives contour-following structure — the one pattern that reads as landform rather
 *   than as texture, because it bends with the terrain and tells you which way is down.
 *   Every band carries a whole little section: a bare rock riser, a hard shadow under
 *   its lip, a talus apron below that ending in a lobate toe, and a bench under the toe
 *   carrying a different cover. Four hard edges and three value steps per 19-34 m of
 *   elevation, and the riser tips the shading normal so the sun does the rest.
 *
 *   DRAINAGE. A noise field sampled in the local fall-line frame and stretched 4:1
 *   downhill, ridged to a level set so it has two hard edges and a floor instead of a
 *   gradient. It runs the way water runs, incises with a real normal, and carries damp.
 *
 *   A PALETTE THAT SEPARATES BEFORE DETAIL RESOLVES. The seven surfaces used to land
 *   within 1.4:1 of each other in value once their detail layers faded — rock and
 *   hardpack were within 4%, which is why the mountain converged to one brown. They are
 *   now spread over 4:1 with distinct hue families: cool light rock, warm pale scree,
 *   orange clay hardpack, near-neutral dark loam, green grass. The landform-scale value
 *   wash that used to swamp that separation is cut from +-24% to +-12%.
 *
 * None of the structure is faded by the graze clamp and none of it dies at Nyquist
 * inside the far plane: a 4 m channel at 500 m is still a dozen pixels across. It also
 * stays out of the near field — bands and channels ramp in over 12-45 m — so the R3
 * clast work is untouched by all of it.
 * ---------------------------------------------------------------------------
 * WAVE 3: this file only ever owned ALBEDO, and albedo was the wrong axis.
 *
 * Six rounds of competent work on colour, and the two things that kept coming back are
 * both about light, not about paint. `src/render/shading/relief.ts` is the shared library
 * built for exactly that, and this round adopts it. Four changes, in order of how much
 * they move the image:
 *
 *   1. COVER PATCHES ARE NO LONGER A LEVEL SET. `coverP` was
 *      `smoothstep(0.47 - fwidth(pf), 0.47 + fwidth(pf), pf)` — a boundary exactly one
 *      pixel wide *by construction*, so it could never be a material edge, with no
 *      thickness and no contact, swapping 56% of the mineral weights into grass and loam
 *      across that one pixel. It is now `rlfCoverBlend`: an art-width feather measured in
 *      metres of ground, a margin broken into fingers by an fBm that fades at its own
 *      Nyquist limit, a real depth in metres whose rim catches the sun through
 *      `rlfBumpH`, and an occlusion band in the ground banked against that rim. Plus the
 *      part that is ours rather than the library's — A MAT IS THINNER THAN A STONE. The
 *      clast layers already carry an exact height in metres, so anything standing proud
 *      of the duff pushes the cover weight back down and the clast field survives the
 *      boundary instead of dying on it.
 *
 *   2. OCCLUSION NOW SCALES THE IRRADIANCE, not the albedo. Everything this shader knew
 *      about contact — the clast creases, the micro cavity, the gully floors, the
 *      undercut at the foot of a riser, the rim of a cover patch — was a multiply on
 *      `diffuseColor`, which is indistinguishable from painting the ground darker and
 *      does nothing whatsoever to how the surface responds to the sky. It is now split:
 *      roughly a third stays on albedo (a crease really does collect dark fines) and the
 *      rest goes to `gRlfOcc`, which `RELIEF_STD_AO_PATCH` puts on `indirectDiffuse` in
 *      full and on `directDiffuse` at 65%. Under a clear sky the total darkening is
 *      within a couple of per cent of where it was; under overcast — where a normal map
 *      gives you nothing at all — it is now the thing holding the ground together.
 *
 *   3. THE MID-SCALE FORM WAS THREE CENTIMETRES TALL. The metre-scale fBm was in the bump
 *      stack at `gradMid * 0.34 * bumpAmt * 1.1`, which works out to an RMS slope near
 *      0.017 — one degree — i.e. 3 cm of relief at a 6 m period. That is a rounding
 *      error, and it is the whole reason everything past 30 m had to be carried by flat
 *      albedo marks: there was no form out there for the sun to find. The clipmap
 *      tessellates at 0.5 m inside 32 m, 2 m at 128 m and 8 m at 512 m, so a 6-8 m
 *      undulation is in the geometry near and gone by ~150 m. It is now put back as a
 *      shading height field over exactly that range, ramped in past 10 m so the near
 *      field is untouched, and faded on the library's own Nyquist and far-fade policy —
 *      minus the graze clamp, which is a pixel-scale correction and demonstrably wrong for
 *      a six-metre form at 300 m. A second, coarser band at 24 m covers 45-400 m, where
 *      the 6 m field is genuinely at its sampling limit and the clipmap has decimated the
 *      geometry's own structure away.
 *
 *   4. THE SUN AND THE LOD POLICY ARE NOW SHARED. `rlfEnv().sunW` instead of digging the
 *      first directional light out of view space, and `rlfLodAt` / `rlfBandLod` /
 *      `rlfReliefAmp` instead of a private copy of the same constants. The numbers are
 *      identical — `ReliefLod` was lifted from this file — so this is a no-op today and a
 *      guarantee that it stays one.
 * ---------------------------------------------------------------------------
 * WAVE 4 ROUND 3: THE TREAD THAT READS AS TARMAC IS NOT DRAWN BY THIS FILE.
 *
 * Carried gap, verbatim: "The ridden line does not read as dirt. Measured on untracked,
 * sunlit tread vs the ground 2 m beside it: tread L 0.334-0.377, hue 23-25 deg, sat
 * 0.108-0.112; ground L 0.317-0.331, hue 29-35 deg, sat 0.182-0.320... The cause is this
 * round's own change: sweeping treadBlock/Stone/Clod 0.42/0.34/0.40 -> 0.14/0.16/0.28
 * while keeping treadGrain at 0.72 leaves the tread's albedo dominated by mix(cHard,
 * gStoneTint(vec3(0.214,0.196,0.172), ...), grMask*0.72) — a NEUTRAL grey grain tint at
 * 72%."
 *
 * The OBSERVATION is exact and reproduces: on the critic's own frame
 * (`w4crit-terrain-shading-r2-fullrun/fullrun_130p0s.png`) the tread measures HSV sat
 * 0.144 / 0.164 at hue 12-14 against ground at 0.436 / 0.451 at hue 26. The ATTRIBUTION
 * is wrong, and it took an instrument to show it, because the named line is in this file
 * and the pixels are not.
 *
 * `src/world/trails.ts` draws a tread RIBBON — its own mesh, its own material
 * (`makeTreadMaterial`), its own vertex colours (`TREAD_V_DARK` 0.119 with `HUE_DARK`
 * [1.0, 0.812, 0.660], a deliberately near-neutral "damp compacted" line) and its own
 * clast field with its own crease — over the carve. So the measurement to make is the
 * same population with that mesh hidden. `captures/w4ts-tread.mjs` does it inside one page
 * session (`__gripTrails.tread.visible`), on stations found through the trail network's
 * own queries so it survives the trail moving under this piece:
 *
 *     delivered tread (ribbon on)   sat 0.125   hue 11.3   L 0.261
 *     the carve underneath          sat 0.336   hue 22.7   L 0.218
 *     ground 2 m beside             sat 0.262   hue 28.8   L 0.363
 *
 * The ribbon accounts for ALL of the chroma collapse and reverses the hue. This file's
 * substrate under the line is already warm clay at 0.34 — MORE saturated than the ground
 * beside it, which is the direction the reference asks for. The same instrument's view-9
 * dump also shows the terrain false-colour output is completely hidden under the ribbon in
 * the near field, i.e. the ribbon is effectively opaque there: none of the tread's near
 * colour is ours. And the hollow rings the gap reports "on the wet tread" are the RIBBON'S
 * crease (`trails.ts`, `albedo *= 1 + crown*0.16 - crease*0.30 - contact*0.34`), visible
 * at 2x in `captures/w4ts-r3-wetvista/wetvista_5p5s.png` on the ribbon's own polygons while
 * the terrain beside them carries solid domes.
 *
 * Three things went in anyway, because two of them are the half of the gap that IS here.
 *
 *   1. THE GRIT ON A CLAY LINE IS CLAY-COLOURED GRIT. The four clast tints on hardpack and
 *      the mineral fleck were neutral greys (sat 0.20) mixed into the one saturated member
 *      of the palette at 72/52/34/30%. They are now warm-and-dusty at the grain end and
 *      mineral at the block end, EACH AT ITS OLD LUMA to three decimals, so this is a pure
 *      chroma change and no calibration in the palette block moves. Measured honestly it is
 *      small: +1.8% of blue-yellow chroma on the nearest ground and nothing past ~45 m,
 *      because grMask/clMask carry their own Nyquist fade and are dead by then. It is
 *      correct, it is not the fix, and the gap's stated mechanism is not live at the
 *      distances the gap was measured at.
 *
 *   2. THE RING NOW LEAVES WHEN THE OBJECT IS FLUSH. Round 2 made every cue read the same
 *      entitlement, which fixed the arithmetic and not the read: a crease is a two-pixel
 *      ANNULUS and the body is the DISC, so at equal integrals and an entitlement of 0.16
 *      the outline is still visible and the solid inside it is 1% of a value step. The
 *      crease and the body — the mean-preserving pair — now go through
 *      e * (0.35 + 0.65 * e): identity at e = 1 so every fully entitled surface is
 *      bit-for-bit unchanged, and hardpack's 8-95 cm members drop from 0.14/0.16/0.28 to
 *      0.06/0.07/0.15. Measured in view 9 over four stations, crease/solid on the tread
 *      footprint falls 0.862 -> 0.806, 0.929 -> 0.879, 0.874 -> 0.834, 0.805 -> 0.734,
 *      while the whole near band moves 1%. That shape — all of the change on the surfaces
 *      entitled down, none on the rest — is the point.
 *
 *   3. A FOURTH LANDFORM SCALE, 300 m. Round 9's own note said the far field's remaining
 *      defect is that its structure is ONE spatial frequency, "a comb", where the reference
 *      reads from a few large masses. So: 24 m, 90 m, 300 m, each ~3.3x apart, the new one
 *      at the same 0.20 slope ratio the 90 m band uses, driving the landform light, the
 *      shading normal, the sky term AND `formPos` (weighted 1.35, so a whole flank lands on
 *      one side of the hollow/spur material split instead of the coarse term being averaged
 *      away by the two finer ones). Measured on the station with the most far ground in
 *      frame: far-population standard deviation 33.5 with it against 30.8 without, +8.9%,
 *      p95/p5 +2.5%, mean unchanged to 1.8%.
 *
 * AND THE MEASUREMENT THAT OUTRANKS ALL THREE. The distance census (view 4) now run on SIX
 * poses — four found on four different runs plus the `rockcam` and `floorcam` stands —
 * says the same thing every time: 53-80% of every frame is terrain INSIDE 100 m, and the
 * 100-500 m band is 0.1-9.1% of it. Even the frame that looks across a whole valley reads
 * 1.9% at 100-200 m and 0.05% at 200-300, then jumps to 3.6% past 500 m. Round 8 found this
 * on one pose and it was reasonable to wonder whether that pose was unlucky. It is not the
 * pose. Three waves of critique about "100 to 500 m" are about a band that is a few per
 * cent of the image, and no material change inside this file can be worth more than a few
 * per cent of a frame there. The far field is a framing and heightfield problem before it
 * is a shading one — see the report.
 * ---------------------------------------------------------------------------
 * WAVE 4 ROUND 9: THE CLAST LAYER'S TWO FAILURES WERE ONE ARITHMETIC BUG.
 *
 * Carried gap, verbatim: "The block-clast layer is the defect at both ends: it degenerates
 * into hollow dark elliptical outlines with featureless interiors across near and mid
 * ground (including on the compacted built tread, where nothing is scattered), and it
 * aliases into a hard regular chain-mail lattice from roughly 150-500 m instead of dying
 * at Nyquist."
 *
 * Both ends turned out to be the same fault: **the five cues an object owns were each
 * gated by a different product of terms.** Wave 2 found one instance (a cast shadow
 * running at 100% behind a caster at 16%) and fixed it for the shadow alone. The rest was
 * still live, and the worst place for it is the crease, because the crease is a RING.
 *
 *   NEAR AND MID. `crease` was a scalar SUM over four layers, and once four layers are
 *   summed the result cannot be entitled per surface. Everything else an object owns was:
 *   the dome relief through bkRel/stRel/clRel/grRel, the tint through the per-surface mix
 *   weights. So on the built tread hardpack took 42% of the block layer's relief, 30% of
 *   its tint — and 100% of its crease. The tread was covered in rings with nothing inside
 *   them, which is the critique word for word. The three cue sums are now vec4s carried to
 *   the point of use and entitled there with one dot product against `relV`; the crease
 *   fades as lod SQUARED like the dome rather than as lod like an albedo mark; and
 *   TerrainClast.tread* takes hardpack's 8-95 cm entitlement to a seventh and a fifth of
 *   what it was, leaving the grains, because grit is what a compacted line is made of.
 *
 *   AND THE RING NOW HAS AN INSIDE. Integrated over the disc the crease covers 0.463 of
 *   R^2 and the crown covers 0.49 — matched — but `crown` is the middle 37% of the RADIUS,
 *   so the flank between the crown and the ring carried no light-independent cue at all.
 *   In sun the dome normal fills it; in shade, and on any surface entitled down, nothing
 *   did. `clastBody` is the whole disc on the ambient channel, its coefficients set to the
 *   crease coefficients times 0.463/0.80 so the pair is mean-preserving per layer, with
 *   TerrainAmbient.crown cut 0.8 -> 0.34 to pay for it at constant total.
 *
 *   FAR. `lodBlock` was `rlfBandLod(L, F_BLOCK)` — the Nyquist limit of the layer's CELL
 *   LATTICE. A cell is mostly empty; the thing with an edge in it is the clast, 0.38 of a
 *   cell across. At F_BLOCK the cell criterion reaches zero at a footprint of 0.656 m/px,
 *   where a 45 cm block is 0.69 PIXELS WIDE. On grazing ground the anisotropy budget
 *   masks that (L.foot is ~2x the geometric-mean footprint and the frequency ratio is
 *   2.63, so the two nearly cancel) — but a far hillside is seen FACE-ON, and there the
 *   layer was being drawn at 43% strength at 400 m with its objects under one pixel. That
 *   is not small stones, it is the beat between a square object lattice and the pixel
 *   grid: regular, hard, and exactly the chain-mail. `gClastRes` states the criterion once,
 *   in pixels of object, against the geometric-mean footprint; the mask edge also softens
 *   as the object runs out of pixels, which is the prefilter the hard edge never had.
 *
 * MEASURED, in one page session on one latched pose (scenario `clastab`, appended this
 * round, whose shot 24s is the distance-ring dump proving the frame holds ground from 30 m
 * to past 500 m). Shot 8s all on, 12s the rings alone off, 16s the whole field off, 28s
 * all on again as a leak check. That leak check is the measurement floor and it is not
 * zero — 0.39% of mean luminance, because scatter and cloud move between shots — so:
 *   near-field ground   rings alone   0.40% mean |dL|  (i.e. AT the floor)
 *                       whole field   1.78% mean |dL|  (4.5x the floor)
 *                       whole field   +0.83% mean dL when removed, so the object field
 *                                     darkens the ground it lies on by under one per cent
 * Before this round the ring was the only cue on the tread at all. Shot 20s is view 9, the
 * object channels — red crease, green crown+body, blue cast shadow, all after entitlement —
 * where a healthy clast is a green disc with a thin red edge and the defect is a red ring
 * with black inside it. That dump is the instrument this defect needed and never had.
 *
 * TWO THINGS CAME OFF THE BACK OF IT. The block layer's far density cut goes 0.40 -> 0.78:
 * it existed because the layer was drawn to 600 m where thinning was the only lever, and
 * with the cause fixed, thinning inside 130 m is just an empty hillside. And the cliff
 * bands hand over at 120-260 m instead of 160-330 with the swathe gate tightening over the
 * same range — `farlayerab` 16p0s (bands off) is indistinguishable from 8p0s at 300 m, so
 * the old handover was paying for a layer that was not there, while `rockcam` 10p0s shows
 * the bands as a stack of parallel terraces running the full width of a face at 60-200 m.
 * That is "iso-elevation banding is still the dominant readable pattern", one range closer
 * in than the handover was written for.
 *
 * WHAT THIS ROUND DID NOT CLOSE. The far field now separates by value and hue and it is
 * the form fields doing it (`farlayerab` 20p0s, form off, is visibly flatter and warmer) —
 * but its structure is one spatial frequency: parallel ribs of near-uniform width running
 * the fall line. The reference (`assets/reference/target/mavrix-chase-ridge-vista.jpg`)
 * reads from a few LARGE masses and from depth banding between whole landmasses, not from
 * a comb. Getting there needs a second, much coarser landform scale — or it needs the
 * heightfield to have one.
 * ---------------------------------------------------------------------------
 * WAVE 4 ROUND 8: THE FAR FIELD IS NOT IN THE FRAME. MEASURED, TWICE, THREE WAYS.
 *
 * Carried gap: "past ~100 m the mountain is one flat mauve wash organised by elevation...
 * no gully, rib or spur carries any shade... iso-elevation banding is still the dominant
 * readable pattern."
 *
 * This round set out to close that with two new things, and both are in the file below and
 * both work. What it actually established is why seven rounds of far-field work have moved
 * the frame so little, and that finding outranks either of them.
 *
 *   1. THERE IS NO GROUND PAST 200 m IN ANY FRAME THE HARNESS PRODUCES. The distance-ring
 *      dump (view 4) is CATEGORICAL — five flat colours — so it survives any grade, which
 *      matters because the post chain was mid-edit by another agent all afternoon. On the
 *      pose the vista frames hold (scenario `vistafar`, appended, shot 28s) every pixel of
 *      terrain reads the < 100 m band except a strip along the ridge that reads 100-200 m.
 *      Nothing in the frame is in the 200-500 m band the carried gap is about. Confirmed
 *      independently by a hard probe: forcing `gTerrLand` to 0.45 behind `landOn`
 *      (smoothstep 45-130 m) changed the frame by 0.4%, and forcing it to 0.45
 *      unconditionally darkened the whole mountain — so the channel is live and the
 *      distance simply is not there. Three hours earlier, on the same bearing and the same
 *      spawn, the same dump read 300-500 m over that hillside. The camera and the massif
 *      both moved under this piece during the wave; the mountain now presents a ~100 m
 *      bubble bounded by its own convex shoulder.
 *
 *   2. AND WHERE THERE IS DISTANCE, THE HEIGHTFIELD HAS NO LANDFORM IN IT. View 8, added
 *      this round: dot(gN, sun) off the clipmap's exact analytic normal, before anything
 *      here touches it — the one measurement that separates "the material is not shading
 *      the landform" from "there is no landform to shade". It runs smoothly from 0.45 at
 *      the foot of the visible slope to 0.90 at the ridge with nothing in between: a
 *      smooth convex dome. No gully, no rib, no spur, at any scale in view. No shading
 *      model can put shade on landform that is not there, so this half belongs upstream.
 *
 *      That dump is BANDED rather than continuous, and it had to be. The first version was
 *      a continuous ramp and it read as a uniform white field — which would have been
 *      reported as "the heightfield is one flat facet". It was the post grade clipping.
 *      Five hard bands cannot clip into each other. Three consecutive waves have shipped a
 *      wrong conclusion off a broken instrument; this is what nearly made it four.
 *
 * WHAT WENT IN ANYWAY, because both are correct and both scale automatically the moment
 * there is distance to act on:
 *
 *   THE LANDFORM LIGHT (`gTerrLand`, `TerrainLandform`). A signed multiplier on the direct
 *   term, and on a third of the indirect, driven by the landform-scale relief and nothing
 *   else. The measurement that motivates it is still true: N.sun on far ground has a median
 *   near 0.85, so the ground sits at the flat maximum of the cosine and ten degrees of
 *   relief moves the direct term two or three per cent. The cosine is a bad transfer
 *   function for landform at that range. This puts the landform's OWN contribution to
 *   N.sun through a saturating odd sigmoid: mean-preserving by construction, driven by the
 *   sun vector so it inverts at evening and is worth nothing at night, and band-limited
 *   because its input is three fields that each carry their own Nyquist fade.
 *
 *   FAR-FIELD MATERIAL SEPARATION. An inverse of a measured transfer, not a look. On the
 *   build this round started from, aerial perspective took the 300-500 m band's value ratio
 *   from 2.35:1 to 1.52:1 and FLIPPED its mean hue from warm to blue — that was the mauve
 *   wash, and it is `sky.ts`'s. The surfaces were never the problem: the weight dump
 *   (view 1) shows rock, hardpack, grass and loose in areas tens of metres across and the
 *   raw albedo dump (view 2) shows them separating cleanly. So the albedo now sends more
 *   separation into the haze than it wants to arrive with. The atmosphere piece thinned
 *   that haze substantially during this round, which is most of why the wash is already
 *   gone from the current captures.
 *
 * MEASURED, in-session A/B through `__gripTerrainShading.far2()` so nine agents' edits
 * cannot contaminate it (`vistafar`, shots 8/12/16/20): on the furthest strip in frame the
 * pair adds 1.9% of standard deviation and 3% of p95/p5 with the mean unchanged to 0.3%.
 * That is small, and it is small for the reason above, not because the terms are inert:
 * the same channel forced to a constant moves the whole mountain.
 * ---------------------------------------------------------------------------
 * WAVE 4: THE FAR FIELD, MEASURED INSTEAD OF ARGUED ABOUT.
 *
 * Carried gap: "from roughly 100 m to 500 m the mountain is still a value-flat violet wash
 * carrying two painted patterns — parallel contour-band stripes and dark elliptical clast
 * shadows — instead of light on landform: hardpack, rock and scree do not separate out
 * there by value or hue at all."
 *
 * Three rounds have now been spent on that band on the strength of guesses about which
 * term produced which mark, and every guess was wrong. So this round built the instrument
 * first: an A/B rig on a uniform (`__gripTerrainShading.layers/form/far/view`) that
 * switches one layer off at a time inside a SINGLE page session, five false-colour dumps
 * written past the fog and past ACES, and four scenarios that hold one bearing still —
 * `farhill`, `farhilldbg`, `farhillab`, `farhilltilt`, `farhilllight`. What they found:
 *
 *   1. THE DARK ELLIPSES WERE THE BLOCK CLAST LAYER. `farhillab` 4p0s is 1p0s with that
 *      one layer off and essentially every pockmark between 70 m and 220 m goes with it.
 *      Wave 2 and wave 3 both tried to make a six-pixel knob READ — density down, crease
 *      off, tint remapped bright, short hard shadow beside a lit crown. The failure is
 *      structural: at six pixels an object can only contribute a light/dark pair, and four
 *      hundred of those across a hillside is a texture, not boulders. The layer now keeps
 *      its dome normal (form) past 64 m and gives up its shadow, its tint and its crown
 *      (marks). After: the same A/B moves the far window by 0.2 of a standard deviation.
 *
 *   2. THE CONTOUR STRIPES WERE NOT THE CLIFF BANDS — not mostly. Switching the whole band
 *      block off left them in place. Half of it is `strat`, the bedding-plane term, which
 *      is sampled at wp.y * 0.34 — a three-metre VERTICAL period — and swings the rock ramp
 *      nearly 5:1; on a 30% hillside that is a contour line every ten metres, and the far
 *      field is mostly rock. It is now confined to genuine faces past ~110 m. The other
 *      half is real: `farhilltilt` shows the band normal tips are the largest perturbation
 *      on the far hillside, up to 30 degrees, strictly iso-elevation. Bands now hand over
 *      to the form fields between 160 m and 330 m.
 *
 *   3. WHY FORM ALONE CANNOT FIX THIS BAND, which is the finding worth carrying forward.
 *      `farhilllight` takes the far hillside apart with sky.ts's own switches: the terrain
 *      self-shadow is 1.00 with a standard deviation of 3/255 (nothing out there is
 *      shaded); N.sun off the final normal has a MEDIAN near 0.85, so the sun sits close to
 *      the surface normal and cos is at its flat maximum — eight degrees of relief moves
 *      the direct term two or three per cent, and no amount of amplitude fixes the
 *      derivative of a cosine at zero (verified: 5.4x the form amplitude moved the frame's
 *      standard deviation by 9%); and AERIAL PERSPECTIVE IS 45% OF THE PIXEL'S RADIANCE,
 *      taking the ground's own contrast from 7.3:1 to 2.8:1 in linear. Two of those three
 *      are sky.ts's and are not this file's to change.
 *
 *      So the answer here is not more relief, it is that LANDFORM POSITION AND MATERIAL ARE
 *      NOT INDEPENDENT. Hollows hold water, litter and timber and go dark and green; spurs
 *      are wind-scoured, thin-soiled and pale. `formPos` drives the material from the same
 *      height field that drives the normal, so where the sun does rake — every other
 *      bearing, and every hour that is not mid-morning — colour and light agree about the
 *      same landform instead of being two patterns laid over each other.
 *
 *   4. AND THE FAR FIELD HAD NO SCREE IN IT. The surface-weight dump says the hillside at
 *      300-500 m is ~85% rock with hardpack and grass in blotches and almost no loose. The
 *      palette block below spent two rounds spreading rock (0.148, cold) against loose
 *      (0.259, warm) to 1.75:1 so the far field would have a value structure, and the far
 *      field never contained the pale member: it only ever arrived through the cliff-band
 *      apron, i.e. in horizontal stripes. There is now a landform-scale MINERAL COVER —
 *      `rlfCoverBlend` at 78 m of patch with 1.25 m of thickness, so the fan has a lit
 *      shoulder, a fingered toe and a contact band in the ground banked against it.
 * ---------------------------------------------------------------------------
 * WAVE 3 ROUND 2: the ground now answers a SECOND question about the light.
 *
 * The carried gap: "in shade the ground stops being shaped and becomes drawn — with no
 * direct sun the dome normals contribute nothing against near-uniform ambient, so each
 * clast collapses to a hollow dark AO ring with a featureless interior, over a plaid of
 * pure-albedo litter strokes, on an otherwise flat dark plane."
 *
 * Three separate causes, measured rather than guessed:
 *
 *   1. NOTHING IN THIS SHADER COULD MAKE THE SKY BRIGHTER ANYWHERE. Every light-side term
 *      it owned — crease, cavity, cover rim, undercut, gully — was an occlusion on
 *      `gRlfOcc`, which is clamped at 1 and spends 65% of itself on the direct term too.
 *      So relief could only ever subtract, which is exactly a dark ring round a flat
 *      interior. `gTerrAmb` is the new channel: signed, coloured, multiplied onto
 *      `indirectDiffuse` alone. Being ambient-only makes it self-gating — a few per cent
 *      of a sunlit pixel, the whole read of a shaded one — so it needs no knowledge of the
 *      shadow map. See `TerrainAmbient`, which also records what the SH probe's own normal
 *      response actually measures (a bright side, and no answer at all to "how much sky
 *      can this facet see").
 *
 *   2. BETWEEN 1 m AND 6 m OF WAVELENGTH THERE WAS NO RELIEF AT ALL, and inside 10 m there
 *      was none above a metre full stop: the clast layers stop at a 95 cm knob, the
 *      mesoform is held out to 10 m and the coarse form to 45 m. That band is the ground a
 *      rider looks at from the saddle. `TerrainGround` fills it with one 2-octave field at
 *      1.6 m, sized per surface — 10 cm on duff and scree, 2 cm on the built tread, which
 *      stays smooth on purpose.
 *
 *   3. THE ROOT NETWORK WAS THE PLAID. A level set of a 1.8 m fBm drawn as a 2.6:1 albedo
 *      step, with a comment claiming the strands were 8 cm proud and a relief term
 *      delivering about a millimetre of it. It is now a real height through `rlfBumpH`
 *      with a contact crease at its flanks, and the albedo step is a quarter smaller. The
 *      litter fleck came down with it: its strokes were stretched 2.6:1 on a 13-26 cm cell,
 *      i.e. half-metre needles, which chain across cells into a hatch.
 *
 * WHAT THIS ROUND FOUND AND DID NOT FIX. In a fully stocked stand the surface a rider is
 * actually looking at is NOT this material: painting `diffuseColor` a flat grey leaves the
 * forest floor brown and still carrying its polygonal light-line lattice, so that near-
 * field ground — and the "plaid" seen on it — belongs to whatever draws ground cover over
 * the terrain. Captures: `captures/w3-terrain-shading-r2{,-shade,-descend}`; the `shadecam`
 * scenario forces overcast so the ground is lit by sky and nothing else, and
 * `window.__gripTerrainShading.ambient(0..1)` A/Bs the sky term in place.
 * ---------------------------------------------------------------------------
 * WAVE 2 ROUND 2: the bands existed and still did not read. Three reasons, all fixed.
 *
 * The round-1 cliff bands measured, on the far hillside, +-5% of local luminance with the
 * dark excursions half again the size of the bright ones. They read as faint contour
 * ruling on a smooth dome rather than as benches and risers. Turning the amplitude up did
 * essentially nothing, twice, which is what forced a false-colour dump of the masks — and
 * that showed the masks were already at full strength over every square metre of every
 * face. So the fault was never amplitude.
 *
 *   1. THE PROFILE COULD ONLY SUBTRACT. A lip shadow and a riser darkening with nothing
 *      lit opposite them. The previous pass had correctly refused to brighten the bench —
 *      it is the majority member, so lifting it lifts most of the mountain and every place
 *      the gate closes becomes a dark disc. The answer is a fifth member: the CREST, the
 *      narrow convex nose of the bench standing above the riser. It is exactly the width
 *      of the lip and of the riser, so the mean-preserving lift is just K_LIP + K_RISER,
 *      and it carries the same bandLive * bandSteep gate, so the cancellation is pointwise
 *      and the gate can never leave a residue. Measured after: bright 8.4% against dark
 *      8.1%, a ratio of 1.03 where it was 0.51.
 *
 *   2. THE BANDS WERE SPACED TEN PIXELS APART. They were faded out at their Nyquist limit
 *      rather than at their on-screen spacing, so the far field was covered in perfectly
 *      correct, hard-edged, form-following terraces the size of a hatching stroke. A step
 *      in the ground needs room to be a step. fwidth of the band coordinate is cycles per
 *      pixel, so its reciprocal is the spacing in pixels, and both sets are now faded on
 *      that: the minor set hands off to the 3.3x coarser major set, which hands off to
 *      pure landform.
 *
 *   3. THE SWATHE GATE WAS NOT GATING. Its ramp sat two and a half standard deviations
 *      below the mean of its own input, because an fBm is a sum of octaves and piles up
 *      near 0.5 instead of spreading over 0..1. Every band therefore ran unbroken from one
 *      side of a face to the other, which is a contour map. Gates are now placed against
 *      the distribution of the field driving them.
 *
 * Two things came off the back of it. The structure ramp starts at 9 m instead of 12-45 m,
 * because "15 m to the horizon" means the slope you are about to ride onto, and that was
 * the one part of every capture the whole feature never touched. And the block clast layer
 * gives up its crease and its cast shadow past 30 m: a 1 m knob at 150 m is 8 px, too
 * small to show a lit crown and big enough to show a solid dark ellipse, and the far
 * hillside was pocked with them.
 *
 * WETNESS. Weather.wetness drives uWetness and always did; what was wrong was the mapping.
 * The amount used to scale the spatial preference field, so a full downpour reached about
 * 0.2 on ordinary ground and the rain preset came out nine per cent darker than a clear
 * day. The amount now interpolates the preference field toward flat as it rises: a trickle
 * wets only the gullies and the undercuts, a downpour wets everything and leaves the
 * pattern as a bias. wetnessBase went 0.13 -> 0.17 to suit the new curve.
 * ---------------------------------------------------------------------------
 *
 * The rest of the design, in order of what the eye notices:
 *
 *  1. SURFACE DECODE. terrainMesh writes one of seven base colours per vertex and the
 *     rasteriser interpolates them, so the fragment sees a *mixture*. Nearest-reference
 *     matching fails on that mixture (the hardpack reference sits almost exactly on the
 *     loose<->grass midline). Instead we decode with three near-orthogonal colour features
 *     that survive interpolation: luma separates snow and the soil family by brightness,
 *     chroma isolates rock (it is the only achromatic surface), and green-excess isolates
 *     grass (0.10 for grass, ~0.00 for everything else). That gives a stable weight per
 *     surface even mid-triangle.
 *
 *  2. HEIGHT BLENDING. Those weights are then fought out against a per-surface procedural
 *     height field, so a loose/rock boundary interlocks — stones poke through the scree,
 *     grass fills the hollows — instead of cross-fading like two decals.
 *
 *  3. TRIPLANAR PROJECTION so cliffs get rock detail at the right scale instead of a
 *     smeared ground projection. Repetition is killed three ways: the hash has no period
 *     over the world extents, every fBm octave is rotated by an irrational angle with
 *     non-integer lacunarity, and the whole detail domain is warped by the macro field.
 *
 *  4. SLOPE AND DRAINAGE. Rock is exposed per-pixel where the ground is too steep to
 *     hold soil (the vertex classifier only does this at vertex resolution, which is
 *     32 m on the outer rings); soil compacts and darkens on the benches; water pools
 *     on the flats and in the low ground, darkening albedo and dropping roughness.
 *
 * No external textures. Everything below is generated from a 2D hash.
 */

import * as THREE from 'three';
import { Weather } from '../world/weather';
import {
  reliefGLSL,
  mergeReliefUniforms,
  installReliefSystem,
  RELIEF_STD_GLOBALS,
  RELIEF_STD_AO_PATCH,
} from './shading/relief';
import {
  Ground,
  Flora,
  Accent,
  FarSeparation,
  PaletteLimits,
  oklch,
  gamutClip,
  linearToOklab,
  paletteGLSL,
  mergePaletteUniforms,
  type PaletteEntry,
  type RGB,
} from './palette';
import { declareInvariant } from '../core/audit';
import { registerSystem, type SystemContext } from '../core/systems';

export interface TerrainMaterialHandles {
  material: THREE.Material;
  /** Called once per frame with camera/world state, for anything time- or view-dependent. */
  update(dt: number, camera: THREE.Camera, time: number): void;
  /**
   * Force the 0..1 global wetness, overriding `Weather.wetness` until `clearWetness()`.
   * Spatial variation is procedural either way — this is only the global amount.
   */
  setWetness(w: number): void;
  /** Hand control of wetness back to the weather system. */
  clearWetness(): void;
  /**
   * A/B the sky term: 1 = live, 0 = the ambient response this round added, removed.
   * Also reachable from the capture harness as `window.__gripTerrainShading.ambient(a)`.
   */
  setAmbient(amount: number): void;
}

/** Local tuning. Physics/control constants live in core/tuning.ts; these are visual only. */
export const TerrainShading = {
  /** Peak elevation of the massif, m. Mirrors Tuning.world.reliefHeight for altitude cues. */
  reliefHeight: 940,
  /**
   * Wetness floor under clear skies — `Weather.wetness` is 0 in the clear preset, and a
   * mountain that is bone dry everywhere on a sunny day is wrong: the gullies and the
   * seeps still run. This is a *global* multiplier on an already-spatial field, so it
   * shows up almost entirely in the drainage lines and the low ground.
   *
   * Recalibrated three times. 0.2 -> 0.13 when the drainage field started feeding the same
   * term and the channels came out as near-black cords on a clear day; 0.13 -> 0.17 once
   * the wetness *amount* stopped simply scaling the spatial field (see `wetPref` / `wet`
   * below); and 0.17 -> 0.22 this round together with a much higher floor on `wetLocal`,
   * which is the term that was actually swallowing the effect. Measured under the new
   * numbers, clear weather costs open ground under 3% of its value — dry, as it should be
   * on a summer afternoon — and puts 8-9% into the gully floors and the cliff undercuts,
   * which is a damp streak you can see and steer around. Full rain now takes ordinary
   * ground down by about 27%, in the 30-50% band that wet dirt actually loses, instead of
   * the 16% the previous calibration managed.
   */
  wetnessBase: 0.22,
  /**
   * Peak-to-peak relief, metres, of the mid-scale form field (~6 m period).
   *
   * This is the number that closes the older carried gap. The same field was already in
   * the bump stack contributing an RMS slope near 0.017 — three centimetres — which is
   * why the mountain past 30 m could only ever be read from albedo. Real ground has
   * 30-60 cm of undulation at a 6 m wavelength; at 1.6 the RMS slope lands near 0.14, or
   * eight degrees, which is a value step of 15-25% under a low sun and, crucially, one
   * that moves when the sun moves and dies when the field dies.
   */
  mesoRelief: 1.6,
  /**
   * Distance band over which the mid-scale form ramps in, metres.
   *
   * It is deliberately absent in the near field: inside ~10 m the real geometry is
   * describing the ground at 0.5 m resolution and the clast layers own the read, and a
   * virtual 6 m lump drawn across ground the rider can plainly see is the same mistake
   * the cliff bands were pulled back from in wave 2.
   */
  mesoNear: 10,
  mesoFar: 34,
  /**
   * Peak-to-peak relief, metres, of the COARSE form field (~24 m period), and the band it
   * ramps in over.
   *
   * The 6 m mesoform above closes 30-150 m and then hits its own Nyquist limit: at 400 m
   * one pixel covers roughly 2.4 m of ground once the anisotropy budget is spent, so a 6 m
   * lump is two and a half pixels and `rlfBandLod` correctly takes it to nothing. Measured
   * against a build with mesoRelief = 0, the far hillside did not move at all. A far
   * hillside needs form at the scale it can still resolve, which is 20-60 m — and that is
   * exactly the band the clipmap loses first, because past 256 m it tessellates at 8 m and
   * past 512 m at 16 m, so the geometry's own 20 m structure is being decimated away at
   * the same distance this picks it up.
   *
   * WAVE 4 — 2.6 -> 7.4, AND IT WAS MEASURED, NOT GUESSED. The wave-3 coherence pass
   * carried this piece's gap as "from roughly 100 m to 500 m the mountain is a value-flat
   * violet wash carrying two painted patterns... instead of light on landform". The
   * instrument that settled it is `__gripTerrainShading.view(3)` — N.sun off the final
   * perturbed normal and nothing else, dumped past the fog and past ACES (scenario
   * `farhilldbg`). On the baseline that dump is a UNIFORM FLAT FIELD over the whole far
   * hillside: every structural cue out there was albedo, exactly as the critic said, and
   * this term was the reason. rlfReliefField's slope is proportional to freq * relief, and
   * the library's own calibration note puts the RMS slope at ~0.55 of that product — so
   * 0.042 x 2.6 was an RMS slope of 0.06, three and a half degrees, at a distance where
   * aerial perspective then multiplies whatever contrast survives by about 0.5. Three and
   * a half degrees of tilt under a 31 degree sun is a 4% value step before haze and 2%
   * after: below the tone curve's ability to show it.
   *
   * 7.4 m over a 24 m wavelength is an RMS slope of 0.17, near ten degrees, which is the
   * spur-and-gully relief a real hillside carries at that scale and lands as a 15-25%
   * value step that survives the haze. It is deliberately NOT in the geometry — the
   * clipmap samples the heightfield every 8 m past 256 m, so the terrain's own 20-40 m
   * structure is being decimated away exactly here — and it is held out of everything
   * inside `formNear` so nothing the rider can ride disagrees with what it can see.
   */
  formRelief: 7.4,
  /**
   * WAVE 4 ROUND 8 — 40/115 -> 26/72, measured against the frame rather than against a
   * distance policy written for a frame that no longer exists.
   *
   * `vistafar` shot 28s is the distance-ring dump on the pose the vista frames hold, and
   * on this build there is no ground in it past ~200 m and very little past 100: the local
   * convex shoulder occludes the massif. So the whole 100-500 m band this layer was
   * written for is not being drawn, and a layer ramped 40-115 m was contributing over a
   * sliver of sky-adjacent ridge and nothing else. Measured: the landform light A/B moved
   * the frame by 0.4% with the old ramps.
   *
   * The other half of the same measurement is shot 24s — dot(gN, sun) off the clipmap's
   * own analytic normal, BANDED so it cannot clip. The visible hillside runs smoothly
   * from 0.45 at the foot to 0.90 at the ridge with no structure in between: the terrain
   * out there is a smooth convex dome, so there is no gully, rib or spur in the geometry
   * for any shading model to find. That is why this file has to supply the shape, and why
   * it has to supply it at 26-72 m rather than at 115.
   */
  formNear: 26,
  formFar: 72,
  /**
   * A third, coarser landform band and the range it owns, metres.
   *
   * The 24 m field above runs out of Nyquist somewhere past 600 m — one pixel covers
   * ~3.5 m of ground out there once the anisotropy budget is spent, so a 24 m form is
   * seven pixels. Past that the only thing left with any structure in it was the macro
   * albedo wash, which is paint. 90 m at 18 m of relief is a spur, and a spur is legible
   * at two kilometres.
   */
  ridgeRelief: 18.0,
  ridgeFreq: 0.011,
  /**
   * WAVE 4 ROUND 8 — 260/620 -> 130/330, and this was a hole rather than a taste change.
   *
   * The carried gap is about 100-500 m. At 300 m the old ramp evaluated to
   * `smoothstep(260, 620, 300) = 0.029`: the 90 m spur band, the only layer in this file
   * whose wavelength is the wavelength of a spur, was switched off across the entire band
   * it was written for. The 24 m form field was carrying 100-500 m alone, and a 24 m lump
   * at 400 m is nine pixels — small enough that a hillside covered in them reads as a
   * mottle, which is exactly the "flat wash" complaint.
   *
   * 130/330 puts both landform bands live over 200-500 m, so the far field has a coarse
   * shape with a finer one on it — a spur with gullies in it — instead of one frequency.
   */
  ridgeNear: 70,
  ridgeFar: 200,
  /**
   * WAVE 4 ROUND 3 — THE MASS BAND. A FOURTH landform scale, and the one the file's own
   * round-9 note said was missing.
   *
   * That note, verbatim: "the far field now separates by value and hue and it is the form
   * fields doing it — but its structure is ONE SPATIAL FREQUENCY: parallel ribs of
   * near-uniform width running the fall line. The reference reads from a few LARGE masses
   * and from depth banding between whole landmasses, not from a comb."
   *
   * That is a diagnosis of a spectrum, and the fix is a spectrum: 24 m, 90 m and now
   * 300 m, each a factor of ~3.3 apart, so the far hillside has a big mass with spurs on
   * it with gullies in them rather than one comb at one pitch. The amplitude keeps the
   * same 0.20 slope ratio the 90 m band uses (18/90), so this is the same landform read
   * one octave coarser and not a new, louder pattern: 0.20 * 300 = 60.
   *
   * IT DRIVES MATERIAL AS WELL AS LIGHT, and that is the half that answers "the surfaces
   * do not separate out there". `formPos` — hollows dark and green, spurs pale and stony —
   * is presently driven at 24 m and 90 m, i.e. it varies four times inside one far
   * hillside and averages to nothing at the range the gap is about. Adding the mass band
   * to it puts whole flanks of the mountain on one side of the material split, which is
   * what "large distinct areas past 100 m" means and what the reference vista shows.
   *
   * What this CANNOT do is invent a landform the heightfield does not have: the silhouette
   * is still the heightfield's, and view 8 (dot(gN, sun) off the clipmap's exact analytic
   * normal) still reads a smooth convex dome. See the report — that half is upstream.
   */
  massRelief: 60.0,
  massFreq: 0.0033,
  /**
   * The band it owns, metres.
   *
   * Written first as 150/360 on the same reasoning the 90 m band used, then measured and
   * moved, which is round 8's lesson applied before shipping rather than after. The
   * distance census (view 4, four independent stations on four different runs, see the
   * report) says 88-98% of the terrain in frame is inside 100 m and essentially all of the
   * rest is 100-200 m. A band ramping 150-360 is therefore worth ~0.1 of its amplitude on
   * the only far ground that exists. 110/300 puts it live at 200 m at half strength, which
   * is where the far field of this build actually is.
   *
   * It is still held out of everything a rider can ride: 60 m of relief tilted into the
   * near field would be a different mountain, and the 90 m band is already fully in by 200.
   */
  massNear: 110,
  massFar: 300,
} as const;

/**
 * THE LANDFORM LIGHT — the far field read from shape and shadow rather than from paint.
 *
 * Read the `gTerrLand` block in TERRAIN_COMMON first; it explains why this is not simply
 * more relief amplitude. These are the four numbers that shape the transfer.
 */
export const TerrainLandform = {
  /**
   * Peak swing of the direct term, either side of 1.0, once the sigmoid saturates.
   *
   * Calibrated against what has to survive: aerial perspective measured on this build
   * takes the 300-500 m band's value ratio from 2.35:1 to 1.52:1 (scenario
   * `farhilllight`, shot 4p0s is the same frame with `skyDebug.aerial = 0`), i.e. it
   * keeps roughly half of any contrast this file produces. Direct light is about 70% of
   * a sunlit far pixel. So a +-0.85 swing on the direct term arrives at the eye as about
   * +-20%, which is a value step you can see a gully in; +-0.30 arrives as 7% and is what
   * three previous rounds delivered.
   */
  gain: 0.85,
  /**
   * Sharpness of the sigmoid, in units of relative change in N.sun.
   *
   * The input is `(N_landform . sun - N_geometric . sun) / |N_geometric . sun|`. On this
   * hillside the landform fields deliver about +-0.10 of that, so a sharpness of 4.5 puts
   * a typical flank at 0.41 of full swing and a strong one at 0.8: lit ribs against shaded
   * hollows with a soft but decided edge, rather than a cosine ramp nobody can see. Raising
   * it further starts to posterise, which is the low-poly-toy failure mode in docs/BAR.md.
   */
  sharp: 8.0,
  /**
   * How much of the incised drainage goes into it. Full weight: a gully IS the case this
   * channel exists for, and its gradient is already clamped where it is built.
   */
  channel: 1.0,
  /**
   * The band it owns. Held out of everything inside `near` because the near field is
   * geometry the rider can see and ride, and a hard light step drawn across it would be
   * the wave-2 cliff-band mistake again.
   *
   * WAVE 4 ROUND 8, SECOND PASS — 80/210 -> 55/150, and the reason is a measurement of the
   * frame rather than of the shader. `vistafar` shot 28s is the distance-ring dump on the
   * pose the vista frames actually hold, and on this build EVERY pixel of ground in it is
   * inside 100 m except a strip along the ridge that reads 100-200 m. There is no 200-500 m
   * ground in the frame at all: the local convex shoulder occludes it. A far-field layer
   * ramped 80-210 therefore evaluated to zero over the whole image, which is exactly what
   * the first pass measured — five A/B frames identical to the last digit. Whatever is or
   * is not wrong with the mountain's framing (see the report), a layer that is meant to
   * carry "past ~100 m" has to be live on the ground that is at 100 m.
   */
  near: 28,
  far: 85,
  /**
   * FAR-FIELD MATERIAL SEPARATION, and this one is a pre-compensation for a term this
   * file does not own — say so in any report that quotes it.
   *
   * Measured on the 300-500 m band, same rect, three ways:
   *   raw albedo out of this shader (view 2)   p95/p5 1.86, chroma b-y -0.090 +- 0.201
   *   lit, aerial perspective OFF              p95/p5 2.35, chroma b-y -0.087 +- 0.194
   *   lit, as shipped                          p95/p5 1.52, chroma b-y +0.118 +- 0.114
   * Aerial perspective removes 60% of the ground's value range out there and FLIPS its
   * mean hue from warm to blue. That is the mauve wash in the critique, and no amount of
   * work on albedo separation is visible underneath it at full strength. `sky.ts` owns it.
   *
   * What this file can do is send more separation into the haze than it wants to arrive
   * with. `value` expands each surface away from the palette's own mid; `chroma` expands
   * it away from its own luma, so hardpack stays warm and rock stays cold through a blue
   * in-scatter. Both are scaled by `rlfFarFade`, so they are exactly zero in the near
   * field and full only where the haze is. If the atmosphere piece ever thins the near-mid
   * haze, these two come back down by the same factor — they are not an art choice about
   * how the ground should look, they are an inverse of a measured transfer.
   */
  farValue: 0.62,
  farChroma: 0.45,
  /**
   * The band the separation ramps over, metres. It used to ride the library's `farFade`
   * (140-470 m), which on this build is 0.007 at the furthest ground in the frame — i.e.
   * off. This is the same argument as `near`/`far` above: the policy is right, the numbers
   * were written for a frame that had 500 m of ground in it.
   */
  sepNear: 40,
  sepFar: 160,
  /** Linear-albedo mid of the palette, the point the value expansion works about. */
  farMid: 0.185,
} as const;

/**
 * Ground form at the scale of a stride — the band this shader did not have.
 *
 * The relief inventory before this round ran: clast layers from 1 cm to 95 cm, then
 * nothing, then a six-metre mesoform that is deliberately faded out inside 10 m, then a
 * twenty-four-metre form field that starts at 45 m. So between about one metre and six
 * metres of wavelength the ground was geometrically flat, and inside ten metres it was
 * flat above one metre full stop. That band is the ground a rider actually looks at from
 * the saddle, and its absence is most of why the shaded forest floor reads as a plane
 * with marks drawn on it: there is nothing there for either light to find.
 *
 * Peak-to-peak metres at a 1.6 m period, per surface. The spread is the point:
 *   - duff, loam and root drift and swell around buried wood: the most of anyone.
 *   - loose runs in drifts and shallow runnels.
 *   - HARDPACK IS NEARLY SMOOTH, and that is deliberate and load-bearing. Hardpack is
 *     the built tread. A compacted line is the one surface on the mountain that has been
 *     ridden flat, and putting lumps in it would both look wrong and pick a fight with
 *     the trail piece's cut bench.
 *   - rock is bedrock: broken at the joint scale, not undulating at the metre scale.
 *   - snow drifts, but wind-packed snow drifts long and low.
 */
export const TerrainGround = {
  /** Cycles/m. 0.62 is a 1.6 m period, with the second octave at 80 cm. */
  lumpFreq: 0.62,
  lumpHard: 0.022,
  lumpLoam: 0.095,
  lumpLoose: 0.105,
  lumpRock: 0.05,
  lumpRoot: 0.10,
  lumpGrass: 0.075,
  lumpSnow: 0.06,
  /**
   * Height of an exposed root strand above the soil it arches out of, metres.
   *
   * The strand's own comment has claimed 8 cm since round 3 while the shader delivered
   * about a millimetre of it, which is how the network ended up reading as a pale hatch
   * drawn on a flat plane. 0.06 is a real root: enough to carry a lit crown and a shaded
   * flank, short enough that the profile stays a strand rather than a pipe.
   */
  rootProud: 0.06,
} as const;

// ===========================================================================
// WAVE 5 ROUND 2: THE SLOPE GATES WERE IN THE WRONG UNITS, AND THAT IS WHY THE MOUNTAIN
// ARRIVES AS ONE MATERIAL.
//
// The carried gap: "the piece's own classifier finds at most TWO ground classes per
// distance band, always drawn from {hardpack, grass, loam}. Rock, loose, root and snow
// never reach the frame in any band in any scenario." And BAR's own anchor for this piece
// — "rock exposed where it is too steep to hold soil, soil in the benches" — was satisfied
// in wave 4 and is not now.
//
// THE MEASUREMENT, taken in node against `src/world/terrain.ts` directly, so no renderer,
// camera or shader sits between the number and the ground (`captures/w5ts-r2-tools/`).
// This shader measures slope as `slope = 1 - gN.y`, i.e. **1 - cos(angle)**. Every
// threshold in the block below was authored as if that number were a GRADE (a tangent).
// The two agree to within a per cent at 5 degrees and are a factor of three apart at 45,
// so the constants read plausible and fire nowhere:
//
//   gate                     shipping onset..full        fraction of ground it delivers
//                            (as an angle)               rockcam / midmtn / timber / massif
//   scree  (hardpack->loose) 25 -> 48 deg                2.3% / 1.4% / 0.2% / 1.0%
//   steepRock (soil->rock)   46 -> 63 deg                0.0% / 0.0% / 0.0% / 0.0%
//   bench  (loose->hardpack) 35 -> 14 deg, INVERTED      84.7% / 89.8% / 93.3% / 91.0%
//
// `steepRock` is dead code. It is the only per-pixel source of exposed bedrock in this
// file and it fires on ZERO PER CENT of the mountain, at every station sampled, because
// the mountain's p99 slope is 37 degrees and its maximum is 52. Meanwhile `bench` — whose
// job is to CONVERGE materials, moving scree back into hardpack and loam — runs at 85-93%.
// The one gate that separates is off; the one gate that merges is on everywhere. That is
// the whole "one warm brown from bench to crest", in two constants.
//
// So the thresholds are now stated in DEGREES and converted once, at build time, by
// `slopeAt()`. A future round cannot re-introduce the unit error without editing a number
// that says `deg` next to it.
//
// The angles themselves are the real ones. Dry soil holds to its angle of repose, roughly
// 33-37 degrees; fines wash off well before that and leave gravel behind; bedrock is bare
// where nothing at all can lie on it. Sized against the measured distribution so each gate
// lands on a real, named fraction of the ground rather than on a guess:
//
//   scree     22 -> 32 deg   11.2% / 6.5% / 3.0% / 5.5%
//   steepRock 28 -> 40 deg    3.8% / 2.0% / 0.2% / 1.5%
//   bench     17 ->  5 deg   ~40%  (was 85-93%)
//
// Loose (OKLab L 0.752, warm pale) and rock (L 0.470, the only cool ground) against
// hardpack (L 0.598) are 0.154 and 0.128 apart on the authored ladder — three and two
// rungs. Grass against hardpack is 0.066 authored and 0.036 as this file actually builds
// it (see `P.grassDry*`), which is BELOW the 0.055 budget before a single photon is cast.
// The near field was being asked to separate on the one pair that cannot do it.
// ===========================================================================

/**
 * A slope angle in degrees, in this shader's own slope measure (`1 - cos`).
 *
 * Exists so that the numbers in `TerrainRepose` are readable as angles and cannot be
 * confused with grades again. Evaluated on the CPU at shader-build time; costs nothing.
 */
const slopeAt = (deg: number): number => 1 - Math.cos((deg * Math.PI) / 180);

export const TerrainRepose = {
  /**
   * Where the fines stop sitting and the surface coarsens to gravel and scree.
   *
   * Real dry soil starts shedding its fine fraction well below the repose angle; by the
   * mid thirties there is nothing finer than gravel left on an open face. Delivers 11.2%
   * of the ground at the `rockcam` stand, 3.0% in the timber, which is the right shape:
   * scree belongs on the open steeps and not on a forest floor.
   */
  screeOnsetDeg: 22,
  screeFullDeg: 34,
  /**
   * ...and where even the gravel cannot lie and the bedrock is bare.
   *
   * This is BAR's "rock exposed where it is too steep to hold soil". 28 degrees is inside
   * the angle of repose on purpose: the vertex classifier in `world/terrain.ts` already
   * calls Rock at a 0.52 gradient (27.5 degrees) and delivers 8-22% of the ground as rock
   * around the `rockcam` stand, so this per-pixel term is not inventing the material — it
   * is giving the same decision a per-pixel, outcropped edge on the faces the vertex grid
   * can only resolve at 2-32 m. 3.8% at `rockcam`, 0.2% in the timber.
   */
  rockOnsetDeg: 28,
  rockFullDeg: 40,
  /**
   * A bench is ground flat enough that what washes off the steeps stays on it.
   *
   * Restated from `smoothstep(0.17, 0.03, slope)` — 35 down to 14 degrees, i.e. "almost
   * everything" — to 17 down to 5. This term MERGES materials (scree settles and compacts
   * into hardpack and loam), so running it at 85-93% of the mountain was most of the
   * convergence the near field was being sent back for. At 17 degrees it runs at ~40% and
   * it means what its name says.
   */
  benchOnsetDeg: 17,
  benchFullDeg: 5,
} as const;

/**
 * MINERAL LAG — stone pavement on gentle open ground. See the block in the shader for the
 * measurement that forced it; the short version is that with the repose gates fixed the
 * FAR band came back with six classes and the NEAR band came back 99.9% hardpack, because
 * the trailhead is a gentle plateau and nothing in this file varied material inside 60 m
 * on ground that is not steep.
 *
 * Sized as the duff mat's mirror. The two are the same mechanism with opposite biases, so
 * their constants are deliberately in the same shape as `TerrainCover`'s.
 */
export const TerrainLag = {
  /**
   * Patch size, metres.
   *
   * Half the duff mat's 34 m. A gravel lag patch is a fellfield feature — metres to a few
   * tens of metres — and at 16 m several of them are in a riding frame at once, which is
   * what makes the ground read as a surface with a process on it rather than as two big
   * blotches.
   */
  patchM: 16,
  /** Decorrelates the lag from the duff mat and from the talus fan. */
  seed: 8.3,
  /** Feather half-width, field units. Wider than the duff mat's: a pavement edge is a
   *  gradation over a metre or two, not a cut. */
  margin: 0.042,
  /** Cycles/m of the margin break: ~2.5 m lobes on the edge. */
  breakFreq: 0.40,
  /**
   * Armour thickness, metres.
   *
   * 8 cm is a real stone pavement — one to two clast diameters. It is what gives
   * `rlfBumpH` a shoulder along the upslope edge and `rlfCoverBlend` a contact band in the
   * silt banked against the downslope toe, and it is the difference between this being a
   * material and being a stain. `depth * 9` inside `rlfCoverBlend` saturates the contact
   * at 0.11 m, so 0.08 buys 72% of the available contact.
   */
  depth: 0.08,
  /** Fraction of the packed ground the pavement takes. Partial on purpose — see the
   *  shader block. */
  take: 0.66,
  /** ...and how much of the take is bedrock slab rather than gravel. */
  toRock: 0.18,
  /**
   * The threshold on the cover field, and it was set by measurement rather than by
   * reasoning — the reasoning was wrong in a way worth recording.
   *
   * `rlfCoverField` has a mean near 0.50 and a standard deviation near 0.09, so the level
   * sets the coverage directly. The first pass argued that the biases below are "mostly
   * positive" and put the base at 0.455 to compensate. On the trailhead plateau they are
   * not: the altitude term alone takes 0.052 off it. Measured on the finished frame, the
   * near band came back **99.0% loose** — the same defect as before with the colours
   * swapped, which is the failure mode BAR names in both directions.
   *
   * 0.545 was a second overshoot, on a massif another agent made steeper mid-round: the
   * near band came back 0.0% hardpack. 0.575 puts an exposed, mineral, high patch at
   * roughly a fifth to a quarter pavement, which is what a fellfield looks like and what
   * leaves hardpack the dominant ground the trail is cut into. COVERAGE is the knob here,
   * not opacity: the height blend resolves a partial take to a near-pure pixel by design,
   * so `take` changes how a patch READS and `level` changes how much mountain is one.
   */
  level: 0.575,
  /**
   * Distance ramp, metres. Live in the near field, gone by the time the landform-scale
   * talus fan (45-130 m) has taken over — two mineral covers running at once on the same
   * hillside is a patchwork, which is the other half of the carried gap.
   */
  far: 60,
  gone: 150,
  /** Where the contact band stops being a contact band. An 8 cm feature at 3 cycles/m. */
  occFadeFreq: 3.0,
} as const;

/**
 * THE CLAST LAYER'S TWO FAILURE MODES, AND THE ONE INVARIANT THAT FIXES BOTH.
 *
 * Carried gap, verbatim: "The block-clast layer is the defect at both ends: it degenerates
 * into hollow dark elliptical outlines with featureless interiors across near and mid
 * ground (including on the compacted built tread, where nothing is scattered), and it
 * aliases into a hard regular chain-mail lattice from roughly 150-500 m instead of dying
 * at Nyquist."
 *
 * Both halves are the SAME bug seen from two distances, and it is an arithmetic bug rather
 * than an art one. An object in this shader is drawn as five separate cues — a dome
 * normal, a per-clast albedo tint, a lit crown, a base crease and a cast shadow — and
 * every one of them was gated by a DIFFERENT product of lod terms, surface entitlements
 * and distance ramps. Wave 2 already found one instance of that (a cast shadow running at
 * 100% behind a caster running at 16%) and fixed it for the shadow alone. The same fault
 * was still live for the crease, and the crease is the worst place to have it because the
 * crease is the one cue that is a RING:
 *
 *   NEAR AND MID. `crease` was `bkId.z * lodBlock * 0.80 + stId.z * lodStone * 0.72 + ...`
 *   — no surface entitlement at all, while everything the object's *body* owns (its dome
 *   normal via bkRel/stRel/..., its tint via the per-surface mix weights) was entitled per
 *   surface. On the built tread hardpack is entitled to 0.42 of the block layer's relief
 *   and 0.34 of the stone layer's, and to a third of their albedo — but to 100% of their
 *   creases. So the compacted line, the one surface on the mountain that is supposed to be
 *   swept clean, was covered in rings with nothing inside them. That is the defect
 *   *literally*: the ring is the only cue that was not entitled down.
 *
 *   FAR. `lodBlock` is `rlfBandLod(L, F_BLOCK)` — the Nyquist limit of the layer's CELL
 *   LATTICE, 1.18 m. But a cell is mostly empty. The thing in it with an edge is the
 *   clast, and at this radius distribution it is 0.38 of a cell across: 45 cm. So every
 *   clast layer was drawn until its objects were about two thirds of a pixel wide, which
 *   does not give you small stones — it gives you the beat between a square object lattice
 *   and the pixel grid. That beat is regular, hard-edged, and exactly the "chain-mail" a
 *   critic named. `rlfBandLod` is not wrong; it was being asked about the wrong feature.
 *
 * THE INVARIANT, stated once so it cannot rot again: **every cue an object owns is
 * multiplied by that object's own single amplitude.** One number per layer, built from its
 * resolution, its surface entitlement and its distance ramp, and applied to the crease,
 * the crown, the cast shadow, the tint and the dome alike. A ring can then never outlive
 * the solid it belongs to, on any surface, at any distance, because there is no longer a
 * path by which it can.
 */
export const TerrainClast = {
  /**
   * Object diameter, in cell units, for the resolution gate.
   *
   * The radius is `mix(rad.x, rad.y, rr*rr)` with rr uniform, so E[rr^2] = 1/3 and the
   * mean radius across the four layers is ~0.19 cells; the diameter is twice that. This
   * is the number `gClastRes` divides by the pixel footprint to ask "how many pixels wide
   * is a typical object of this layer".
   */
  objDia: 0.38,
  /**
   * Object width in pixels at which a layer is fully gone, and at which it is fully
   * present. Nyquist for a feature with an edge is two pixels; 1.6 is a little under that
   * because the density LOD has already thinned the field by then and a lone half-resolved
   * boulder is better than none, and 3.6 is where the ellipse is unambiguously an ellipse.
   *
   * Measured against the previous behaviour: `rlfBandLod(L, F_BLOCK)` alone reached zero
   * at a footprint of 0.656 m/px, where a 45 cm block is 0.69 px. That is 2.3x past this
   * gate, and the whole of that 2.3x was the lattice.
   */
  resOff: 1.6,
  resOn: 3.6,
  /**
   * How much the object's albedo edge softens as it approaches its resolution limit.
   *
   * The mask is `smoothstep(0, edge, dome)`, and a hard edge is right at 100 px and is
   * precisely what aliases at 3 px. Widening the threshold as the object shrinks is the
   * cheapest prefilter there is and it costs one mix. 0.55 takes a 3 px block from a hard
   * ellipse to a soft blob over the same range the resolution gate fades it out.
   */
  edgeSoft: 0.55,
  /**
   * Hardpack's entitlement to each clast layer. Absolute, replacing 0.42 / 0.34 / 0.40 /
   * 0.72.
   *
   * `TerrainGround.lumpHard` is 0.022 against 0.105 for loose for exactly this reason —
   * hardpack is the built line and it has been ridden flat, and this file already argues
   * at length that a compacted tread must not "acquire a case of the lumps". The clast
   * entitlements never got the same treatment, so the one surface on the mountain that is
   * supposed to be swept clean kept 42% of its buried boulders and 34% of its stones.
   *
   * Grains keep everything: a compacted surface is still made of grit, and grit at 1-3 cm
   * is the pixel-scale texture that stops the tread reading as plastic. It is the 8-95 cm
   * members that have no business on a ridden line — and they are precisely the two whose
   * creases are big enough on screen to read as drawn outlines.
   */
  treadBlock: 0.14,
  treadStone: 0.16,
  treadClod: 0.28,
  treadGrain: 0.72,
} as const;

/**
 * How the ground responds to the SKY, as opposed to how it responds to the sun.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS BLOCK EXISTS. The carried gap, in the critic's words: "in shade the ground
 * stops being shaped and becomes drawn — with no direct sun the dome normals contribute
 * nothing against near-uniform ambient, so each clast collapses to a hollow dark AO ring
 * with a featureless interior, on an otherwise flat dark plane."
 *
 * That is a correct diagnosis of a real mechanism, and it is worth writing down exactly
 * what the mechanism is, because "the ambient is uniform" turns out to be false in one
 * specific and very informative way. three's `lights_fragment_begin` evaluates the SH
 * probe and the hemisphere light at the *shading* normal, so a perturbed normal does move
 * the ambient. Measured on this build's live probe (mid-morning, sun 61 degrees up), a
 * facet tilted 45 degrees off vertical comes out between 0.92x and 1.31x the flat
 * ground's irradiance — and *which* it gets depends almost entirely on its azimuth
 * relative to the sun. Averaged over azimuth the same 45 degrees of tilt is worth about
 * +5%. So the sky the probe describes has a bright side, which is real, and essentially
 * no answer at all to the question "how much sky can this facet see", which is the
 * question that makes a bump on the ground look like a bump on the ground.
 *
 * That is because a facet on the ground is NOT unoccluded, and the SH probe has no way to
 * know it. It sits in a hillside, with the rest of the ground standing in the half of the
 * hemisphere it is tilted away from, and with the neighbouring clasts and duff taking
 * more. The visible sky of a tilted micro-facet is a much narrower cone than the
 * geometric hemisphere, so its true response to tilt is far steeper than the probe's.
 * Every term below is a cheap, mean-preserving stand-in for that: no extra taps, no new
 * fields, just the relief this shader already builds, asked the second question — not
 * "how much sun does this catch" but "how much sky does it see, and which sky".
 *
 * Everything is referenced to the UNPERTURBED normal, so undisturbed ground is exactly
 * 1.0 and the whole block is a no-op wherever there is no relief. That is what keeps it
 * from becoming a global exposure change, and it is what makes it die on its own at
 * distance: the perturbed normal already carries every layer's LOD fade, so at 500 m
 * there is no deviation left and no ambient shaping either.
 */
export const TerrainAmbient = {
  /**
   * Extra sky-visibility response to facet tilt, over and above the (1 + N.y)/2 the
   * probe already applies.
   *
   * 0.9 says a facet tilted until it sees half the sky an undisturbed one does comes out
   * 45% darker rather than the ~10% the SH gives. That is roughly the cosine-weighted
   * visible-sky ratio for a bump sitting on a plane, and it is the single term that
   * turns a clast from a ring into a solid in overcast.
   */
  tilt: 1.6,
  /**
   * How much of `tilt`'s own mean to hand back.
   *
   * Sky visibility is a ONE-SIDED term and this is the trap in it: on ground that is
   * flat to begin with, every direction a facet can lean is a direction that sees less
   * sky, so the average of the term over any patch of relief is negative and the whole
   * hillside quietly gets darker. Measured on the first pass: -10.5% of the forest
   * floor's mean value, in both clear and overcast. That is a real physical effect —
   * rough ground genuinely has a lower effective albedo under a sky than smooth ground —
   * but it is a MEAN, and the mean is already the business of `cavity` and `crease` two
   * hundred lines above. Taking it twice is how a shaping term turns into an exposure
   * change, which is the one thing this round must not do.
   *
   * The isotropic part is analytic: averaged over the azimuth of a tilt of magnitude T,
   * (1 + N.y) comes to (1 + gN.y * cos T), so (1 - cos T) * gN.y / (1 + gN.y) is exactly
   * the amount to give back. 0.7 of it leaves a quarter of the physical darkening in
   * place — rough ground still reads a little heavier than smooth — and takes the mean
   * shift to under 3%.
   */
  tiltRestore: 0.35,
  /**
   * Directional lobe: the sky is not a uniform dome and shade is not a uniform light.
   *
   * Under a clear sky the bright part of what a shaded patch of ground can see is the
   * circumsolar quadrant and the open sky above it; in a forest the second source is
   * warm bounce off the sunlit trunks and ground beyond the shadow. Both point the same
   * way in azimuth — toward the sun — and both are well up from the horizon. One dot
   * product against that direction gives every dome a light side and a dark side *in
   * shade*, which is the cue the critic correctly said was entirely missing.
   *
   * Mean-preserving against the geometric normal, so it tilts the light across relief
   * without brightening or darkening the ground it stands on.
   *
   * Half what the first pass used, because the measurement above says the probe already
   * does part of this job — +-20% at 45 degrees of tilt, azimuthally — and the term that
   * was genuinely missing is `tilt`, not this one. What this adds over the probe is the
   * behaviour at a LOW sun, where `keyDir` swings out to 55-60 degrees off vertical and
   * skylight really does become directional; at the noon sun the measurement was taken
   * at, keyDir is within 15 degrees of straight up and this quietly folds into `tilt`.
   */
  lobe: 0.8,
  /** How far up from the sun's own azimuth the ambient centroid sits, in units of the
   *  horizontal component. 1.3 puts it about 50 degrees up for a low sun. */
  lobeLift: 1.3,
  /**
   * THE TERM THAT DOES THE WORK: where in its own relief this fragment sits.
   *
   * `tilt` and `lobe` above are both functions of the facet's SLOPE, and slope turns out
   * to be a weak handle on sky visibility — the sky-view factor of a tilted plane is
   * (1 + cos T)/2, which for a 26 degree flank is 0.95. Correct, and worth 5%. Measured
   * on the forest floor, the first build of this block moved 17% of the frame's pixels by
   * more than 2% and had an RMS effect of 7%, which is not a gap being closed.
   *
   * What a hollow in real ground actually loses is not its own cosine — it is the sky its
   * NEIGHBOURS are standing in. That is an occlusion, it is a function of height rather
   * than of slope, and for a zero-mean relief field the height is already the answer:
   * mounds see more than the mean, hollows see less, and the average over any patch is
   * exactly the mean by construction. It is 90 degrees out of phase with the two slope
   * terms — they light one flank and darken the other, this lights the tops and buries
   * the bottoms — so the two together read as relief rather than as either one twice.
   *
   * 0.32 says the floor of a hollow one full amplitude down sees a third less sky than
   * the crest beside it, which is about right for the 1-6 m relief this is fed from and
   * is the single largest contributor to shaded ground having form at all.
   */
  height: 0.75,
  /**
   * Pixel-scale grain: a crown sees the sky, a pit does not. Signed, so mean-preserving.
   *
   * Deliberately the SMALLEST of the shaping terms, and it was three times this on the
   * first calibrated build. The field behind it is the screen-locked mottle, which is
   * value noise on a rotated square lattice — and the moment you amplify it enough to
   * read, what reads is the lattice. Measured on the `shadecam` frame at 0.38: the near
   * ground came out in hard-edged polygons, which is the crazy-paving failure this file
   * demoted the mottle for in the first place. Form is carried by `height` (world-scale)
   * and `crown` (objects); this is only here to keep the gaps between them from going
   * dead flat.
   */
  grainMicro: 0.12,
  grainMeso: 0.06,
  /**
   * The clast crown, moved off the albedo.
   *
   * It used to be `albedo *= 1 + crown * 0.16` — a paint mark, identical in sun, in
   * shade and at night, which is precisely the "featureless interior" the ring was drawn
   * around. The top of a dome sees the whole sky and the ground beside it does not; that
   * is an irradiance statement, and it belongs here. A third of the old amount stays on
   * albedo because a proud crown really is scoured cleaner than the fines around it.
   *
   * WAVE 4 ROUND 2 — 0.8 -> 0.34, AND THE DIFFERENCE MOVES TO `body`. NOT AN AMPLITUDE
   * CUT: A REDISTRIBUTION, and the integrals say it was the shape that was wrong.
   *
   * Integrated over the disc, the crease ring covers 0.463 of R^2 and this crown covers
   * 0.49 — but `crown` is `smoothstep(0.16, 0.86, dome)`, which is the middle 37% of the
   * object's RADIUS. So 0.8 was putting the entire answer to a ring 70-118% of the way out
   * into a pip in the middle, and leaving the flank between them — most of the object's
   * visible area — with no light-independent cue at all. In sun the dome normal fills that
   * flank. In shade, and on any surface whose entitlement takes the dome normal down (the
   * built tread takes it to 14%), nothing does. The result is a bright dot inside a dark
   * ring inside a featureless annulus, which is exactly the "hollow dark elliptical
   * outlines with featureless interiors" the round-1 critic measured.
   *
   * Checked against the mean, because moving weight around a profile is how an exposure
   * change gets in: in shade the old crown delivered (0.06 albedo + 0.8 ambient) * 0.343
   * = 0.295 of lift against 0.172 of ring, i.e. clasts made shaded ground 12% BRIGHTER on
   * average. The new pair delivers 0.36 * 0.343 + 0.34 * 0.463 = 0.281 — the same total to
   * within 5% — spread over the whole disc instead of over a pip.
   */
  crown: 0.34,
  /**
   * The clast BODY: an object standing proud of the ground sees more sky than the ground
   * does, over all of it, not only at its summit.
   *
   * Sized so that `bodyIntegral * bodyCoefficient` equals `ringIntegral *
   * ringCoefficient` per layer — the body coefficients in the shader are the crease
   * coefficients times 0.58, which is 0.463/0.80 — and then this is the per-unit strength.
   * At 0.34 a clast is very nearly mean-preserving in flat overcast, which is the
   * condition under which the ring had no answer at all.
   */
  body: 0.34,
  /** Litter strokes, given a lit side and a shaded side instead of being pure paint. */
  litter: 0.3,
  /**
   * Chroma swing between the sky's own colour on the up-facing side of a bump and the
   * ground bounce on its underside, at constant luminance.
   *
   * This is the cheapest thing in the file and it is most of what stops shaded ground
   * reading as ink: a shadow outdoors is lit by the sky and by what the sky bounced off,
   * so its highlights are blue and its undersides are warm. Luma-normalised on both
   * sides so it is a pure hue rotation and cannot move the value structure.
   */
  chroma: 0.6,
  /**
   * The cliff-band section, as an ambient event as well as an albedo one.
   *
   * The three members are a partition of a band and have to stay balanced, so these keep
   * the albedo block's ratios exactly (crest = lip + riser) and only the scale differs.
   * An undercut sees less sky and a crest sees all of it, which is true in overcast, in
   * shade and at night — unlike the albedo copy, which is true always and therefore
   * true nowhere.
   */
  bandLip: 0.3,
  bandRiser: 0.11,
} as const;

/**
 * Where the cover patches live and how thick they are.
 *
 * Split out from the shader because it is the round's headline change and the numbers
 * want to be readable. `margin` and `breakAmp` are in *field* units: the cover field's
 * gradient is around 0.07 per metre, so 0.030 is a margin roughly 45 cm of ground wide
 * and 0.070 lets the boundary wander about a metre either side of it.
 */
export const TerrainCover = {
  /** Metres. Patch size of the shared `rlfCoverField`. */
  patchM: 34,
  /** Decorrelates our cover from any other consumer's. */
  seed: 3.7,
  /** Feather half-width, field units. THE art width — not a pixel. */
  margin: 0.03,
  /** Cycles/m of the margin break: ~1.3 m fingers. */
  breakFreq: 0.75,
  /**
   * How far the margin wanders, field units.
   *
   * Has to be read against the cover field's own spread, which is a standard deviation
   * near 0.085 — at the 0.07 the first pass shipped, the break was almost as large as the
   * whole field and it punched holes through the middle of every mat instead of only
   * disturbing its edge. A false-colour dump of the mask showed exactly that: patches
   * shot through at 1.3 m. 0.05 is still ~0.8 m of wander at the boundary and leaves the
   * interior solid.
   */
  breakAmp: 0.05,
  /** Duff depth, metres: needle litter through to a moss bolster. */
  depthDry: 0.045,
  depthLush: 0.055,
  /** Contact darkening at the rim, 0..1. */
  occ: 0.55,
  /** Fraction of the mineral weights the mat interior takes. Partial on purpose. */
  take: 0.62,
  /** How hard a proud clast pushes the mat back off itself. */
  poke: 0.82,
  /**
   * Distance ramp, metres. Much closer than the structure ramp, and that is deliberate.
   *
   * The cliff bands are held out of the near field because the real geometry is
   * describing the ground there and a virtual bench would fight it. A duff mat is the
   * opposite case: it is a real object lying on real ground, its margin is 45 cm across,
   * and every cue this round added — the fingered edge, the rim shoulder, the contact
   * band, the pebbles standing through it — is only legible inside about 30 m. The first
   * pass ramped 4-16 m, and the false-colour dump showed the whole rideable foreground
   * carrying no cover at all, which is where a rider spends the entire game looking.
   */
  near: 2,
  far: 10,
  /**
   * Where the contact band stops being a contact band.
   *
   * `rlfCoverBlend` floors its feather at 1.5 px so the rim survives to any range, and on
   * flat-on ground that is right. On a hillside seen at a grazing angle it is not: fwidth
   * is dominated by the near-vertical screen axis, so the band is a couple of pixels wide
   * measured *down* the gradient and can be a hundred wide across it, and the far hill
   * comes out in dark swathes. Measured off the mask dump at 200-500 m the rim covered as
   * much ground as the cover did. So it fades on the Nyquist limit of its own width —
   * a 45 cm feature at 2.5 cycles/m — which puts it gone by roughly 200 m, past which
   * there is nothing there to have contact with anyway.
   */
  occFadeFreq: 2.5,
} as const;

/**
 * GLSL float literal. `smoothstep(4, 16, d)` does not compile — GLSL has no implicit
 * int->float promotion in an overload match — so every number interpolated into the
 * shader below goes through this.
 */
const gl = (v: number): string => (Number.isInteger(v) ? v.toFixed(1) : String(v));

// ===========================================================================
// WAVE 5: THE PALETTE AUTHORITY OWNS THE SURFACE COLOURS. THIS FILE OWNS THE LIGHT.
//
// The carried gap, in one sentence: "surface separation is INVERTED across distance."
// Inside 100 m — 52-80% of every frame — the two dominant classes this material's own
// classifier marks distinct arrived one flat mid-brown wash; at 100-200 m the same
// classifier separated the same members by 218 degrees of hue into an orange / lavender /
// chartreuse camouflage patchwork. Washed out where the player looks, garish where they
// do not.
//
// Both halves have the same cause and it is not a tuning error. It is that this file was
// authoring colour twice, in two places, with two different mechanisms:
//
//   1. SEVEN HAND-AUTHORED `gRamp3` TRIPLES, twenty-one linear-RGB constants, retuned
//      independently five times across five rounds by five agents each looking at one
//      distance band. Their MID stops happened to land in a sensible order; their SPANS
//      sat on top of each other (hardpack ran L 0.35-0.75, rock L 0.34-0.72), so no
//      amount of core separation could survive. That is exactly the failure
//      `PaletteEntry.lSpan` was invented to stop, and the palette's own header names this
//      file's ramps as the thing it names it about.
//
//   2. A FAR-FIELD EXPANSION THAT ROTATED HUE. `albedo += (albedo - 0.185) * 0.62` is a
//      PER-CHANNEL expansion about a scalar mid in linear RGB. Any channel below the mid
//      is pushed toward — and past — zero, then clamped; the surviving channels are not,
//      so the residual's DIRECTION rotates. Rock (0.087, 0.107, 0.126) at full strength
//      goes (0.026, 0.098, 0.158): a 3.2:1 blue channel ratio that started at 1.45:1.
//      That is the 218 degrees, and it is a mechanism, not a coincidence.
//
// So: `src/render/palette.ts` is now the author of every ground colour in this file, and
// this file's job is what it should always have been — form, light, contact and grain.
// Every constant below comes out of a `PaletteEntry`, baked at build time so the shader
// pays nothing for it (see `palRamp` — an OKLab round trip per surface per pixel would
// have been seven of them, which is why `palShade` is called on the CPU, not in GLSL).
// The far expansion is now `palFarSeparate`, which is bounded in L, capped in chroma and
// chroma-CLIPPED rather than channel-clamped, and so cannot rotate a hue at all.
//
// If a colour in this file looks wrong, the fix is in palette.ts. Do not re-add a local
// constant; that is how there came to be five palettes and no author.
// ===========================================================================

/** A linear-sRGB triple as a GLSL literal. */
const glv = (c: RGB | readonly number[]): string =>
  `vec3(${c[0].toFixed(5)}, ${c[1].toFixed(5)}, ${c[2].toFixed(5)})`;

/**
 * A `gRamp3` triple straight off a palette entry: the entry at its own rung, plus and
 * minus its own authored `lSpan` in OKLab L and NOTHING else.
 *
 * This is `palShade()` evaluated at t = 0, 0.5, 1 on the CPU. Same semantics, same
 * numbers, zero per-pixel cost — the shader keeps its existing three-stop `mix`, which is
 * a linear interpolation between three points that are already on the entry's own L axis.
 *
 * `k` widens the span for a layer that is a genuinely different object lying ON the
 * surface rather than the surface itself (a stone, a clod). It is never used on a body
 * ramp: the body is the rung, and `PaletteLimits.spanClearance` guarantees two rungs'
 * bodies leave 15% of their gap clear only if nobody widens it.
 */
function palRamp(e: PaletteEntry, k = 1): [string, string, string] {
  const span = e.lSpan * k;
  const at = (L: number): string => glv(gamutClip(oklch(Math.max(L, 0.02), e.C, e.h)));
  return [at(e.L - span), at(e.L), at(e.L + span)];
}

/** A palette entry as a GLSL literal, optionally moved in L (OKLab) and scaled in chroma. */
function palAt(e: PaletteEntry, dL = 0, cGain = 1): string {
  return glv(gamutClip(oklch(
    Math.min(Math.max(e.L + dL, 0.02), 0.985),
    Math.min(e.C * cGain, PaletteLimits.accentChromaCeiling),
    e.h,
  )));
}

/** Blend two entries in linear light (never by rotating hue — see palette.ts:732). */
function palMix(a: PaletteEntry, b: PaletteEntry, t: number, dL = 0): string {
  const ca = gamutClip(oklch(a.L, a.C, a.h));
  const cb = gamutClip(oklch(b.L, b.C, b.h));
  const m: RGB = [
    ca[0] + (cb[0] - ca[0]) * t,
    ca[1] + (cb[1] - ca[1]) * t,
    ca[2] + (cb[2] - ca[2]) * t,
  ];
  if (dL === 0) return glv(m);
  const lab = linearToOklab(m[0], m[1], m[2]);
  const C = Math.hypot(lab[1], lab[2]);
  const h = (Math.atan2(lab[2], lab[1]) * 180) / Math.PI;
  return glv(gamutClip(oklch(Math.max(lab[0] + dL, 0.02), C, h)));
}

/**
 * How far dry bunchgrass is pulled back down toward its own rung after the hue walk.
 *
 * Measured: `palMix(grass, hardpack, 0.42)` lands at L 0.562 against hardpack 0.598.
 * -0.030 restores the authored 0.066 step (grass 0.532, hardpack 0.598), which is
 * `PaletteLimits.minValueStep` plus 20% of margin, while leaving the hue at 95 degrees —
 * still 27 degrees off hardpack's 68, so the pair separates in both axes rather than
 * relying on either alone.
 */
const GRASS_DRY_DL = -0.030;

// The seven body ramps, and every object layer that lies on them. One place, all of it
// from the ladder. Read this block instead of hunting twenty-one constants through the
// recipes below.
const P = {
  hard: palRamp(Ground.hardpack),
  loam: palRamp(Ground.loam),
  loose: palRamp(Ground.loose),
  rock: palRamp(Ground.rock),
  root: palRamp(Ground.root),
  grassLush: palRamp(Ground.grass),
  // Dry bunchgrass is the same plant with the water gone: yellower, and on its way to the
  // hardpack it is standing in. Built from two ladder entries rather than invented, so it
  // cannot walk out of the authored set.
  //
  // WAVE 5 R2 — IT WAS ALSO WALKING OFF ITS OWN RUNG, and that is a measured 0.030 of the
  // near field's missing separation. `palMix(grass, hardpack, 0.42)` moves BOTH hue and
  // lightness 42% of the way to hardpack: the mid stop landed at OKLab L 0.562 against
  // hardpack's 0.598 — 0.036 apart, under the 0.055 ladder step, before any light falls on
  // it. Since dry grass is the near field's second most common class on open ground, the
  // pair the invariant measures was authored below its own budget.
  //
  // `DRY_DL` puts the lightness back on grass's rung while the hue walk is untouched, so
  // dry heath is still a yellow-olive straw and is still a whole ladder step darker than
  // the pack it stands in. It is a correction to a hue interpolation's side effect, not a
  // new colour: every stop is still a linear blend of two authored entries.
  grassDryLo: palMix(Ground.grass, Ground.hardpack, 0.42, -Ground.grass.lSpan + GRASS_DRY_DL),
  grassDryMid: palMix(Ground.grass, Ground.hardpack, 0.42, GRASS_DRY_DL),
  grassDryHi: palMix(Ground.grass, Ground.hardpack, 0.42, Ground.grass.lSpan + GRASS_DRY_DL),
  snow: palRamp(Ground.snow, 2.2),
  // --- object layers lying on the ground ---------------------------------------
  /** Clay-dusted grit on the built line: hardpack's own rung, slightly down in L. */
  gritHard: palAt(Ground.hardpack, -0.055),
  /** Clods of turned earth in loam / root: the entry, lifted a rung's worth. */
  clodLoam: palAt(Ground.loam, 0.075),
  clodRoot: palAt(Ground.root, 0.055),
  /** Scree stones: loose's own rung, spread by gStoneTint. */
  stoneLoose: palAt(Ground.loose, -0.030),
  /** Blocks spalled off a face: rock's rung, lifted. */
  blockRock: palAt(Ground.rock, 0.075),
  /** Buried mossy stone under duff. */
  stoneLoam: palAt(Ground.rock, -0.055),
  /** Stones pushing through heath. */
  stoneGrass: palAt(Ground.rock, 0.045),
  /** Needle litter and duff — the authored forest-floor entry, not a local brown. */
  duff: palAt(Flora.duff),
  duffDry: palAt(Flora.duff, 0.030, 1.15),
  duffLush: palMix(Flora.duff, Flora.understory, 0.55),
  /** Dead thatch in dry heath. */
  thatch: palAt(Flora.duff, 0.060),
  /** Moss fleck on a forest floor. */
  moss: palAt(Flora.understory, -0.070),
  /** Root strand and knuckle: weathered polished wood, the authored entry. */
  rootStrand: palAt(Flora.deadwood),
  rootKnuckle: palAt(Flora.deadwood, -0.040, 1.4),
  /** Iron oxide on a weathered band — one of the palette's two sanctioned accents. */
  oxide: palAt(Accent.oxide),
  /** Lichen on rock — the other one. */
  lichen: palAt(Accent.lichen),
  /** A glint in granite. Value only, effectively no hue. */
  feldspar: palAt(Accent.feldspar),
  /** The same glint dusted with clay on a ridden line — same L, hardpack's hue. */
  feldsparDusted: palMix(Accent.feldspar, Ground.hardpack, 0.34),
} as const;

// ---------------------------------------------------------------------------
// THE SEPARATION INVARIANT — declared here, measured in the FINISHED FRAME.
//
// See `installTerrainAudit()` at the bottom of this file for the instrument and for the
// argument that it can actually fail. The short version: the previous three metrics in
// this area all measured the CLASSIFIER (a false-colour weight dump), which by
// construction separates perfectly — that is what a classifier does — and told four
// rounds in a row that the materials "ARE distinct out there". They were. The pixel was
// not. So this one reads the canvas after post has drawn to it, attributes each pixel to
// a class with a second pass through the same camera, and states the budget on what a
// critic can pick off a screenshot.
// ---------------------------------------------------------------------------

/**
 * The near-field lightness separation the two dominant ground classes must arrive with.
 *
 * 0.055 in OKLab L is `PaletteLimits.minValueStep` — one rung of the authored ladder,
 * i.e. the palette's own promise, restated on the delivered pixel instead of on the
 * albedo. Measured before this round: 0.021. (The critique's "V 0.239 vs 0.219" is the
 * same quantity in HSV; 0.020 of HSV V at that brightness is 0.021 of OKLab L.)
 */
export const NEAR_SEP_MIN_L = PaletteLimits.minValueStep;
/**
 * ...and in hue, degrees, for pairs that are close in value.
 *
 * Only checked where BOTH classes carry enough chroma for a hue to be readable
 * (`SEP_CHROMA_GATE`), for the reason `AerialTransport.hueLockChromaGate` gives: a hue
 * check without a chroma gate reports colour-space curvature as a defect, which is how
 * three previous metrics in this project produced confident wrong answers.
 */
export const NEAR_SEP_MIN_HUE = 18;
/**
 * Below this OKLab chroma a pixel is within a few code values of neutral and its hue is
 * not readable.
 *
 * Taken from the palette's own rule rather than invented: `AerialTransport.hueLock` is
 * only checked above 0.6 of the horizon's chroma, and the clear-day horizon is authored at
 * C 0.030, which puts the gate at 0.018. Stated at 0.020 here. The reason the palette
 * gives is exactly the failure this instrument would otherwise have: a hue check without a
 * chroma gate reports colour-space curvature as a defect, and a deep-shade pixel lit by a
 * blue sky is a couple of code values off neutral and can carry any hue at all. Measured
 * at 0.012 this metric reported 170 degrees of far-field spread between two classes whose
 * authored hues are 14 degrees apart — the spread was in the shadows, not in the paint.
 */
export const SEP_CHROMA_GATE = 0.020;
/**
 * The far-field ceiling — the other half of the inversion.
 *
 * The spread of hue across the ground classes at 100-200 m may not EXCEED this. 218
 * degrees was measured; 60 is a warm/cool split you can still read as one mountain.
 * Stated as a max, because the defect out there is too much separation, not too little.
 */
export const FAR_HUE_SPREAD_MAX = 60;
/**
 * HOW MANY GROUND CLASSES MUST REACH THE NEAR FIELD AT ALL.
 *
 * The invariant this round exists for, and the one whose absence let round 1 report a
 * clean sheet on a mountain that arrives as one material.
 *
 * The two separation invariants above compare THE TOP TWO classes. On a frame that
 * contains only one, `near.length >= 2` is false, nothing is observed, and SUMMARY.md
 * prints "declared but never observed (owner has not wired it up)" — which is what the
 * shipping build did on `vista`, `rockcam` and `wetvista` alike. A metric that goes silent
 * exactly when the defect is total is worse than no metric: it reads as an owner who never
 * connected it, and a critic has to go and find the defect by hand. That is the third of
 * the three wave-4 instrument failures docs/AGENTS.md names, in a new costume.
 *
 * So this counts, and the two above now observe 0 rather than nothing when there is no
 * second class (see `installTerrainAudit`).
 *
 * THE BUDGET IS 3 AND IT COMES FROM BAR, not from taste: "hardpack vs loam vs loose vs
 * rock vs root separate by value and hue alone, before detail resolves" names five ground
 * materials that must be legible. Three is the floor at which a single frame can be said
 * to show a mountain made of more than one thing — a dominant ground, a second material,
 * and the mineral or organic accent that says which way the hill is working. Measured
 * before this round: 1 on `rockcam`, 2 on `vista`, and both of the two always drawn from
 * {hardpack, grass, loam}.
 */
export const NEAR_CLASS_MIN = 3;

// --- the attribution channel's wire format (debug view 10) -------------------------
// Eight-bit, because the tile is a plain UnsignedByte render target and a float one costs
// four times the readback for a quantity with seven possible values. The codes are spaced
// 0.10 apart — 26 code values — so nothing in this encoding is within rounding distance of
// anything else in it.
const DBG_CLASS_BASE = 0.16;
const DBG_CLASS_STEP = 0.10;
/**
 * Below this dominant weight the pixel is a BOUNDARY, and a boundary pixel is not evidence
 * about either of the materials meeting there — it is a mixture of both, by construction
 * (see the height blend). Measured at 0.42 the near-field populations came back with grass
 * arriving BRIGHTER than hardpack, the reverse of the authored ladder, because a
 * "grass-dominant" pixel at 45% grass carries 55% of whatever it is interlocking with, and
 * on open ground that is usually `loose` — the palest entry on the ladder. Raising the
 * gate measures the two materials rather than the seam between them.
 */
const DBG_CLASS_MIN_W = 0.62;
/** The G channel's full scale, metres. 1 m per code value at 8 bits. */
const DBG_CLASS_DIST_M = 255;

const NEAR_SEP_L = declareInvariant({
  id: 'terrainshading.nearClassSeparationL',
  owner: 'terrain-shading',
  what: 'OKLab lightness between the two dominant ground classes inside 100 m, in the finished frame',
  unit: '1',
  budget: NEAR_SEP_MIN_L,
  compare: 'min',
  severity: 'error',
  phase: 'run',
  consequence: 'the ground the rider is looking at is one flat mid-brown wash and no surface reads as a surface',
  source: 'src/render/terrainMaterial.ts NEAR_SEP_MIN_L (= palette.ts PaletteLimits.minValueStep)',
});

const NEAR_SEP_H = declareInvariant({
  id: 'terrainshading.nearClassSeparationHue',
  owner: 'terrain-shading',
  what: 'hue between the two dominant ground classes inside 100 m when they are close in value, finished frame',
  unit: 'deg',
  budget: NEAR_SEP_MIN_HUE,
  compare: 'min',
  severity: 'warn',
  phase: 'run',
  consequence: 'two materials at the same value and the same hue are one material',
  source: 'src/render/terrainMaterial.ts NEAR_SEP_MIN_HUE',
});

const NEAR_CLASS_N = declareInvariant({
  id: 'terrainshading.nearClassCount',
  owner: 'terrain-shading',
  what: 'distinct ground classes holding a readable share of the near field in the finished frame',
  unit: 'count',
  budget: NEAR_CLASS_MIN,
  compare: 'min',
  severity: 'error',
  phase: 'run',
  consequence: 'the mountain arrives as ONE material — rock, loose and root never reach the frame at any distance',
  source: 'src/render/terrainMaterial.ts NEAR_CLASS_MIN (docs/BAR.md, Terrain shading)',
});

/**
 * ...and the instrument's own bill, on the same bus as everything it measures.
 *
 * An instrument that is not itself audited is how `camera.pixelProbeMs` came to be running
 * at 112 ms a sample — 28x its own stated budget — inside the frame budget every other
 * piece is graded on.
 *
 * WHAT THIS NUMBER IS, because it is a headless number and quoting it as a shipping cost
 * would be its own measurement bug. It is the MARGINAL cost past the driver flush (see the
 * note in `update`): a 1600x900 canvas readback and a 320x180 re-render of the scene with
 * the attribution shader. In the harness's software rasteriser that is 10-25 ms; the same
 * work on a GPU is a ~0.4 ms buffer copy and a 57k-fragment pass. It runs on one frame in
 * SEP_PERIOD_S — one in 240 at 60 fps — so even taken at the headless figure it is under
 * 0.01% of the frame budget, and it never lands on two consecutive frames.
 */
export const SEP_COST_MS_MAX = 25;

const SEP_COST = declareInvariant({
  id: 'terrainshading.sepAuditMs',
  owner: 'terrain-shading',
  what: 'CPU cost of one separation measurement, including the canvas read and the attribution pass',
  unit: 'ms',
  budget: SEP_COST_MS_MAX,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'a measuring hitch the player feels, in a frame budget shared with eight other pieces',
  source: 'src/render/terrainMaterial.ts SEP_COST_MS_MAX',
});

const FAR_SPREAD_H = declareInvariant({
  id: 'terrainshading.farHueSpread',
  owner: 'terrain-shading',
  what: 'total hue spread across the ground classes at 100-200 m, in the finished frame',
  unit: 'deg',
  budget: FAR_HUE_SPREAD_MAX,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the mid distance reads as an orange/lavender/chartreuse camouflage patchwork, not as a mountain',
  source: 'src/render/terrainMaterial.ts FAR_HUE_SPREAD_MAX',
});

// ---------------------------------------------------------------------------
// Shared GLSL: hashing, derivative value noise, rotated fBm, triplanar, clasts.
// ---------------------------------------------------------------------------
const TERRAIN_COMMON = /* glsl */ `
varying vec3 vWPos;
varying vec3 vWNrm;
varying float vWDist;

uniform float uTime;
uniform float uWetness;
uniform float uRelief;
/**
 * Metres of texel footprint per metre of view distance: 2*tan(fovY/2) / drawingBufferHeight.
 *
 * THE ONE UNIFORM ADDED THIS ROUND, and it exists because of an artefact the frame showed
 * and no number did. See gReliefAmpS below.
 */
uniform float uPxPerM;
// A/B handle for the sky term below. 1 = live, 0 = the build this round started from.
// Not a feature switch: it exists because every measurement in this file's ambient block
// was taken while eight other agents were editing the same tree, and a before/after run
// separated by two minutes of someone else's commits is not a measurement of anything.
// One uniform makes the comparison happen inside a single page session.
uniform float uAmbAmt;
// ---------------------------------------------------------------------------
// THE A/B RIG. Wave 4, and it is the only reason this round could answer "which of the
// four things in the far field is the artefact" in one afternoon rather than in four
// builds. Nine agents edit this tree at once, so two captures taken two minutes apart are
// not a comparison of anything; every A/B below therefore happens inside one page session
// through window.__gripTerrainShading.
//   uDbgA.x  block-clast layer            uDbgA.y  cliff bands (all members, all channels)
//   uDbgA.z  cover mats (duff + mineral)  uDbgA.w  drainage
//   uDbgB.x  far form fields (meso + coarse + ridge)
//   uDbgB.y  false-colour view: 0 off, 1 surface weights, 2 raw albedo, 3 N.sun only,
//            4 distance rings
//   uDbgB.z  bedding-plane face gate   uDbgB.w  landform-scale talus cover
// All default to (1,1,1,1) / (1,0,1,1), so the shipped image is byte-identical to one with
// the rig removed — every use is a multiply by 1.0 that the compiler folds away.
//   uDbgC.x  the landform light channel    uDbgC.y  far-field material separation
// Both default to 1. They are round 8's two changes and they are the two things a critic
// will want to switch off inside one page session rather than across two builds.
//   uDbgD.x  hardpack's clay-dusted grit tints (0 = round 2's neutral greys)
//   uDbgD.y  the crease ring's super-linear entitlement (0 = round 2's linear relV)
//   uDbgD.z  the 300 m mass band
// Round 3's three changes, each switchable on its own inside ONE page session. Two of the
// three are palette/constant edits rather than layers, and without a switch the only way
// to price them is to compare two builds of a tree that nine agents are editing — which is
// how wave 3 shipped a wrong conclusion. All default to 1.
uniform vec4 uDbgA;
uniform vec4 uDbgB;
uniform vec4 uDbgC;
uniform vec4 uDbgD;
uniform vec4 uDbgE;

// False-colour output, written only when uDbgB.y > 0.5 and consumed after tone mapping so
// the dump is the raw quantity rather than the graded one.
vec3 gTerrDbg;

// ---------------------------------------------------------------------------
// THE AMBIENT CHANNEL. A multiplier on indirectDiffuse ONLY, written at the end of the
// shade block and consumed by our own line after relief.ts's AO patch.
//
// It is a fourth channel rather than more gRlfOcc for one reason, and it is the reason
// this round exists. gRlfOcc is an occlusion: it is clamped at 1, so it can only ever
// subtract, and the AO patch deliberately spends 65% of it on the direct term as well.
// What the shaded half of the mountain was missing is the opposite of an occlusion — a
// *lift* on the sky-facing side of every piece of relief, balanced against the darkening
// on the away side, applied to the sky term and to nothing else. That is what a dome in
// open shade actually looks like, and there was no channel in this shader that could
// express it.
//
// Being ambient-only is also what makes it self-gating, which is why it needs no
// knowledge of the shadow map or of the terrain occlusion field (neither of which is
// even in scope this early in the fragment shader): in full sun the indirect term is
// roughly a quarter of the light, so a +-30% shaping of it moves a sunlit pixel by a few
// per cent; in shade the indirect term is *all* of the light and the same expression is
// the entire read. One set of constants, correct in both.
// ---------------------------------------------------------------------------
vec3 gTerrAmb;

// ---------------------------------------------------------------------------
// THE LANDFORM LIGHT CHANNEL. A signed multiplier on the DIRECT term (and on a third of
// the indirect), written at the end of the shade block from the landform-scale relief and
// from nothing else.
//
// WAVE 4 ROUND 8 — WHY A FOURTH CHANNEL, AND WHY IT IS NOT THE COSINE AGAIN.
//
// The carried gap says "no gully, rib or spur carries any shade out there". Measured on
// the 300-500 m band (scenario farhilldbg, view 3 = N.sun off the FINAL perturbed
// normal, dumped past the fog and past ACES): p95/p5 = 1.34, sd 11% of the mean. Eleven
// per cent is the entire directional light variation on a hillside that fills a third of
// the frame, and the tilt dump (view 5) beside it is *busy* — there is plenty of relief
// out there, it simply produces no light.
//
// The reason is written in this file already and it is right: N.sun has a median near
// 0.85, so the ground sits at the flat maximum of the cosine and ten degrees of relief
// moves the direct term two or three per cent. The wrong conclusion drawn from it last
// round was "so form cannot carry this, drive the material instead". The right one is
// that the cosine is a *bad transfer function* for landform at this range, not that the
// landform is absent. A real hillside at 300 m does not read as a smooth cosine ramp
// either: it reads as lit ribs against shaded gullies, because at that scale every
// sunward flank also collects direct sun on its sub-resolution roughness and every lee
// flank is partly occluded by the rib above it. Both effects are S-shaped in the flank
// angle, and neither is in a Lambert dot product.
//
// So this channel takes the landform's OWN contribution to N.sun — the difference the
// 24 m form field, the 90 m spur band and the incised drainage make, and nothing else —
// normalises it by how much sun this ground receives at all, and puts it through a
// saturating odd sigmoid. Odd, so it is mean-preserving by construction and cannot
// darken or lift the hillside; saturating, so a flank does not have to reach 60 degrees
// to read; and driven by the sun vector, so it moves when the sun moves, inverts at
// evening, and is worth exactly nothing at night — which is what separates it from
// painting the shade into albedo.
//
// The cliff bands are deliberately NOT one of its inputs. They are iso-elevation by
// construction, and iso-elevation banding is the pattern this whole round exists to
// displace; amplifying them would be the opposite of the brief.
// ---------------------------------------------------------------------------
float gTerrLand;

// ---------------------------------------------------------------------------
// The hash, the derivative value noise and the rotated fBm used to live here, in a
// private copy that was character-for-character the same as relief.ts's. Two identical
// copies of a noise basis is not a saving, it is a way for the terrain and everything
// drawn on top of it to drift apart one edit at a time — a cover patch placed by
// vegetation with rlfFbm and one placed here with gFbm would eventually stop agreeing
// about where the duff is. Aliased rather than renamed at ~90 call sites, and G_ROT is
// RLF_ROT down to the last digit, so this is provably a no-op today.
// ---------------------------------------------------------------------------
#define gHash21 rlfHash21
#define gHash22 rlfHash22
#define gNoiseD rlfNoiseD
#define gFbmD   rlfFbmD
#define G_ROT   RLF_ROT

// Triplanar fBm. Returns (value 0..1, world-space gradient.xyz). Planes whose blend
// weight is negligible are skipped, so flat ground pays for one projection, not three.
vec4 gTriFbm(vec3 p, vec3 bw, float freq, int oct, float gain) {
  vec4 r = vec4(0.0);
  if (bw.y > 0.003) {
    vec3 n = gFbmD(p.xz * freq, oct, gain);
    r += bw.y * vec4(n.x, n.y * freq, 0.0, n.z * freq);
  }
  if (bw.x > 0.003) {
    vec3 n = gFbmD(p.zy * freq + 19.7, oct, gain);
    r += bw.x * vec4(n.x, 0.0, n.z * freq, n.y * freq);
  }
  if (bw.z > 0.003) {
    vec3 n = gFbmD(p.xy * freq + 51.3, oct, gain);
    r += bw.z * vec4(n.x, n.y * freq, n.z * freq, 0.0);
  }
  return r;
}

// ---------------------------------------------------------------------------
// Screen-locked detail.
//
// Kept from round 2, but demoted. A layer of noise at a fixed world frequency is only
// visible in one band of distance; a screen-locked one is present at every distance,
// because the caller picks the octave whose period lands on a target number of pixels
// and this cross-fades between it and the next exactly the way trilinear filtering does.
// That is genuinely useful as the *matrix* between objects. What it cannot do — and what
// R2 asked it to do — is be the whole surface: it has no world scale, so it contains no
// objects, and a field of pure value at a grazing angle filters into a directional smear.
//
// The renormalisation matters. A plain mix() of two independent noise fields loses
// 29% of its contrast at the halfway point, which would put a visible band of "less
// grainy" ground at every octave crossover. Dividing by the RMS of the blend weights
// keeps the variance flat right through the transition.
// ---------------------------------------------------------------------------
// Two more rotations. These are not decoration: value noise lives on a square lattice,
// and two lattices at exactly 2:1 with the same orientation beat into a rectilinear
// plaid — which on screen reads as *digital camouflage*, blocky and unmistakably
// synthetic. Giving the two bracketing octaves different irrational rotations, and
// keeping both off the world axes, is what turns the same two taps into grain.
const mat2 G_ROTA = mat2(0.91816, 0.39620, -0.39620, 0.91816);   // ~23.3 deg
const mat2 G_ROTB = mat2(0.52250, 0.85264, -0.85264, 0.52250);   // ~58.5 deg

// One rotated octave, gradient returned in the caller's (unrotated) frame:
// for q = R*(p*fr), d/dp = fr * transpose(R) * d/dq.
vec3 gLockTap(vec2 p, float fr, mat2 rot) {
  vec3 n = gNoiseD(rot * (p * fr));
  return vec3(n.x, fr * dot(rot[0], n.yz), fr * dot(rot[1], n.yz));
}

vec3 gLocked(vec2 p, float fa, float fb, float f) {
  vec3 a = gLockTap(p, fa, G_ROTA);
  vec3 b = gLockTap(p + 23.71, fb, G_ROTB);
  float w = inversesqrt(f * f + (1.0 - f) * (1.0 - f));
  return vec3(
    0.5 + mix(a.x - 0.5, b.x - 0.5, f) * w,
    mix(a.yz, b.yz, f) * w
  );
}

// A single jittered speck per cell, guaranteed to stay inside its own cell so no
// neighbour sweep is needed: two hashes for a hard-edged grain of litter. Hard edges
// are the point — a soft blob reads as blur, a speck with an edge reads as a piece of
// something. The aniso term stretches the speck along a per-cell direction, which turns
// same two hashes into needles, twigs and splinters instead of dots.
// Returns (mask 0..1, per-speck id, signed cross-speck offset for a light/dark side).
vec3 gFleck(vec2 p, float cover, float rMin, float rMax, float aniso) {
  vec2 ip = floor(p);
  float pick = gHash21(ip + 4.13);
  if (pick > cover) return vec3(0.0, pick, 0.0);
  vec2 hv = gHash22(ip + 9.71);
  vec2 o = 0.21 + hv * 0.58;
  float R = mix(rMin, rMax, fract(pick * 63.17));
  vec2 d = (p - ip) - o;
  vec2 dir = normalize(hv - 0.5 + 1e-3);
  vec2 rd = vec2(dot(d, dir) / aniso, d.x * dir.y - d.y * dir.x);
  return vec3(
    1.0 - smoothstep(R * 0.45, R, length(rd)),
    fract(pick * 21.31),
    clamp(-d.y / max(R, 1e-4), -1.0, 1.0)
  );
}

// ---------------------------------------------------------------------------
// DISCRETE CLASTS — the round 3 fix.
//
// One rounded object per cell: a paraboloid dome of its own radius, proudness, position
// and colour id. Found with an exact 2x2 sweep rather than the usual 3x3, which is legal
// and seam free provided every radius stays at or below half a cell: if |c - p| < R <= 0.5
// then floor(c) is one of the four cells at floor(p - 0.5) + {0,1}^2, in each axis
// independently. That halves the cost of the most expensive thing in this shader, which
// is what buys three layers of objects plus their cast shadows instead of one.
//
// The dome is h = proud * R * (1 - t^2) with t = |d| / R. Its slope works out to
// -2 * proud * d / R, which is *scale free*: the same expression whether the layer is
// 20 cm stones or 2 cm grains, and it never needs to know the world frequency. That is
// why the relief here is physically sized instead of the 1.6 cm pancakes R2 shipped.
//
// Returns  .x  dome shape 0..1 (1 at the crown, 0 at the edge)
//          .yz surface slope in the detail plane, dimensionless
//          .w  height in cell units (metres * cells-per-metre), for shadow lengths
// out oid  .xy two decorrelated per-clast ids   .z base contact crease 0..1
// ---------------------------------------------------------------------------
vec4 gClast(vec2 p, float cover, vec2 rad, vec2 proud, float aspMax, out vec3 oid) {
  vec2 base = floor(p - 0.5);
  vec4 best = vec4(0.0);
  vec2 bid = vec2(0.5);
  float rim = 0.0;
  for (int j = 0; j < 2; j++) {
    for (int i = 0; i < 2; i++) {
      vec2 cell = base + vec2(float(i), float(j));
      float pick = gHash21(cell + 0.37);
      if (pick > cover) continue;                    // empty cell: matrix shows through
      vec2 hv = gHash22(cell + 1.71);                // anywhere in the cell — no lattice
      // Shape. A perfect circle is exactly what reads as a soap bubble, so every clast
      // gets its own aspect ratio and its own orientation. Both come out of fract() of
      // the rescaled position hash, which is decorrelated enough and costs no extra hash.
      vec2 u = normalize(fract(hv * vec2(41.31, 27.77)) - 0.5 + 1e-4);
      float a = mix(1.0, aspMax, fract(pick * 13.71));
      float rr = fract(pick * 71.31);
      // Squared so most clasts are small and a few are large, which is what a real
      // grain-size distribution looks like. The clamp is the containment ceiling: the
      // long semi-axis is R*a and the crease reaches 1.18 of it, and 1.18 * 0.42 < 0.5,
      // so the 2x2 sweep still catches every clast that can touch this pixel.
      float R = min(mix(rad.x, rad.y, rr * rr), 0.42 / a);
      vec2 d = p - cell - hv;
      vec2 e = vec2(dot(d, u), d.y * u.x - d.x * u.y);      // into the clast's own frame
      vec2 es = vec2(e.x / a, e.y * a);                     // squash it
      float t = length(es) / R;
      // Soil banked against the base. Light-independent, so it survives overcast.
      rim = max(rim, smoothstep(0.70, 1.0, t) * (1.0 - smoothstep(1.0, 1.18, t)));
      if (t >= 1.0) continue;
      float pr = mix(proud.x, proud.y, fract(pick * 29.77));
      float dome = 1.0 - t * t;
      float hc = pr * R * dome;
      if (hc > best.w) {
        // grad_d of |M d|^2 is 2 M^T M d, with M = diag(1/a, a) * Rot. Apply the squash
        // a second time, then rotate back out of the clast frame.
        vec2 g2 = vec2(es.x / a, es.y * a);
        vec2 gd = vec2(g2.x * u.x - g2.y * u.y, g2.x * u.y + g2.y * u.x);
        best = vec4(dome, -2.0 * pr * gd / R, hc);
        bid = fract(hv * vec2(53.17, 31.79) + vec2(0.217, 0.643));
      }
    }
  }
  oid = vec3(bid, rim);
  return best;
}

// Height only, for the shadow tap. Same field — it has to be the *same* field or the
// shadow detaches from its caster — at a third of the work.
float gClastH(vec2 p, float cover, vec2 rad, vec2 proud, float aspMax) {
  vec2 base = floor(p - 0.5);
  float best = 0.0;
  for (int j = 0; j < 2; j++) {
    for (int i = 0; i < 2; i++) {
      vec2 cell = base + vec2(float(i), float(j));
      float pick = gHash21(cell + 0.37);
      if (pick > cover) continue;
      vec2 hv = gHash22(cell + 1.71);
      vec2 u = normalize(fract(hv * vec2(41.31, 27.77)) - 0.5 + 1e-4);
      float a = mix(1.0, aspMax, fract(pick * 13.71));
      float rr = fract(pick * 71.31);
      float R = min(mix(rad.x, rad.y, rr * rr), 0.42 / a);
      vec2 d = p - cell - hv;
      vec2 e = vec2(dot(d, u), d.y * u.x - d.x * u.y);
      vec2 es = vec2(e.x / a, e.y * a);
      float t2 = dot(es, es) / (R * R);
      if (t2 >= 1.0) continue;
      best = max(best, mix(proud.x, proud.y, fract(pick * 29.77)) * R * (1.0 - t2));
    }
  }
  return best;
}

// Cast contact shadow: step up-sun through the clast height field and ask whether
// anything there stands above the ray. Heights and steps are both in cell units, so the
// ray climbs s * sunUp / |sunH| over a horizontal step of s and the whole test is one
// subtraction. sMul scales the step against the natural shadow length of a clast, so a
// low sun automatically throws a long one.
//
// This is the cue a normal map cannot produce at any amplitude — a normal has no
// occlusion — and it is specifically what stops a foreshortened clast collapsing into a
// brush stroke, because the dark side and the shadow stay on opposite sides of the crown
// no matter how the object is squashed on screen.
float gClastShadow(vec2 p, float cover, vec2 rad, vec2 proud, float aspMax,
                   vec2 sunH, float sunUp, float hHere, float sMul) {
  float hl = length(sunH);
  if (hl < 1e-3) return 0.0;
  float s = clamp(0.15 * sMul * hl / max(sunUp, 0.10), 0.03, 0.55);
  float hOcc = gClastH(p + (sunH / hl) * s, cover, rad, proud, aspMax);
  return smoothstep(0.0, 0.055, hOcc - hHere - s * (sunUp / hl));
}

// ---------------------------------------------------------------------------
// NYQUIST ON THE OBJECT, NOT ON THE LATTICE.
//
// rlfBandLod( L, freq ) is handed a layer's CELL frequency everywhere else in this file,
// and for a field of value noise that is correct — the cell is the feature. For a clast
// layer it is not: a cell is mostly empty and the thing with an edge in it is the clast,
// which at this radius distribution is TerrainClast.objDia = 0.38 of a cell across. The
// arithmetic that follows is the whole of the far-field half of the carried gap. At
// F_BLOCK = 0.85 cycles/m, rlfBandLod reaches zero at a footprint of 1.45/(0.85*2.6) =
// 0.656 m per pixel, where a 45 cm block is 0.69 PIXELS WIDE. Drawing a hard-edged
// ellipse at two thirds of a pixel does not produce a small stone; it produces the beat
// between a square object lattice and the pixel grid, which is regular, which is hard,
// and which is the chain-mail a critic measured from 150 m to 500 m.
//
// So: state the criterion once, in pixels of object, and let every layer share it.
// Returns 1 where the layer is unambiguously resolved and 0 where it must not be drawn.
//
// The 'foot' argument should be the GEOMETRIC-MEAN footprint rather than L.foot. L.foot is
// long axis after the anisotropy budget, which is the right thing to ask about a value
// field being filtered along one screen axis and the wrong thing to ask about whether a
// blob is a blob: an ellipse 8 px across and 2 px tall is still visibly an ellipse. The
// geometric mean is the honest "area per pixel", and it is one inversesqrt.
float gClastRes(float cellFreq, float foot) {
  float dia = ${gl(TerrainClast.objDia)} / max(cellFreq, 1e-4);
  return smoothstep(${gl(TerrainClast.resOff)}, ${gl(TerrainClast.resOn)},
                    dia / max(foot, 1e-5));
}

// ---------------------------------------------------------------------------
// A NYQUIST FADE FOR ANYTHING THAT FEEDS THE NORMAL — and the artefact that forced it.
//
// WHAT THE FRAME SHOWED. With this round's mineral lag in, the near field came back with
// a hard dark LATTICE across it: continuous lines along the clipmap's grid rows and a
// DOTTED line down each quad's split diagonal, ~2 m apart, over exactly the ground the lag
// had turned to scree and nowhere else. It is unmistakable at 2x and invisible in every
// number this file reports, which is why it belongs in a comment this long.
//
// WHAT IT IS. rlfLodAt builds L.foot from dFdx(wp) / dFdy(wp). Those derivatives
// are CONSTANT INSIDE A TRIANGLE and JUMP ACROSS ITS EDGES, because each triangle of the
// clipmap is its own plane. So rlfBandLod(L, f), and everything built on it, is a
// piecewise-constant staircase on the triangulation. On an ALBEDO that is harmless: a step
// in a colour is a step in a colour and there is nothing to see at these magnitudes. On a
// HEIGHT it is fatal, because rlfBumpH DIFFERENTIATES its argument — and the derivative
// of a step is a delta, i.e. a one-pixel line, drawn on the mesh, in the normal.
//
// The step scales with the layer's own amplitude, which is why this has been latent for
// three waves and appeared this round: TerrainGround.lumpHard is 0.022 m and
// lumpLoose is 0.105 m, so the moment the lag turned a patch of plateau to scree the
// same staircase got 4.8x taller and became a wireframe. It is not the lag's fault and
// removing the lag would only hide it.
//
// THE FIX, and the library sanctions it in its own words — rlfLodManual exists "for
// consumers with no world-space derivative to hand". A footprint is distance * (metres
// per pixel per metre), and both of those are SMOOTH: vWDist is a varying and
// uPxPerM is a uniform. No dFdx anywhere in the chain, so no staircase, so no lattice.
//
// Two deliberate differences from rlfReliefAmp:
//   - NO GRAZE CLAMP. It is computed from the anisotropy of the same two derivatives, so
//     it carries the identical staircase — and this file already argues in two places that
//     the graze clamp is a pixel-scale correction and wrong for a 1.6 m form at 5 m.
//   - the footprint is ISOTROPIC. That is the honest thing to say when you have thrown
//     away the derivative that measured obliquity, and it errs toward keeping relief,
//     which is the direction BAR asks for.
//
// Use this for anything that ends up inside bumpH. Keep rlfBandLod(L, ...) for albedo
// and coverage terms, which are tuned against it and cannot show the artefact.
float gBandLodS(float freq) {
  return clamp(RLF_NYQ_BIAS - max(vWDist * uPxPerM, 1e-5) * freq * RLF_NYQ_RATE, 0.0, 1.0);
}
float gReliefAmpS(float freq) {
  RlfEnv E = rlfEnv();
  float n = gBandLodS(freq);
  return n * n * mix(1.0, RLF_FAR_FLOOR_RELIEF, rlfFarFade(vWDist))
       * (1.0 - E.motion) * E.scale;
}

// Per-clast colour. A scree field is not one rock repeated. Value spans nearly 3:1
// between a shaded damp stone and a sun-bleached one, and the hue swings from
// iron-warm to granite-cool. The value curve is squared so most clasts sit mid and a
// few are bright, which is what a photograph of scree actually looks like.
// Wave 2 narrowed the low end from 0.44 to 0.58. The spread itself is right, but the
// soil matrix around these got brighter this round (hardpack's mid went 0.14 -> 0.17,
// loose 0.18 -> 0.25) and a clast at 0.44 of base against a matrix half a stop brighter
// stops reading as a dark stone and starts reading as a hole. Across the whole mid
// distance that came out as a field of dark spots — which is exactly the sort of thing
// that survives Nyquist and ruins a value structure, because a spot is a feature.
vec3 gStoneTint(vec3 base, vec2 id) {
  float v = 0.58 + 1.06 * id.x * id.x;
  return base * v * mix(vec3(0.78, 0.88, 1.12), vec3(1.22, 0.99, 0.71), id.y);
}

// Three-stop ramp. Two-stop mixes can only move value; real soil moves hue as well as
// value between its dark humus, its mid body and its dry silt.
vec3 gRamp3(vec3 a, vec3 b, vec3 c, float t) {
  return t < 0.5 ? mix(a, b, t * 2.0) : mix(b, c, t * 2.0 - 1.0);
}

// smoothstep that also hands back d/dx, for turning a level set into an incision whose
// walls have a real slope. Zero outside the ramp, which is what keeps the floor of a
// channel flat instead of domed.
float gStepD(float e0, float e1, float x, out float d) {
  float w = max(e1 - e0, 1e-5);
  float t = clamp((x - e0) / w, 0.0, 1.0);
  d = (t > 0.0 && t < 1.0) ? 6.0 * t * (1.0 - t) / w : 0.0;
  return t * t * (3.0 - 2.0 * t);
}

// One terrace section, from a 0..1 position inside a band and the antialiasing width the
// screen can afford. Read upward:
//   0.00 .. 0.12  crest  — the sunlit nose of the bench standing above the riser below
//   0.12 .. toe   bench  — the flat that collects everything above it
//   toe  .. 0.70  apron  — talus, ending at a lobate toe
//   0.70 .. 0.84  lip    — the shaded undercut at the foot of the riser
//   0.84 .. 0.98  riser  — the face
//   0.98 .. 1.00  crest  — continues across the fract seam
// The seam of the caller's fract() is deliberately inside the crest, whose profile is
// flat there, so there is no undamped discontinuity anywhere in the section. The five
// components sum to 1 by construction, which is what lets two of these at different
// periods be averaged into one nested profile without the total drifting.
//
// Bench is deliberately the majority of the section (30-58% depending on the toe). The
// first proportions gave riser + lip + apron 65% of every band, and since those three
// are the pale, stony members the whole mountain came out chalk-coloured. A hillside is
// mostly the ground between its cliffs.
//
// WAVE 2 ROUND 2 — THE CREST, AND WHY IT IS EXACTLY 0.12 WIDE.
// R1 shipped a section that could only ever *subtract*: a lip shadow and a riser
// darkening with nothing lit opposite them. Measured on the far hillside that came out
// as +-5% of local luminance with the dark excursions the larger ones (bright/dark
// 0.70), which reads as faint contour ruling on a smooth dome, not as benches and
// risers. The obvious fix — brighten the bench — is the one the previous pass correctly
// rejected: benchBand is the *majority* member, so lifting it lifts most of the
// mountain, and every place the band mask gates off then reads as a dark disc.
//
// The crest is the fix that does not have that failure mode. It is the outer nose of the
// bench: the strip immediately above the riser, convex, wind-scoured, and the one part
// of a terrace that genuinely catches the sun. Crucially it is NARROW — 0.02 above the
// seam plus 0.12 below it, which is 0.14, exactly the width of the lip and exactly the
// width of the riser. Three members of identical width means the mean-preserving lift is
// simply (K_LIP + K_RISER), a constant, and the whole profile integrates to zero over a
// band. When the gate closes there is nothing left to leave behind, so the gate itself
// never becomes the visible feature.
//
// Returns (riser, lip, apron, bench). CREST IS THE REMAINDER — 1 minus the sum — which
// is what makes the partition exact under the caller's mix() of two periods.
const float BAND_W = 0.14;      // the shared width of crest, lip and riser
const float BAND_CREST = 0.12;  // + the 0.02 above the seam
vec4 gBandProfile(float bt, float aa, float toe) {
  float eCst = smoothstep(BAND_CREST - aa * 0.5, BAND_CREST + aa * 0.5, bt);
  float eToe = smoothstep(toe - aa * 0.6, toe + aa * 0.6, bt);
  float eApr = smoothstep(0.70 - aa * 0.7, 0.70 + aa * 0.7, bt);
  float eLip = smoothstep(0.84 - aa * 0.5, 0.84 + aa * 0.5, bt);
  float eTop = smoothstep(0.98 - aa * 0.5, 0.98 + aa * 0.5, bt);
  return vec4(eLip * (1.0 - eTop), eApr * (1.0 - eLip), eToe * (1.0 - eApr),
              eCst * (1.0 - eToe));
}
`;

// ---------------------------------------------------------------------------
// The albedo / roughness / normal block. Replaces <color_fragment>.
// ---------------------------------------------------------------------------
const TERRAIN_SHADE = /* glsl */ `
{
  vec3 wp = vWPos;
  vec3 gN = normalize(vWNrm);
  float slope = 1.0 - clamp(gN.y, 0.0, 1.0);          // 0 = flat, 1 = vertical
  float alt = clamp(wp.y / uRelief, 0.0, 1.0);

  // --- the shared environment and the shared sampling budget --------------------
  // Both used to be private to this file. The sun was dug out of the first directional
  // light's *view*-space direction, which is correct but means the terrain and the nine
  // other things drawn on top of it each re-derive the sun from a different place; and
  // the footprint / anisotropy / graze / far-fade policy was a hand-rolled copy of what
  // is now ReliefLod. The constants there were lifted verbatim from this shader, so
  // rlfLodAt() and rlfBandLod() are byte-identical to what they replace — the point is
  // that they cannot drift, not that they change anything today.
  RlfEnv E = rlfEnv();
  RlfLod L = rlfLodAt(wp);
  vec3 sunW = E.sunW;

  float foot = L.foot;
  float graze = L.graze;
  float farFade = L.farFade;                           // art direction, on top of Nyquist
  float bumpCut = 1.0 - E.motion;                      // motion pulls back relief, not colour

  // Occlusion accumulates here and lands on the LIGHT at the end of the block. See the
  // "occlusion" section below for why that is not the same thing as darkening albedo.
  gRlfOcc = 1.0;
  // ...and the sky term accumulates here. Occlusion can only subtract and is spent on
  // both halves of the light; this one is signed and spent on the ambient alone. See
  // TerrainAmbient and the block at the very bottom of this shader.
  gTerrAmb = vec3(1.0);
  // ...and the landform light channel. 1.0 is "this fragment has no landform-scale relief
  // in it", which is the whole near field and every fragment past the far fields' Nyquist.
  gTerrLand = 1.0;

  // Fixed-world-scale noise layers. These carry *physical scale* at metre sizes; the
  // clast layers below carry it at object sizes.
  const float F_MID = 0.16;    // ~6 m   clods, blotching, bench fill
  const float F_FINE = 0.80;   // ~1.2 m clumps, cracks, tread

  float lodMid = rlfBandLod(L, F_MID);
  float lodFine = rlfBandLod(L, F_FINE) * (1.0 - farFade * 0.55);

  // --- shared noise fields -----------------------------------------------------
  // Macro survives to any distance: it is what makes 300 m read as landform.
  vec3 macro = gFbmD(wp.xz * 0.0125, 4, 0.5);
  vec3 region = gFbmD(wp.xz * 0.0032 + vec2(23.7, 91.3), 2, 0.55);

  // Hash-driven domain warp. Low frequency, so it displaces whole neighbourhoods of
  // detail rather than smearing them, and no two areas of the mountain get the same one.
  vec3 wpw = wp + vec3(macro.y, 0.0, macro.z) * 2.6;

  vec3 bw = abs(gN);
  bw = bw * bw; bw = bw * bw;                          // ^4: tight, no mush on 45 deg faces
  bw /= max(bw.x + bw.y + bw.z, 1e-4);

  float mv = 0.5, fv = 0.5;
  vec3 gradMid = vec3(0.0), gradFine = vec3(0.0);
  if (lodMid > 0.004) {
    vec4 t = gTriFbm(wpw, bw, F_MID, 3, 0.5);
    mv = mix(0.5, t.x, lodMid);
    gradMid = t.yzw * lodMid;
  }
  if (lodFine > 0.004) {
    vec4 t = gTriFbm(wpw * 1.017 + 5.3, bw, F_FINE, 3, 0.5);
    fv = mix(0.5, t.x, lodFine);
    gradFine = t.yzw * lodFine;
  }

  // --- the detail plane ---------------------------------------------------------
  // One projection for everything object-scale: the ground plane, swapped for the
  // dominant wall plane on anything steep. Keeping the two basis vectors around (rather
  // than just the UV) is what lets the clast layer turn its in-plane slope back into a
  // world normal and lets the sun be resolved into the same frame, so cliffs get real
  // blocky relief and real shadows instead of a smeared ground projection.
  vec2 detUV = wpw.xz;
  vec3 axU = vec3(1.0, 0.0, 0.0);
  vec3 axV = vec3(0.0, 0.0, 1.0);
  if (bw.y < 0.5) {
    if (bw.x > bw.z) { detUV = wpw.zy; axU = vec3(0.0, 0.0, 1.0); axV = vec3(0.0, 1.0, 0.0); }
    else             { detUV = wpw.xy; axU = vec3(1.0, 0.0, 0.0); axV = vec3(0.0, 1.0, 0.0); }
  }
  vec2 sunH = vec2(dot(sunW, axU), dot(sunW, axV));
  float sunUp = dot(sunW, gN);
  // No contact shadow on a facet the sun cannot see — it is already in shade, and a
  // phantom dark blob there is worse than none. E.sunVis is the second half of that and
  // it is the sky's own number: below the horizon there is no sun to cast anything, and
  // the whole scene's sprites, foliage and clast shadows now switch off on the same frame
  // instead of each piece choosing its own dawn.
  float sunVis = smoothstep(0.02, 0.26, sunUp) * E.sunVis;

  // --- discrete clasts ----------------------------------------------------------
  // Three layers of objects at fixed world scale. Sizes are chosen so their Nyquist
  // ranges overlap and hand off cleanly: grains carry ~0-9 m, clods ~0-45 m, stones
  // ~0-135 m, and past that the metre-scale fBm and the macro field take over as
  // landform. There is no distance at which the ground has nothing in it.
  // The BLOCK layer exists for one reason: distance. A 20 cm stone is below the sampling
  // limit by about 45 m at riding camera angles, and everything past that used to be the
  // metre-scale noise on its own — which is a smooth wash, and a smooth wash seen at a
  // grazing angle is the brush stroke this round exists to kill. A half-buried 20-95 cm
  // knob survives to about 150 m, which covers the whole band you actually look down.
  const float F_BLOCK = 0.85;   // cell 1.18m -> knobs and buried boulders 20-95 cm
  const float F_STONE = 3.1;    // cell 32 cm -> clasts 8-26 cm
  const float F_CLOD  = 9.0;    // cell 11 cm -> clods  3-9 cm
  const float F_GRAIN = 26.0;   // cell 3.8cm -> grains 1-3 cm
  const vec2  BK_RAD = vec2(0.09, 0.40), BK_PROUD = vec2(0.11, 0.44);
  const vec2  ST_RAD = vec2(0.13, 0.42), ST_PROUD = vec2(0.16, 0.66);
  const vec2  CL_RAD = vec2(0.12, 0.40), CL_PROUD = vec2(0.10, 0.44);
  const vec2  GR_RAD = vec2(0.14, 0.40), GR_PROUD = vec2(0.10, 0.36);
  // Elongation. The squash divides one semi-axis and multiplies the other, so the
  // aspect ratio a clast actually shows is asp*asp — 1.85 came out as 3.4:1, which is
  // not a stone, it is a fish scale. Kept under 2:1 across the board.
  const float BK_ASP = 1.30, ST_ASP = 1.26, CL_ASP = 1.32, GR_ASP = 1.40;

  // WAVE 2 ROUND 3 — HOW FAR PAST THE NEAR FIELD THIS PIXEL IS.
  //
  // The block layer is the only clast layer that survives past ~50 m, so it is the only
  // one whose behaviour has to change *in kind* with distance rather than just fade. At
  // 5 m a knob is a hundred pixels and every cue it owns is legible. At 120 m it is six,
  // and six pixels can hold exactly one light/dark pair — so the layer has to pick which
  // pair, and cover the ground with far fewer of them.
  //
  // R2 picked wrong. It kept the crease ring and the cast shadow at a 30% floor, dropped
  // the dome relief to 45%, and left the per-clast tint spanning 0.58..1.64 of the matrix.
  // Six pixels of ring + shadow + a below-matrix tint is not a boulder, it is a hole, and
  // the R2 vista came out with the far hillside pocked in ~2 m dark ellipses at a density
  // of a third of the ground. Three things follow from that and all three are below:
  // density falls to a quarter, the ring goes away entirely (it is the cue that makes a
  // solid blob), and what is left is the one pair that reads — a crown BRIGHTER than the
  // matrix with a short hard shadow off its lee side.
  float blockFar = smoothstep(26.0, 72.0, vWDist);
  float blockNear = 1.0 - blockFar;
  // WAVE 4 — AND THIS IS THE OTHER HALF OF THE CARRIED GAP, proved rather than argued.
  // Scenario farhillab shot 4p0s is the same frame as 1p0s with uDbgA.x = 0, i.e. this layer and
  // nothing else switched off, and between them essentially every dark ellipse on the
  // hillside from 70 m to 220 m disappears. What is left is the vegetation piece's rock
  // scatter, which is a handful of objects rather than a pox.
  //
  // Wave 2 and wave 3 both tried to make the far knob READ — density down, crease off,
  // tint remapped into its bright half, a short hard shadow next to a lit crown. Three
  // rounds of that and the frame still shows pockmarks, and the reason is structural: at
  // six pixels the only thing an object can contribute is a light/dark PAIR, and a
  // light/dark pair at 30% contrast repeated four hundred times across a hillside is a
  // texture, not a field of boulders. It reads as damage.
  //
  // So the far block clast keeps the one cue that is form — its dome normal, which fades
  // as lodBlock squared and is gone by ~180 m on its own — and gives up every cue that is
  // a MARK: the cast shadow, the per-clast albedo tint and the crown lift. What replaces
  // it in that band is TerrainShading.formRelief, which is landform and is what should
  // have been carrying 100-500 m all along.
  float blockMark = 1.0 - smoothstep(64.0, 132.0, vWDist);

  // Coverage is spatial, not constant. Stones on a hillside come in drifts and washes
  // with bare ground between them; a constant occupancy over the whole mountain is what
  // turns an object field into cobblestone paving. Driving the *cover* rather than the
  // opacity is the point — a faded stone is a ghost, an absent stone is bare ground.
  //
  // ...which is also why the distance cut goes on the cover and not on a mask. A far
  // hillside carrying a quarter of the boulders at full contrast reads as a slope with
  // boulders on it; the same hillside carrying all of them at quarter contrast reads as a
  // stain. Cells drop out one at a time over 26-72 m, which is a density LOD, not a pop.
  float drift = smoothstep(0.26, 0.80, mv * 0.55 + macro.x * 0.62);
  float wash = smoothstep(0.32, 0.84, fv * 0.50 + region.x * 0.66);
  // WAVE 4 ROUND 2 — the far density cut goes 0.40 -> 0.78, and it is the resolution gate
  // below that pays for it. That 0.40 existed because the layer was being drawn to 600 m,
  // where a knob is under a pixel and four hundred of them are a pox; thinning the field
  // was the only lever available for a problem that was really "this layer is drawn past
  // its Nyquist limit". gClastRes fixes the cause, the layer is gone by roughly 130 m on
  // grazing ground, and inside that a knob is 2 px or more and reads as an object — so
  // thinning it there is now just an empty hillside. Measured on the same latched pose
  // (scenario clastab, appended this round): with the gate in and the density still at
  // 0.40, the 60-150 m band had neither boulders nor a lattice, which is a wash.
  float bkCover = (0.03 + 0.26 * smoothstep(0.34, 0.86, macro.x * 0.62 + region.x * 0.52
                                           + slope * 0.6)) * mix(1.0, 0.78, blockFar);
  float stCover = 0.05 + 0.40 * drift;
  float clCover = 0.10 + 0.42 * (0.35 + 0.65 * wash);
  float grCover = 0.26 + 0.46 * (0.30 + 0.70 * drift);

  // THE RESOLUTION GATE — the far half of the carried gap, and it is one line per layer.
  //
  // rlfBandLod is the Nyquist limit of the CELL LATTICE; gClastRes is the Nyquist limit of
  // the OBJECT IN IT, which is 0.38 of a cell across (see gClastRes). Both apply — the
  // lattice still has to be resolved for the field to have any structure at all — but past
  // about 150 m it is the object criterion that binds, and the 2.3x of distance between
  // the two is precisely the band the chain-mail lattice occupied.
  //
  // Geometric-mean footprint, not L.foot: L.foot is the long axis after the anisotropy
  // budget, which is the correct question for a value field smeared along one screen axis
  // and the wrong one for "is this blob a blob". An ellipse 8 px wide and 2 px tall still
  // reads as an object; the same object at 2x2 does not.
  float footG = L.foot * inversesqrt(clamp(L.aniso, 1.0, RLF_ANISO_MAX));
  float resBlock = gClastRes(F_BLOCK, footG);
  float resStone = gClastRes(F_STONE, footG);
  float resClod  = gClastRes(F_CLOD, footG);
  float resGrain = gClastRes(F_GRAIN, footG);

  // uDbgC.z switches the WHOLE clast field, all four layers and every cue each of them
  // owns. The wave-4 rig could only switch the block layer, which is why three rounds of
  // A/B could not tell a block ring from a stone ring on the same piece of tread.
  float lodBlock = rlfBandLod(L, F_BLOCK) * resBlock * uDbgA.x * uDbgC.z;
  float lodStone = rlfBandLod(L, F_STONE) * resStone * uDbgC.z;
  float lodClod  = rlfBandLod(L, F_CLOD) * resClod * uDbgC.z;
  float lodGrain = rlfBandLod(L, F_GRAIN) * resGrain * uDbgC.z;

  vec4 bkD = vec4(0.0), stD = vec4(0.0), clD = vec4(0.0), grD = vec4(0.0);
  vec3 bkId = vec3(0.5, 0.5, 0.0), stId = vec3(0.5, 0.5, 0.0);
  vec3 clId = vec3(0.5, 0.5, 0.0), grId = vec3(0.5, 0.5, 0.0);
  float bkSh = 0.0, stSh = 0.0, clSh = 0.0;

  // Each layer lives on its own rotated lattice so the four square cell grids cannot
  // stack into a plaid. The rotation has to be undone on the way out: the sun direction
  // goes *into* the rotated frame (M * v) and the returned slope comes back *out* of it
  // (transpose(M) * v, which is dot against the columns).
  if (lodBlock > 0.004) {
    vec2 q = detUV * F_BLOCK;
    bkD = gClast(q, bkCover, BK_RAD, BK_PROUD, BK_ASP, bkId);
    // Shorter step at distance: a long soft shadow at 6 px is just a second dark blob
    // stuck to the first. A short one is a crescent hard against the lee edge of a lit
    // crown, which is the whole read.
    if (sunVis > 0.01) {
      bkSh = gClastShadow(q, bkCover, BK_RAD, BK_PROUD, BK_ASP, sunH, sunUp, bkD.w,
                          mix(1.0, 0.55, blockFar));
    }
    // THE FAR KNOB IS THE BRIGHT TAIL, NOT THE WHOLE DISTRIBUTION. gStoneTint spans
    // 0.58..1.64 of the surface base, which is right when you can see the object: a scree
    // field is light and dark stones together. It is wrong at 6 px, because the dark half
    // has no visible form left to explain why it is dark and so reads as a hole punched in
    // the hillside. Remapping the value id into its upper half at distance puts every far
    // clast at 0.94..1.64 — at or above the matrix — so what the eye picks up out there is
    // a lit crown against ground, which is the thing the critic asked to have back.
    bkId.x = mix(bkId.x, 0.58 + 0.42 * bkId.x, blockFar);
  }
  if (lodStone > 0.004) {
    vec2 q = (G_ROTA * detUV) * F_STONE;
    stD = gClast(q, stCover, ST_RAD, ST_PROUD, ST_ASP, stId);
    stD.yz = vec2(dot(G_ROTA[0], stD.yz), dot(G_ROTA[1], stD.yz));
    if (sunVis > 0.01) {
      vec2 sh = G_ROTA * sunH;
      stSh = gClastShadow(q, stCover, ST_RAD, ST_PROUD, ST_ASP, sh, sunUp, stD.w, 0.9);
    }
  }
  if (lodClod > 0.004) {
    vec2 q = (G_ROTB * detUV) * F_CLOD + 37.1;
    clD = gClast(q, clCover, CL_RAD, CL_PROUD, CL_ASP, clId);
    clD.yz = vec2(dot(G_ROTB[0], clD.yz), dot(G_ROTB[1], clD.yz));
    if (sunVis > 0.01) {
      vec2 sh = G_ROTB * sunH;
      clSh = gClastShadow(q, clCover, CL_RAD, CL_PROUD, CL_ASP, sh, sunUp, clD.w, 0.9);
    }
  }
  if (lodGrain > 0.004) {
    vec2 q = (G_ROT * detUV) * F_GRAIN + 71.7;
    grD = gClast(q, grCover, GR_RAD, GR_PROUD, GR_ASP, grId);
    grD.yz = vec2(dot(G_ROT[0], grD.yz), dot(G_ROT[1], grD.yz));
  }

  // Masks. The albedo mask is deliberately hard — a stone has an edge, and a soft blob
  // is exactly what reads as sponge paint — while the crown is soft, because the top of
  // a dome is where it sees the whole sky.
  // bkMask is the ALBEDO mask, so it carries blockMark — the far knob stops being painted.
  // The dome normal below reads bkD.yz directly and is untouched by this, which is the
  // whole point of the split.
  //
  // ...AND THE EDGE SOFTENS AS THE OBJECT RUNS OUT OF PIXELS. A hard edge is right at
  // 100 px and is precisely what aliases at 3, and the resolution gate above cannot fix
  // that on its own — it controls how much of the layer is there, not how sharp what is
  // left is, so without this the layer's last twenty metres of range are its sharpest and
  // its most aliased. Widening the mask threshold is the cheapest prefilter there is: one
  // mix per layer, and it turns a 3 px ellipse into a soft blob over exactly the range
  // gClastRes is fading it out.
  float sfBk = ${gl(TerrainClast.edgeSoft)} * (1.0 - resBlock);
  float sfSt = ${gl(TerrainClast.edgeSoft)} * (1.0 - resStone);
  float sfCl = ${gl(TerrainClast.edgeSoft)} * (1.0 - resClod);
  float sfGr = ${gl(TerrainClast.edgeSoft)} * (1.0 - resGrain);
  float bkMask = smoothstep(0.0, 0.12 + sfBk, bkD.x) * lodBlock * blockMark;
  float stMask = smoothstep(0.0, 0.13 + sfSt, stD.x) * lodStone;
  float clMask = smoothstep(0.0, 0.15 + sfCl, clD.x) * lodClod;
  float grMask = smoothstep(0.0, 0.19 + sfGr, grD.x) * lodGrain;
  float bkTop = smoothstep(0.16, 0.86, bkD.x);
  float stTop = smoothstep(0.18, 0.88, stD.x);
  float clTop = smoothstep(0.18, 0.88, clD.x);
  float grTop = smoothstep(0.20, 0.90, grD.x);

  // Contact crease and cast shadow, the two light-independent / light-dependent halves
  // of "this object is sitting on that ground".
  //
  // WHICH CUES A FAR KNOB KEEPS, AND WHY IT IS EXACTLY THESE.
  //
  // The crease is a *ring*. At a hundred pixels that is soil banked against a base and it
  // is the best cue in the shader. At six it closes over the object and the whole thing
  // is one dark ellipse — no orientation, no light direction, nothing to tell the eye it
  // is a solid rather than a stain. So the crease goes to zero with distance. That single
  // change is most of what turned the R2 far hillside into a pockmarked contour map.
  //
  // The cast shadow is a *crescent*, always on the lee side, and it is the one cue a
  // normal map cannot fake. It survives at six pixels perfectly well provided the thing
  // casting it is brighter than the ground. So it stays, at 72% of its near strength and
  // with a shorter step (above), and it now sits next to a crown that is genuinely lit.
  //
  // The crown carries the extra weight out there for the same reason: a bright cap plus a
  // dark crescent is a legible light/dark pair at any size, and it is the pair the sun
  // actually makes on a boulder.
  //
  // WAVE 4 ROUND 2 — AND WHY ALL THREE ARE NOW VECTORS.
  //
  // These were three scalars, each a hand-weighted sum over the four layers, and the sum
  // is where the carried gap lives. Once four layers are added together the result cannot
  // be entitled per surface any more — and everything ELSE an object owns is: its dome
  // relief goes through bkRel/stRel/clRel/grRel, its albedo tint through the per-surface
  // mix weights. So on the built tread, hardpack took 42% of the block layer's relief,
  // 30% of its tint, and 100% of its crease. A ring with no solid in it, drawn on the one
  // surface that is supposed to be swept clean, four hundred times per frame.
  //
  // Keeping the four components apart to the point of use costs three vec4s of registers
  // and makes the entitlement a single dot product down there (see relV). It also makes
  // the invariant checkable by eye: every line below is one per-layer cue times that
  // layer's own amplitude, and the amplitude vector is shared by the crease, the shadow,
  // the crown and the dome.
  //
  // THE CREASE NOW FADES AS lod SQUARED, like the dome normal, not as lod like an albedo
  // mark. That is the wave-2 shadow lesson applied to the cue it was never applied to: a
  // crease is not paint, it is the visible consequence of an object having a base, so it
  // has to leave when the object does. At the distance where a block's dome is at 25% the
  // ring used to be at 50%, and a ring at twice the strength of the solid inside it is the
  // definition of a hollow outline.
  vec4 clastRim = vec4(bkId.z * lodBlock * lodBlock * blockNear * 0.80,
                       stId.z * lodStone * lodStone * 0.72,
                       clId.z * lodClod * lodClod * 0.50,
                       grId.z * lodGrain * lodGrain * 0.44) * uDbgC.w;
  // EVERY SHADOW IS SCALED BY ITS OWN CASTER'S LOD, and the absence of that was the
  // single largest contributor to the carried gap. gClastShadow is called inside a
  // a lod > 0.004 test and then used at full weight, while the object casting it fades as
  // lod (albedo tint, crown) and as lod*lod (dome normal). So at 150-400 m the shadow was
  // running at 100% behind a caster running at 40% and 16% — a dark ellipse with nothing
  // visible making it. That is precisely "solid ~2 m dark ellipses scattered across the
  // whole slope", and no amount of tint or crown work fixes it, because the thing you are
  // looking at out there is not the stone, it is the stone's shadow.
  // WAVE 4: bkSh now carries blockMark as well. The wave-2 note above is right about the
  // *arithmetic* — a shadow must never outlive its caster — and it fixed that by scaling
  // the shadow to lodBlock. What it did not do is ask whether a cast shadow is a cue worth
  // keeping at six pixels at all, and the A/B says it is not: a crescent that small has no
  // readable direction, so four hundred of them read as a pox rather than as boulders.
  vec4 clastSh = vec4(bkSh * lodBlock * blockMark * (0.72 + 0.28 * blockNear),
                      stSh * lodStone * 0.90,
                      clSh * lodClod * 0.55,
                      0.0) * sunVis;
  vec4 clastCrn = vec4(bkTop * bkMask * (0.55 + 0.75 * blockFar),
                       stTop * stMask * 0.55,
                       clTop * clMask * 0.34,
                       grTop * grMask * 0.30);
  // THE BODY. The cue that did not exist, and its absence is the other half of "hollow
  // outlines with featureless interiors".
  //
  // Integrate the two profiles over the disc and the arithmetic is unambiguous: the crease
  // ring covers 0.46 of R^2 and the crown covers 0.49, so per unit strength they are
  // almost exactly matched — which is why a *sunlit* clast reads correctly. But the crown
  // is smoothstep(0.16, 0.86, dome), i.e. the middle 37% of the object's radius, and the
  // annulus between the crown and the ring — from t = 0.61 out to t = 0.70, most of the
  // visible flank — carries no light-independent cue at all. In full sun the dome normal
  // fills it. On the tread, where the dome normal is entitled down to 42%, and in shade,
  // where there is no direct light for a normal to modulate, nothing does, and what is
  // left is a dark circle with the matrix inside it.
  //
  // clastBody is the whole disc, weighted toward the flank rather than the crown, and it
  // goes on the ambient channel where a solid belongs: an object standing proud of the
  // ground sees more sky than the ground does, everywhere on it, not only at its summit.
  // Sized against the ring integral below so a clast is mean-preserving by construction —
  // it can no longer darken the surface it is scattered on, which is what four hundred
  // unmatched rings per frame were quietly doing to the mid distance.
  vec4 clastBody = vec4(smoothstep(0.0, 0.34, bkD.x) * lodBlock * blockMark,
                        smoothstep(0.0, 0.36, stD.x) * lodStone,
                        smoothstep(0.0, 0.40, clD.x) * lodClod,
                        smoothstep(0.0, 0.46, grD.x) * lodGrain);

  // --- screen-locked grain (the matrix between the objects) --------------------
  // Half the amplitude it had in R2. Its job now is to keep something at the pixel
  // scale in the gaps, not to be the surface.
  const float MICRO_F0 = 0.22;   // coarsest locked octave: ~4.5 m
  const float MICRO_PX = 5.0;    // target period, pixels
  float footLock = foot * (1.0 + (1.0 - graze) * 2.2);
  float micLvl = clamp(log2(max(1.0 / (footLock * MICRO_PX * MICRO_F0), 1.0)), 0.0, 9.0);
  float micL0 = floor(micLvl);
  float micF = micLvl - micL0;
  float micA = MICRO_F0 * exp2(micL0);

  vec3 micro = gLocked(detUV, micA, micA * 2.0, micF);
  vec3 meso = gLocked(detUV * 0.9731 + 41.7, micA * 0.38, micA * 0.76, micF);

  float micV = micro.x;
  float mesV = meso.x;
  float micS = clamp(0.5 + (micV - 0.5) * 1.50 * graze, 0.0, 1.0);
  float mesS = clamp(0.5 + (mesV - 0.5) * 1.10 * graze, 0.0, 1.0);
  float micC = micS * 2.0 - 1.0;
  float mesC = mesS * 2.0 - 1.0;

  // Litter and mineral flecks, locked to the same screen frequency so the near field
  // always has something at the pixel scale.
  //
  // TWO THINGS WERE WRONG WITH THEM AND THE CRITIC NAMED BOTH IN FOUR WORDS — "a plaid of
  // pure-albedo litter strokes".
  //
  // PLAID: gFleck puts one speck per cell of a square lattice, and both populations were
  // sampled on rigid lattices (one rotation each) at a fixed ratio. Rotating a grid does
  // not stop it being a grid; at a grazing angle its rows line up along the view axis and
  // the eye reads rank and file, which is exactly what a hand-drawn hatch looks like. The
  // domain is now warped by the locked noise that is already computed two lines above —
  // a third of a cell, which is enough to destroy the row structure and far too little to
  // smear a speck — so the specks scatter instead of parading. No extra taps.
  //
  // PURE ALBEDO: fixed below, in the ambient block. gFleck's .z is a signed offset across
  // the speck, which is a ready-made near/far side; spending part of it on the sky term
  // instead of all of it on the colour is what makes a needle read as lying on the ground
  // rather than as being printed on it.
  float flAmt = (1.0 - farFade * 0.75) * graze;
  vec2 flWarp = vec2(micro.x - 0.5, meso.x - 0.5) * 0.66;
  vec2 flUV = (G_ROTB * detUV) * micA * 0.55 + flWarp;
  // ...and the third thing wrong with them, which took a capture of the overcast forest
  // floor to see: THE STROKES WERE HALF A METRE LONG. gFleck stretches a speck by its aniso
  // along a per-cell direction, and at 2.6 on a cell that lands at 13-26 cm of ground in
  // the near field that is a 35-70 cm needle. A needle is 5-15 cm; what was being drawn
  // was a branch, at 30% coverage, in a colour 2.2x the loam under it — and adjacent
  // cells' directions chain up into exactly the pale hard-edged net the critique called a
  // plaid. Shorter, sparser, and a quarter less contrast in every recipe below.
  vec3 litter = gFleck(flUV, 0.24, 0.15, 0.27, 1.7);                 // twigs, needles, bark
  vec3 mineral = gFleck(G_ROTA * flUV * 1.73 + flWarp.yx * 0.7 + 13.3,
                        0.24, 0.10, 0.21, 1.4);                      // quartz, silt
  // Clustering. Real ground is not uniformly gritty: there are patches of clean fine
  // dust, patches of gravel, drifts of litter in the lee of things. A uniform speckle
  // spread evenly over everything reads as sponge paint, so each population gets its own
  // metre-to-decametre mask and the masks are anti-correlated so they do not pile up.
  float clumpLit = smoothstep(0.28, 0.78, 1.05 - mv * 0.50 - region.x * 0.60);
  float clumpMin = smoothstep(0.26, 0.80, fv * 0.62 + region.x * 0.52);
  float fLit = litter.x * flAmt * (0.18 + 1.00 * clumpLit);
  float fMin = mineral.x * flAmt * (0.30 + 0.85 * clumpMin);
  float litLit = 1.0 + litter.z * 0.30;

  // Bedding planes: varies fast vertically, slowly horizontally, so cliffs get strata.
  // Both octaves are gated on their own *vertical* frequency and on the grazing term.
  // A cliff seen from the side is the most foreshortened thing in the scene, and an
  // ungated stripe pattern on it is the single most obvious aliasing artefact there is.
  //
  // WAVE 4 — THIS WAS THE CONTOUR RULING, and the layer A/B rig is what found it. The
  // carried gap names "parallel contour-band stripes" on the far hillside, and for two
  // rounds this file assumed those were the cliff bands and spent its budget on the band
  // profile. Switching the whole band block off with uDbgA.y (scenario farhillab, shot
  // 7p0s) leaves the stripes exactly where they were. It is this term instead, and the
  // mechanism is plain once you look at it: strat is sampled at wp.y * 0.34, a THREE
  // METRE vertical period, and it swings the rock ramp over nearly 5:1 from 0.058 to
  // 0.298. On a vertical cliff that is bedding. On a 30% hillside — which is most of what
  // you see at 100-500 m — three metres of rise is ten metres of ground, so it rules the
  // entire slope into horizontal stripes, and the far field is mostly rock, so it rules
  // essentially all of it.
  //
  // The fix is not amplitude and it is not a distance fade: bedding on a real cliff IS
  // legible at half a kilometre, and killing it there would trade one wrong answer for
  // another. It is that a bedding plane belongs on a FACE. Near, everything keeps what it
  // had; past ~110 m the term is confined to ground steeper than about 40 degrees, where
  // an iso-elevation stripe is a stratum rather than a contour line.
  float stratFace = smoothstep(0.24, 0.52, slope);
  float stratFar = mix(stratFace, 1.0, 1.0 - smoothstep(40.0, 110.0, vWDist));
  stratFar = mix(1.0, stratFar, uDbgB.z);
  float lodStratA = rlfBandLod(L, 0.42) * graze * stratFar;
  float lodStratB = rlfBandLod(L, 1.30) * graze * stratFar;
  float strat = 0.5;
  if (lodStratA > 0.004) {
    strat = mix(0.5, gNoiseD(vec2(dot(wp.xz, vec2(0.023, 0.017)) + macro.x * 0.7, wp.y * 0.34)).x,
                lodStratA);
  }
  if (lodStratB > 0.004) {
    float s2 = gNoiseD(vec2(dot(wp.xz, vec2(0.061, -0.048)) + 7.3, wp.y * 1.15)).x;
    strat = mix(strat, strat * 0.62 + s2 * 0.38, lodStratB);
  }

  // Fissures are level sets of a noise field, and level sets need care in two ways.
  // First, the fade factor is not optional: fv drifts toward 0.5 as its layer drops
  // below Nyquist, and a level set evaluated at exactly its own level is 1.0 *everywhere*
  // — so an ungated crack term inverts into a full-strength wash over the whole far field.
  // Second, scale. A crack in soil is a hairline, so it belongs on the screen-locked
  // layer where it stays a hairline at every distance. Rock joints really are metre-scale,
  // so they keep the coarse field and just get a thinner profile.
  float crack = pow(1.0 - abs(micS * 2.0 - 1.0), 9.0) * graze;
  float joint = pow(1.0 - abs(fv * 2.0 - 1.0), 9.0) * lodFine;
  float tuft = clamp(mesS * 0.55 + micS * 0.45, 0.0, 1.0);

  float mvC = mv * 2.0 - 1.0;
  float fvC = fv * 2.0 - 1.0;
  float mac = macro.x * 2.0 - 1.0;
  float reg = region.x * 2.0 - 1.0;

  // Gullies and runnels. The horizontal part of the surface normal is the fall line
  // (terrain.ts routes trails with exactly this), so favouring the macro pattern where
  // its own contours run downhill gives erosion streaks with no extra noise taps and
  // no coordinate frame to go unstable on flat ground.
  vec2 downhill = gN.xz;
  float downLen = length(downhill);
  float streak = 0.0;
  if (downLen > 0.02) {
    vec2 acrossHill = vec2(-downhill.y, downhill.x) / downLen;
    vec2 mg = macro.yz;
    float mgl = length(mg);
    if (mgl > 1e-4) {
      streak = smoothstep(0.55, 1.0, abs(dot(mg / mgl, acrossHill)))
             * smoothstep(0.06, 0.30, downLen);
    }
  }

  // ===========================================================================
  // LANDFORM STRUCTURE — 3-30 m, form-anchored, hard-edged.
  //
  // Everything above this line is grain- or object-scale and has died at Nyquist by
  // somewhere between 10 m and 150 m. Past that the mountain was a wash whose only
  // variable was noise amplitude, faded further by the graze clamp — edge-less,
  // isotropic, unreadable as terrain. The answer is not smaller detail, it is bigger
  // shape, and shape needs an anchor or it is just more noise. Two anchors:
  //
  //   ALTITUDE, for the cliff bands. Bedding is roughly iso-elevation, so a fract() of
  //   a warped altitude gives contour-following structure. Contours are the one pattern
  //   that reads as landform instead of as texture: they bend with the hill and they
  //   tell you which way is down.
  //
  //   THE FALL LINE, for the drainage. Water runs downhill, so a channel network built
  //   in the local (across, down) frame automatically bends where the hill bends.
  //
  // Both stay out of the near field. Inside ~12 m the real geometry is describing the
  // ground and a virtual cliff band drawn across it would fight what you can see; the
  // ramp to 45 m is also exactly where the clast layers start to go, so the two hand off.
  // ===========================================================================
  // 9-26 m, not 12-45 m. The brief for this piece is "15 m to the horizon", and a ramp
  // that is still only a third of the way in at 15 m and two thirds at 30 m left the
  // single most-looked-at part of the frame — the slope you are about to ride onto —
  // completely untouched by any of it. That ground was a smooth brown ramp in every
  // capture of the last two rounds and no amount of far-field work was ever going to
  // change it. The near field is still protected: 0-12 m is essentially clear, which is
  // where the real geometry is describing the ground and a virtual bench drawn across it
  // would fight what the rider can plainly see.
  float structFar = smoothstep(9.0, 26.0, vWDist);
  // Structure needs a fall line, but only just: the gate is set at a 3% grade, not at a
  // rideable one. The first pass gated at 17% and the whole 15-70 m band — which is
  // forested bench, the gentlest ground in the scene and the part you look at most —
  // fell straight through it and stayed a brown carpet.
  float structOn = smoothstep(0.025, 0.11, downLen) * structFar;

  // ===========================================================================
  // THE TWO LANDFORM BANDS — 24 m and 90 m — COMPUTED HERE, USED TWICE.
  //
  // They were at the bottom of this shader with the rest of the bump stack, and moving
  // them up is the round's second structural change. The reason is a measurement rather
  // than tidiness. Scenario farhilllight takes the far hillside apart with sky.ts's own
  // switches and reports three things about the ground at 300-500 m on that bearing:
  //   - it is fully lit (the terrain self-shadow term is 1.00 with a standard deviation
  //     of 3/255 over the whole slope, so nothing out there is shaded);
  //   - N.sun off the final normal has a MEDIAN of about 0.85 — the sun sits close to the
  //     surface normal — and cos is flat at its maximum, so eight degrees of relief moves
  //     the direct term by two or three per cent and no amount of extra amplitude fixes
  //     that, because it is the derivative of a cosine at zero;
  //   - aerial perspective is 45% of the pixel's radiance and takes the ground's own
  //     contrast from 7.3:1 down to 2.8:1 in linear.
  //
  // So on a sun-facing hillside, form alone CANNOT carry the read, and the previous three
  // rounds of this piece were right that something structural was missing and wrong about
  // which axis it was. What does carry it is that landform position and MATERIAL are not
  // independent on a real hillside: hollows collect moisture, litter and vegetation and go
  // dark and green; spurs are wind-scoured, stony and pale. Driving the material from the
  // same height field that drives the normal is not painting the light — it is the reason
  // the light and the colour agree on a real mountain, and it means the two reinforce
  // wherever the sun does rake (every other bearing, and every hour that is not mid
  // morning) instead of being two unrelated patterns.
  //
  // Cost is unchanged: the same two rlfReliefField calls, moved, not added.
  // ===========================================================================
  const float F_FORM = 0.042;
  float formLod = rlfBandLod(L, F_FORM * 2.02);
  float formAmt = smoothstep(${gl(TerrainShading.formNear)}, ${gl(TerrainShading.formFar)}, vWDist)
                * formLod * formLod
                * mix(1.0, RLF_FAR_FLOOR_RELIEF, L.farFade)
                * (1.0 - E.motion) * E.scale * uDbgB.x;
  // Branched like every other layer in this file: the two octaves are not sampled at all
  // inside formNear, which is where the frame is most often fill-bound.
  // The SLOPE is kept as well as the height, and it costs nothing: rlfReliefField already
  // returns ( height m, dh/du, dh/dv ) with the chain rule through the frequency scaling
  // done for us, and the previous rounds threw .yz away. That gradient is the input to the
  // landform light channel at the bottom of this shader — the far field's whole read.
  float formH = 0.0;
  vec2 formG = vec2(0.0);
  if (formAmt > 0.004) {
    vec3 fF = rlfReliefField(wp.xz + vec2(311.7, 57.3), F_FORM,
                             ${gl(TerrainShading.formRelief)}, 2);
    formH = fF.x * formAmt;
    formG = fF.yz * formAmt;
  }
  // The ridge band, for the ground past ~350 m where even a 24 m form is running out of
  // pixels. One octave, because at 90 m a second octave lands back on the field above.
  const float F_RIDGE = ${gl(TerrainShading.ridgeFreq)};
  float ridgeAmt = smoothstep(${gl(TerrainShading.ridgeNear)}, ${gl(TerrainShading.ridgeFar)}, vWDist)
                 * (1.0 - E.motion) * E.scale * uDbgB.x;
  float ridgeH = 0.0;
  vec2 ridgeG = vec2(0.0);
  if (ridgeAmt > 0.004) {
    vec3 rF = rlfReliefField(wp.xz + vec2(97.3, 613.9), F_RIDGE,
                             ${gl(TerrainShading.ridgeRelief)}, 1);
    ridgeH = rF.x * ridgeAmt;
    ridgeG = rF.yz * ridgeAmt;
  }
  // THE MASS BAND — 300 m, the coarsest landform scale in the file. See
  // TerrainShading.massRelief for why a fourth band and not more amplitude on the third:
  // the far field's remaining defect is that its structure is one spatial frequency, a comb
  // of parallel ribs, and no amplitude on a comb makes it a landmass. One octave, because
  // a second lands straight back on the 90 m band.
  const float F_MASS = ${gl(TerrainShading.massFreq)};
  float massAmt = smoothstep(${gl(TerrainShading.massNear)}, ${gl(TerrainShading.massFar)}, vWDist)
                * (1.0 - E.motion) * E.scale * uDbgB.x * uDbgD.z;
  float massH = 0.0;
  vec2 massG = vec2(0.0);
  if (massAmt > 0.004) {
    vec3 mF = rlfReliefField(wp.xz + vec2(-421.3, 188.7), F_MASS,
                             ${gl(TerrainShading.massRelief)}, 1);
    massH = mF.x * massAmt;
    massG = mF.yz * massAmt;
  }
  // Where this fragment sits in the landform: -1 in a hollow, +1 on a spur. Each band is
  // normalised by its own half-amplitude so the two arrive at the same weight and each
  // dies exactly where it already died, which is the same construction hAo uses at the
  // bottom of the shader.
  // The 2.6 is not a fudge, it is the difference between an fBm's RANGE and its SPREAD,
  // and getting it wrong cost this round an iteration. rlfReliefField hands back
  // (n - 0.5) * relief with n an fBm on 0..1 — but an fBm is a sum of octaves, so it piles
  // up around 0.5 with a standard deviation near 0.12 rather than filling its range.
  // Dividing by the authored half-amplitude therefore produces a quantity whose sd is 0.24,
  // not 1, and a material swap driven by it moved five per cent of the weights and nothing
  // in the frame. Scaled so a one-sigma hollow reads about -0.6 and the tails clamp.
  // The mass band comes in at 1.35 of its normalised weight rather than at 1.0, and that
  // is the one deliberate imbalance in the stack. The material split is what has to carry
  // "large distinct areas past 100 m", and at equal weight the 24 m and 90 m bands — which
  // vary several times inside one far hillside — average the coarse term away again at
  // exactly the range the gap is about. Weighted up, the coarse band decides which side of
  // the split a whole flank is on and the two finer ones texture it.
  float formPos = clamp((formH * ${gl(2 / TerrainShading.formRelief)}
                       + ridgeH * ${gl(2 / TerrainShading.ridgeRelief)}
                       + massH * ${gl(2.7 / TerrainShading.massRelief)}) * 2.6, -1.0, 1.0);

  vec2 fallD = vec2(0.0, 1.0);
  vec2 acrossD = vec2(1.0, 0.0);
  if (downLen > 1e-3) { fallD = downhill / downLen; acrossD = vec2(-fallD.y, fallD.x); }
  // NOTE, and it cost this round two whole iterations to find: never build a sampling
  // coordinate by projecting an *absolute* world position onto a direction that varies
  // with position. d/dp of dot(wp.xz, a(p)) is a + wp.xz . da/dp, and |wp.xz| out here is
  // hundreds of metres — so even a frame that rotates at 0.001 rad/m contributes a
  // spurious gradient of order 1, which swamps the real one and grows the further you get
  // from the origin. Sampling a noise field in that coordinate produced exactly what you
  // would expect: dense contour loops around every point where the fall line stagnates,
  // getting finer with distance. Wood grain and basket weave. Everything below therefore
  // samples in plain world xz and uses fallD/acrossD only inside dot products, which are
  // pointwise and carry no derivative of the frame at all.

  // --- cliff bands, talus aprons, benches ------------------------------------
  // The band coordinate is altitude, warped at 80 m and at 300 m so the bands sag and
  // rise across the massif instead of ruling it into a contour map, and with a period
  // that itself drifts regionally. Warp amplitude is deliberately tuned so the warp
  // gradient is comparable to the altitude gradient — enough that no two bands are
  // parallel for long, not so much that they close into blobs.
  // Two nested sets, which is how a real hillside is built and also how this survives
  // distance. MAJOR at 15 m of rise is 50 m of ground on a 30% face; at 400 m that is
  // 60 px, so its 12%-wide lip shadow is still a clean 7 px line. MINOR at 4.6 m
  // subdivides it and covers the gentle ground, where the major set stretches out into a
  // regional value change; it fades by *amplitude* as it nears its sampling limit rather
  // than by period, so there is no distance at which the band spacing visibly doubles.
  // The ratio is irrational-ish on purpose so the two never phase-lock into one comb.
  //
  // BOTH PERIODS ARE CONSTANT, and that is not a simplification, it is a correctness
  // requirement — the third and last instance of one mistake this round. bandT is an
  // *absolute* altitude, several hundred metres, divided by the period. d/dp of that
  // carries a term -bandWarp/per^2 * d(per)/dp, so with wp.y at 500 m and the period at
  // 15 m, a 1% wobble in the period moves the band coordinate by a third of a band. The
  // previous pass scaled the period by the local grade to keep the ground-projected band
  // constant; the grade comes off the analytic normal, which carries every bit of the
  // terrain's own half-metre detail, and the result was the whole rideable mid distance
  // corrugated at 2 m. Anything derived from an absolute world coordinate has to be
  // transformed by constants only. Grade still gates *masks* below, where it is a
  // multiplier and a wobble in it costs a wobble in a mask, not an explosion.
  float grade = clamp(downLen, 0.08, 0.95);
  const float bandPer = 15.0;
  const float minPer = 4.0;
  // Warp amplitudes are set against the *gradient* of the field doing the warping, not
  // against how far it moves the band. A 4-octave fBm at 0.0125 has a gradient near
  // 0.08/m — sixfold what the base octave alone suggests — so warping by 0.55 of the
  // period contributed 0.044 cycles/m of band while the altitude term on rideable ground
  // contributes 0.012. The warp was three times the signal, the "bands" were closed loops
  // of the 80 m noise field rather than contours, and on gentle ground they came out as
  // a 7 m corrugation. At 0.13 the two terms are comparable, which is what makes a band
  // wander convincingly instead of ruling a contour line or dissolving into blobs.
  //
  // Each set is warped against *its own* period. The previous pass reused the major
  // warp for the minor coordinate, so the minor set inherited a displacement three times
  // its own period — which is the same failure again, at a third of the scale.
  float bandWarp = wp.y + (macro.x - 0.5) * bandPer * 0.30
                        + (region.x - 0.5) * bandPer * 1.10;
  float minWarp = wp.y + (macro.x - 0.5) * minPer * 0.30
                       + (region.x - 0.5) * minPer * 1.10 + minPer * 0.37;
  float bandT = bandWarp / bandPer;
  float minT = minWarp / minPer;
  // fwidth of the band coordinate is exact antialiasing and an exact LOD in one: as a
  // band shrinks below a pixel the edges widen until the profile flattens to its mean,
  // which is a graceful death instead of a moire.
  float bandAA = clamp(fwidth(bandT) * 1.35, 0.010, 0.24);
  float minAA = clamp(fwidth(minT) * 1.35, 0.010, 0.30);
  // A BAND HAS TO BE FADED OUT BY ITS ON-SCREEN SPACING, NOT BY ITS SAMPLING LIMIT, and
  // that distinction turned out to be the larger half of this round's carried gap.
  // fwidth of a band coordinate is cycles per pixel by construction, so its reciprocal is
  // the spacing of the bands in pixels. R1 faded the minor set only as it approached
  // Nyquist — about 8 px — with the result that the entire far hillside was covered in
  // correct, hard-edged, form-following terraces spaced ten pixels apart. Ten-pixel
  // terraces are not terraces. They are hatching, and no amount of profile work fixes
  // that, which is exactly why the critic saw contour ruling and why pushing the
  // amplitude in the first two iterations of this round barely moved the image.
  //
  // A step in the ground needs room to be a step: call it 60 px to read fully and 25 px
  // to be gone. The minor set (4.6 m of rise, ~18 m of ground) therefore lives in the
  // 15-120 m band and hands the far field to the major set (15 m of rise, ~60 m of
  // ground), which is 3.3x coarser and survives correspondingly further out before it
  // too has to go and leave the mountain as pure landform.
  float minPx = 1.0 / max(fwidth(minT), 1e-5);
  float bandPx = 1.0 / max(fwidth(bandT), 1e-5);
  float minorAmt = smoothstep(24.0, 62.0, minPx);
  float majorAmt = smoothstep(11.0, 30.0, bandPx);

  // Gated on grade, NOT on slope. They are not the same number and the difference is
  // not small: slope above is 1 - cos(theta), which is 0.005 at a 10% grade and 0.04 at
  // 30%, while grade is sin(theta) and reads 0.10 and 0.29. Gating this block on
  // slope at 0.05 meant "steeper than 18 degrees", so every band and every channel
  // landed on the alpine face and none of it reached the rideable, forested ground from
  // 15 to 70 m — which is the entire band this round exists to fix.
  //
  // Only in swathes: a band that runs unbroken round the whole mountain is a contour
  // line, not a cliff.
  // The gate has to be firm, not just present. A band coordinate built from altitude
  // closes into a loop wherever the ground stops having a fall line — around the top of
  // every knoll and the bottom of every hollow — and a closed loop of lip shadow is a
  // dark disc, which reads as a burn mark rather than as a bench. Requiring a 13% grade
  // before any of this appears is what keeps them open curves.
  // The swathe gate was not gating anything, and a false-colour dump of the masks is what
  // finally showed it: bands ran unbroken over every square metre of every face. The
  // reason is that an fBm is NOT uniformly distributed — it is a sum of independent
  // octaves, so it piles up around 0.5 with a standard deviation near 0.12, and the R1
  // ramp of 0.22..0.60 sat two and a half sigma below the mean of its own input. A gate
  // has to be placed against the distribution of the field driving it, not against the
  // 0..1 range that field could theoretically reach.
  //
  // So the field is now computed once and thresholded twice, at two different points on
  // the same distribution. Cover varies over most of the hillside; the hard cliff
  // structure appears in swathes inside it, on roughly half the live ground, which is
  // what stops a benched face from becoming a contour map of the whole mountain.
  float bandFld = region.x * 0.62 + macro.x * 0.60 + mv * 0.22;   // mean 0.72, sd ~0.11
  // ROUND 3: BOTH RAMPS MOVED UP, BECAUSE THE BANDS HAD TO TERMINATE.
  // R2 fixed the gate's *placement* against the distribution but left it generous: the
  // cover ramp at 0.34..0.70 sits three sigma below the mean and so evaluated to 1.0 over
  // essentially the whole mountain, and the swathe ramp at 0.665..0.795 evaluated to ~0.4
  // everywhere rather than to 1 somewhere and 0 elsewhere. The result in the capture is a
  // set of dark stripes running unbroken from one edge of the far hillside to the other —
  // which is a contour map no matter how good the profile on it is. A cliff band on a real
  // mountain runs for a hundred metres and dies out into open slope.
  //
  // The driving field's own structure supplies that: macro is an 80 m fBm and region a
  // 310 m one, so a threshold near +0.5 sigma cuts the hillside into runs of roughly that
  // length.
  //
  // WHICH GATE DOES THE TERMINATING, AND WHY IT IS THIS ONE. The first attempt this round
  // tightened *both* ramps, and the capture showed exactly why that is wrong. bandLive
  // gates the whole section; bandSwathe gates only the three narrow members — riser, lip,
  // crest — which are the ones that carry the shading-normal tip and therefore all of the
  // form. Tightening bandSwathe to +1 sigma left the wide members (apron -> pale scree,
  // bench -> grass and pack) running at full strength with no riser between them, which is
  // a hillside painted in alternating pale and olive contour stripes and nothing else: the
  // carried gap, reproduced exactly, from the opposite direction.
  //
  // So bandSwathe is generous — wherever a band exists it gets a real step — and bandLive
  // is the tight one, so the *whole* section including its cover terminates together and
  // roughly a fifth of the hillside carries no benching at all with a soft edge either
  // side of it. Terminating a band means the cover goes with it, not the form.
  // WAVE 4 — THE BANDS ARE A MID-FIELD FEATURE AND THEY NOW STOP BEING ONE AT 300 m.
  //
  // majorAmt fades a band when it gets closer than 30 px on screen, which on a 15 m
  // section is never inside the far plane: at 400 m a major band is still sixty pixels
  // apart, so the set ran unbroken to the horizon. That was defensible while the bands
  // were the only structure out there. It is not defensible now that the tilt dump
  // (view 5) shows them contributing the largest normal perturbation on the whole far
  // hillside — up to 30 degrees, in strictly iso-elevation lines, on ground where the sun
  // sits close to the surface normal so a 30 degree tip is the ONE thing that can still
  // swing cos. That is the mechanism by which "contour-band stripes" outcompeted every
  // other cue in the frame, and it is why raising the band amplitude in wave 2 and wave 3
  // made the complaint worse rather than better.
  //
  // So the section hands over: bands own 15-160 m, where a bench reads as a bench and is
  // ground you might actually ride; TerrainShading.formRelief owns 160 m outward, where
  // what a hillside should show is spurs and hollows, not contours. Because bandLive gates
  // the whole section — cover, albedo, normal tip and undercut together — the handover
  // leaves nothing behind.
  //
  // WAVE 4 ROUND 2 — THE HANDOVER MOVES IN, AND THE SWATHE GATE NOW TIGHTENS WITH IT.
  //
  // The paragraph above is right and did not go far enough. Two frames settle it, both
  // from this round's captures: farlayerab 16p0s is the far hillside with the whole band
  // block switched off and it is indistinguishable from 8p0s — so at 300 m the bands are
  // already contributing nothing and the 160-330 handover was paying for a layer that is
  // not there. And rockcam 10p0s is a bedrock face at roughly 60-200 m where the bands
  // are the ONLY thing the eye picks up: a stack of parallel horizontal terraces running
  // the full width of the slope. That is precisely "iso-elevation banding is still the
  // dominant readable pattern", one range closer in than the handover was written for.
  //
  // So the section hands over at 120-260 m rather than 160-330, and over the same range
  // the swathe gate tightens — a band still gets its full section wherever it survives,
  // but it survives on progressively less of the hillside as the hillside gets further
  // away. Tightening the GATE rather than fading the amplitude is the same lesson the
  // block clast layer learned from its density LOD: a hillside carrying half the benches
  // at full strength reads as a bedded face with spurs between the outcrops, while the
  // same hillside carrying all of them at half strength reads as ruling on a dome.
  //
  // Deliberately distance-dependent and not global: inside ~60 m a bench is ground you can
  // see the shape of and ride onto, and wave 2 established at length that thinning it there
  // is what made the mid distance flat in the first place.
  float bandThin = smoothstep(60.0, 220.0, vWDist);
  float bandFar = 1.0 - smoothstep(120.0, 260.0, vWDist);
  float bandLive = smoothstep(0.13, 0.30, grade)
                 * smoothstep(mix(0.635, 0.700, bandThin), mix(0.785, 0.860, bandThin), bandFld)
                 * structOn * majorAmt * bandFar * uDbgA.y;
  float bandSwathe = smoothstep(0.585, 0.735, bandFld);
  // How much bedrock the riser is made of. On a steep face it is a cliff; on a forest
  // bench the same step is an earth scarp — a slide edge, an old cut, the lip of a
  // hollow — and painting bare granite on that is the fastest way to make an authored
  // field look like it was applied rather than found.
  float rockRiser = smoothstep(0.30, 0.62, grade);

  // Fans have fingers; a ruled lower edge is the most synthetic thing a talus apron can
  // do, so the toe height is modulated across the hill. floor() of the band coordinate,
  // not the coordinate itself: the toe threshold has to be constant *within* a band or
  // it moves with the value it is being compared against and the edge doubles back on
  // itself. The discontinuity floor() introduces sits at the fract seam, which is buried
  // in the middle of the bench where nothing reads it.
  // ~18 m lobes on both sets. The minor set had them at 6 m in the previous pass, which
  // at 300 m is 8 px and came out as a row of beads strung along every band edge — a
  // scallop that reads as a decorative border, not as the snout of a fan.
  float apronToe = 0.38 + 0.26 * gNoiseD(wp.xz * 0.055 + floor(bandT) * 5.71).x;
  float minToe = 0.38 + 0.26 * gNoiseD(wp.xz * 0.062 + floor(minT) * 5.71 + 8.3).x;
  // The two sets are averaged rather than added, which is legal because each profile
  // sums to 1: the total is still a partition of the surface, so nothing can pile up.
  vec4 prof = mix(gBandProfile(fract(bandT), bandAA, apronToe),
                  gBandProfile(fract(minT), minAA, minToe),
                  minorAmt * 0.45);
  // The crest is the remainder of the partition, so it survives the mix() above exactly.
  float profCrest = max(1.0 - (prof.x + prof.y + prof.z + prof.w), 0.0);

  float apron = prof.z * bandLive;
  float benchBand = prof.w * bandLive;
  // The riser, its undercut and the crest above it need a steeper gate than the cover
  // does, and this is the single most important line in the block. They are the three
  // *narrow* members of the section — 12% of a band each — so wherever the band
  // coordinate has a local extremum they close into a small disc rather than a long
  // line, and the ground would come out spotted on every knoll top. The wide members
  // (apron, bench) degrade gracefully into a patch of cover, which is fine at any grade.
  //
  // Relaxed from 0.20/0.42 to 0.145/0.33 this round, and that is only safe *because* the
  // three narrow members are now mean-preserving as a group. At the old thresholds a 25%
  // grade — which is most of the rideable mid distance — got bandSteep = 0.13, so the
  // cliff structure was gated almost entirely off exactly where the round needed it, and
  // what survived was the faint ruling the critic measured. The old gate could not be
  // opened because opening a subtract-only profile just makes the mountain darker in
  // stripes; opening a balanced one makes it read as terraces.
  float bandSteep = smoothstep(0.145, 0.33, grade) * bandSwathe;
  float riser = prof.x * bandLive * bandSteep;
  float lipDark = prof.y * bandLive * bandSteep;
  float crestLit = profCrest * bandLive * bandSteep;

  // --- drainage ---------------------------------------------------------------
  // An isotropic ridged level set in plain world xz — no rotated frame, for the reason
  // above — with the downhill bias applied afterwards, as a *mask* rather than as a
  // stretch. A level set runs perpendicular to its own gradient, so a channel of this
  // field runs down the fall line exactly when its gradient points across the hill; that
  // is one dot product of two unit vectors and it is completely stable. The network that
  // comes out is dendritic rather than combed, which is also what drainage looks like:
  // the segments that happen to run downhill survive, the ones that run along the contour
  // are dropped, and the survivors join up.
  const float CH_F = 0.055;
  vec3 chN = gFbmD(wp.xz * CH_F + 61.3, 3, 0.52);
  float cu = chN.x * 2.0 - 1.0;
  float ridgeV = 1.0 - abs(cu);
  vec2 dRidge = -sign(cu) * 2.0 * chN.yz * CH_F;
  float chGl = length(chN.yz);
  // Level sets of a ridged field close into rings around every extremum of the underlying
  // noise, and a ring of dark is a crater — the most obviously synthetic mark this shader
  // can leave. The gradient magnitude is exactly the tell: it collapses toward zero at an
  // extremum and is healthy along a real channel, so gating on it removes the rings and
  // keeps the runs, without touching anything else.
  // The magnitude half of this gate was set an order of magnitude too high and shut the
  // whole drainage network off — a false-colour dump of the three channel masks showed the
  // raw level set firing across the far hillside and chLive at zero everywhere on it, so
  // not one gully has been visible in any capture for two rounds. gFbmD returns its
  // gradient in the *sampling* coordinate and normalises by the octave sum, which puts a
  // healthy slope somewhere around 0.4-0.9, not the 0.9-2.3 the ramp was asking for. The
  // job of the term is only to reject the neighbourhood of an extremum, where the level
  // set closes into a ring, so it needs to sit just above zero.
  float chAlign = chGl > 1e-4
    ? smoothstep(0.24, 0.80, abs(dot(chN.yz / chGl, acrossD))) * smoothstep(0.16, 0.55, chGl)
    : 0.0;
  float chAA = clamp(fwidth(ridgeV) * 1.1, 0.0, 0.10);
  // A level set of a single-frequency field comes out evenly spaced, and evenly spaced
  // parallel lines down a hillside are not drainage, they are corduroy — the first pass
  // read as plough furrows for exactly this reason. Moving the threshold around at 6 m
  // and at 80 m costs nothing and turns the comb into a network: where the threshold is
  // high the channel closes up entirely and leaves an interfluve, where it is low a
  // whole catchment opens out.
  // Threshold and ramp both tightened now that the gate above actually lets the field
  // through: at 0.60 with a 0.20 ramp the "channel" was a third of the hillside, which is
  // a stain, not a watercourse. 0.76 with a 0.11 ramp is a ribbon a few metres across.
  float chThr = 0.76 + 0.16 * clamp(0.5 + mac * 0.95 + fvC * 0.55, 0.0, 1.0);
  float dChan;
  // The channel opens up with distance, and it has to. A 3 m creek bed is the right width
  // when you are standing next to it and it is a sub-pixel hairline at 400 m, where the
  // thing you actually see of a drainage is the whole incised valley around it. Widening
  // the level-set band with distance keeps the *network* identical — same threshold field,
  // same dendritic pattern — and just lets more of each catchment through, so the far
  // hillside gets legible fall-line structure instead of a ramp that antialiasing has
  // flattened to nothing. R2's version could not: its ramp width was dominated by chAA,
  // which past ~150 m exceeded the whole distance from the threshold to 1.0, so chanRaw
  // never reached 1 anywhere and the drainage simply was not in any far-field capture.
  float chWide = 0.11 + 0.13 * smoothstep(50.0, 280.0, vWDist);
  float chanRaw = gStepD(chThr, chThr + chWide + chAA, ridgeV, dChan);
  // A channel needs a slope to cut and a catchment to cut with: nothing incises the top
  // of a ridge, everything incises the lower third of a face.
  float chLive = structOn * chAlign * uDbgA.w * smoothstep(0.12, 0.30, grade)
               * smoothstep(0.92, 0.30, alt)
               * (0.40 + 0.60 * smoothstep(0.22, 0.72, macro.x));
  float channel = chanRaw * chLive;
  // Incision, in metres of drop per metre of ground. Clamped rather than tuned down:
  // the level-set derivative spikes where the noise gradient does, and a 70 degree gully
  // wall lit from behind is a black gash, not a landform.
  vec2 chanG = (-0.95 * dChan * chLive) * dRidge;
  float chanGL = length(chanG);
  if (chanGL > 0.85) chanG *= 0.85 / chanGL;

  // --- the shading normal the structure implies -------------------------------
  // A terrace is a horizontal offset as a function of altitude, which is not a height
  // field, so the honest way to build one is to tip the shading normal directly rather
  // than to invent a displacement whose gradient happens to look right. This is where
  // most of the mid-distance read comes from: a bench catching the sun above a riser
  // that has turned away from it is legible at 300 m with no detail in it at all.
  // A terrace is flat treads separated by steep risers whose *average* is the hill it is
  // cut into, so the tips have to balance the same way the albedo does: width times tip
  // toward vertical on the flats equals width times tip toward the fall line on the
  // faces. Crest gets the strongest tip of all — it is the one part of the section that
  // is genuinely turned up at the sky — and the lip joins the riser on the steep side,
  // because an undercut faces further away from the sun than the face above it.
  //
  // WAVE 2 ROUND 3 — THE STEP IS NOW MOSTLY THIS, NOT THE ALBEDO TERM BELOW.
  // R2 split the work roughly half and half between a flat albedo multiplier (K_LIP 0.56)
  // and a gentle normal tip (riser 0.58 of a 1.15 fall-line lean). Measured on the far
  // hillside that came out as a painted dark stripe: an albedo mark has no dependence on
  // where the sun is, so it looks identical on the sunlit and the shaded side of a spur,
  // and identical again under cloud. That is the definition of "expresses its structure as
  // flat albedo marks instead of form catching light".
  //
  // The tip is now the primary term and it is strong: 1.7 of the fall line puts the riser
  // face about 60 degrees off the bench it is cut into, which is a real scarp, and the
  // crest goes fully to vertical. What the eye gets from that is a step whose dark side is
  // wherever the sun is not — so the same band reads bright-over-dark on one aspect and
  // dark-over-bright on the other, terminates where the shading says it terminates, and
  // flattens honestly toward its mean as it goes sub-pixel instead of leaving a mark
  // behind. The albedo terms that remain are the ambient half — an undercut is genuinely
  // occluded from the sky as well as turned away from the sun — at about a third of their
  // old weight.
  vec3 fall3 = vec3(fallD.x, 0.0, fallD.y);
  vec3 nSteep = normalize(gN + fall3 * 2.10);
  float tipAmt = bandSteep;                    // same reasoning as the lip shadow
  // Pre-gate values: riser/lipDark/crestLit already carry bandSteep, and applying tipAmt
  // to those would square it and gut the tip on exactly the moderate ground this round
  // is about.
  //
  // The compensation is CONCENTRATED on the crest rather than spread over the bench, and
  // that is a deliberate reversal of the first pass. Spread over 40% of the section a
  // lift is a broad, shallow gradient that the eye reads as no feature at all, while the
  // darkening it is balancing sits on a 14% strip and reads as a hard line — so the band
  // came out net-dark to look at even where the arithmetic was balanced. Narrow against
  // narrow: the crest is the same width as the lip, so its lift lands as an edge too.
  float profCrestLive = profCrest * bandLive;
  float upTip = clamp((benchBand * 0.18 + apron * 0.13 + profCrestLive * 1.00) * tipAmt,
                      0.0, 1.0);
  vec3 structN = normalize(mix(gN, vec3(0.0, 1.0, 0.0), upTip));
  float dnTip = clamp((prof.x * 1.00 + prof.y * 0.34) * bandLive * tipAmt, 0.0, 1.0);
  structN = normalize(mix(structN, nSteep, dnTip));

  // --- decode the vertex surface mixture --------------------------------------
  vec3 vc = vColor;
  float luma = (vc.r + vc.g + vc.b) * 0.3333333;
  float chroma = max(max(vc.r, vc.g), vc.b) - min(min(vc.r, vc.g), vc.b);
  float green = vc.g - (vc.r + vc.b) * 0.5;

  float wSnow = smoothstep(0.50, 0.72, luma);
  float rest = 1.0 - wSnow;
  float wRock = (1.0 - smoothstep(0.022, 0.118, chroma)) * rest;   // rock is the only grey
  rest -= wRock;
  float wGrass = smoothstep(0.014, 0.082, green) * rest;           // grass is the only green
  rest -= wGrass;
  float sLoose = smoothstep(0.315, 0.375, luma);
  float sHard = smoothstep(0.235, 0.298, luma) * (1.0 - sLoose);
  float sRoot = smoothstep(0.172, 0.198, luma) * (1.0 - sLoose) * (1.0 - sHard);
  float sLoam = max(0.0, 1.0 - sLoose - sHard - sRoot);
  float wLoose = rest * sLoose;
  float wHard = rest * sHard;
  float wRoot = rest * sRoot;
  float wLoam = rest * sLoam;

  // --- slope: soil cannot hold on the steeps ----------------------------------
  // The vertex classifier does this too, but only at vertex resolution — 32 m on the
  // outer rings. Doing it per pixel is what gives cliffs a broken, outcropped edge.
  // Angle of repose, in two stages, because that is how a hillside actually works.
  // Fines wash off a steep face long before the bedrock shows: first the soil coarsens
  // to scree — nothing finer than gravel sits above about 33 degrees — and only past the
  // repose angle does the rock go bare.
  //
  // WAVE 5 R2: the two gates below are now stated as ANGLES in TerrainRepose and
  // converted by slopeAt(), because 'slope' here is 1 - cos(angle) and every constant
  // in this block had been authored as if it were a grade. Measured against the real
  // heightfield, the shipping steepRock fired on 0.0% of the ground at every station on
  // the mountain — see the block above TerrainRepose for the census. The macro term
  // stays: it is +-2 degrees of local variation on the onset, which is what stops the
  // boundary reading as a contour line.
  float screeLo = ${gl(slopeAt(TerrainRepose.screeOnsetDeg))} + (macro.x - 0.5) * 0.030;
  float scree = smoothstep(screeLo,
                           screeLo + ${gl(slopeAt(TerrainRepose.screeFullDeg) - slopeAt(TerrainRepose.screeOnsetDeg))},
                           slope);
  {
    // WAVE 5 R2: hardpack's share of the take goes 0.85 -> 0.60. With the onset in the
    // right units this gate went from firing on 1-2% of the mountain to 12-20% of it, and
    // at 0.85 that emptied the mountain of its dominant ground in the other direction —
    // measured, the near band came back 0.0% hardpack / 68.6% loose / 22.5% rock, which is
    // the same "one material" defect wearing a cold coat. A shedding face keeps its
    // compacted matrix; the scree is the coarse fraction lying ON it, not a replacement
    // for it. Loam, root and grass keep their old shares — an organic horizon really does
    // leave when the slope sheds.
    float m1 = (wHard * 0.60 + wLoam * 0.70 + wRoot * 0.80 + wGrass * 0.60) * scree;
    wHard *= 1.0 - scree * 0.60;
    wLoam *= 1.0 - scree * 0.70;
    wRoot *= 1.0 - scree * 0.80;
    wGrass *= 1.0 - scree * 0.60;
    wLoose += m1;
  }
  // The band widens with 'foot' — the texel footprint in metres — so that a face which is
  // only a few pixels tall does not get a hard bedrock edge drawn across it. That part was
  // always right; what was wrong was the onset.
  float rockLo = ${gl(slopeAt(TerrainRepose.rockOnsetDeg))}
               + (macro.x - 0.5) * 0.040 + (region.x - 0.5) * 0.024;
  float rockBand = ${gl(slopeAt(TerrainRepose.rockFullDeg) - slopeAt(TerrainRepose.rockOnsetDeg))}
                 + min(foot * 0.04, 0.16);
  float steepRock = smoothstep(rockLo, rockLo + rockBand, slope);
  {
    float keep = 1.0 - steepRock;
    wLoose *= keep; wHard *= keep; wRoot *= keep; wLoam *= keep; wGrass *= keep;
    wRock = wRock * keep + steepRock * (1.0 - wSnow);
  }

  // Benches collect and compact what washes off the steeps.
  //
  // WAVE 5 R2: 17 down to 5 degrees, from 35 down to 14. Measured, the old pair called
  // 85-93% of the mountain a bench, and this block MERGES — it takes scree and settles it
  // back into hardpack and loam. A convergence term running on nine tenths of the ground
  // is the mid-brown wash by itself. At 17 degrees it runs at roughly 40% and it is now
  // disjoint from 'scree' (which starts at 22), so the two can never fight over the same
  // pixel: what a face sheds, a bench keeps, and nothing does both.
  float bench = smoothstep(${gl(slopeAt(TerrainRepose.benchOnsetDeg))},
                           ${gl(slopeAt(TerrainRepose.benchFullDeg))}, slope)
              * (1.0 - wSnow) * (1.0 - wRock);
  {
    float settle = bench * 0.32;
    float moved = wLoose * settle;
    wLoose -= moved;
    wHard += moved * 0.7;
    wLoam += moved * 0.3;
  }

  // A hillside is never one material edge to edge. Mid- and macro-scale patches of
  // packed ground through the scree, and bare soil through the grass, so there is
  // material variation at 6 m and at 80 m as well as at the classifier's boundaries.
  {
    // 0.62 -> 0.40, and held off live scree. Packed ground forms where feet, tyres and
    // water have worked a surface that is stable enough to be worked; it does not form on
    // a face that is shedding. At 0.62 unconditioned this was taking about a third of ALL
    // loose on the mountain back into hardpack, on top of the bench settle above — the
    // pale member of the ladder was being manufactured by one term and removed by three.
    float packPatch = smoothstep(0.40, 0.78, mv * 0.55 + macro.x * 0.60)
                    * 0.40 * (1.0 - scree * 0.80);
    float m1 = wLoose * packPatch;
    wLoose -= m1; wHard += m1;
    float barePatch = smoothstep(0.50, 0.82, macro.x * 0.5 + region.x * 0.7) * 0.40;
    float m2 = wGrass * barePatch;
    wGrass -= m2; wLoam += m2 * 0.6; wHard += m2 * 0.4;
  }

  // --- each band carries its own cover ----------------------------------------
  // This is where the mid distance gets its hue and value separation. A hillside that
  // is one material from 15 m to the horizon can only be read from noise amplitude. A
  // hillside whose risers are bare rock, whose aprons are pale scree, whose benches
  // carry grass and dark soil, and whose channels run dark and gravelly is read from
  // shape — which is what survives distance, fog and a grazing view angle.
  float notSnowW = 1.0 - wSnow;
  {
    // Riser: bedrock on a face, cut subsoil on a bench. The bench case is the accent —
    // a fresh scarp is the brightest, most saturated clay on the mountain, and having it
    // appear on a hard edge rather than as a blotch is most of why it reads as a cut.
    float f = riser * 0.90 * notSnowW;
    float k = 1.0 - f;
    float m = (wHard + wLoam + wRoot + wGrass + wLoose) * f;
    wHard *= k; wLoam *= k; wRoot *= k; wGrass *= k; wLoose *= k;
    wRock += m * rockRiser;
    wHard += m * (1.0 - rockRiser) * 0.72;
    wLoose += m * (1.0 - rockRiser) * 0.28;
  }
  {
    // Crest: the nose of the bench, standing above the riser. Wind gets at it, water
    // runs off it, nothing collects on it — so it is stripped back to whatever is under
    // the cover. This is the *cover* half of the mean-preserving profile, paired with the
    // lip block below at the same width and the opposite sign.
    //
    // Which pale surface it strips to has to follow the bedrock fraction, and getting
    // that wrong was the first iteration's biggest single error in reverse: a crest that
    // is always scree paints a pale mineral stripe across forest benches, and a lip that
    // is always dark soil paints a black organic stripe across a bare rock face. Both
    // read as applied rather than found, which is precisely the thing the art direction
    // warns about. So: pack on soil ground, weathered gravel on rock ground.
    float f = crestLit * 0.66 * notSnowW;
    float k = 1.0 - f;
    float m = (wLoam + wRoot + wGrass) * f + wRock * f * 0.34;
    wLoam *= k; wRoot *= k; wGrass *= k;
    wRock *= 1.0 - f * 0.34;
    wHard += m * (0.44 - 0.10 * rockRiser);
    wLoose += m * (0.56 + 0.10 * rockRiser);
  }
  {
    // Lip: the undercut at the foot of the riser. In shade most of the day, collecting
    // every fine that falls out of the face above it, and staying damp. On a soil scarp
    // that is dark humus and root; on a cliff it is shaded, damp bedrock — cool and dark,
    // not black.
    //
    // DELIBERATELY SMALL, and measuring the band profile is what forced that. The lift
    // and the darkening are only balanced if the *material* either side of the section is
    // symmetric too, and it is not: on a scree or rock face the crest strips to loose,
    // which is already what the surrounding ground is made of and so lifts nothing, while
    // the lip drops loose (mid 0.25) to loam (mid 0.07), which is minus seventy per cent.
    // A big lip cover swap therefore re-imports exactly the dark-dominated profile the
    // multiplier below was built to remove. The cover swap now only tints — it moves the
    // hue and a little of the value — and the balanced multiplier carries the value step.
    float f = lipDark * 0.24 * notSnowW;
    float k = 1.0 - f;
    float m = (wHard + wLoose + wGrass) * f;
    wHard *= k; wLoose *= k; wGrass *= k;
    wRock += m * (0.30 + 0.55 * rockRiser);
    wLoam += m * (0.70 - 0.55 * rockRiser) * 0.62;
    wRoot += m * (0.70 - 0.55 * rockRiser) * 0.38;
  }
  {
    // Apron: talus, pale scree. Held back from 0.70 so that the crest is the only
    // *concentrated* pale member of the section — an apron as pale as the crest and three
    // times as wide puts the bright side of every band on the wrong element and leaves
    // the dark lip as the only hard edge in the profile.
    float f = apron * 0.52 * notSnowW;
    float k = 1.0 - f;
    float m = (wHard + wLoam + wRoot + wGrass) * f + wRock * f * 0.72;
    wHard *= k; wLoam *= k; wRoot *= k; wGrass *= k;
    wRock *= 1.0 - f * 0.72;
    wLoose += m;
  }
  {
    // Benches. Which cover a bench carries is not arbitrary — it is moisture and
    // altitude — so a run of benches down one face reads as a sequence rather than as
    // a dither, and the same face gives you green low down and bare pack up top.
    float f = benchBand * 0.66 * notSnowW;
    float m = wLoose * f + wRock * f * 0.55;
    wLoose *= 1.0 - f;
    wRock *= 1.0 - f * 0.55;
    float lush = clamp(0.5 + reg * 0.80 + mac * 0.40 - (alt - 0.34) * 1.5, 0.0, 1.0);
    wGrass += m * lush * 0.60;
    wLoam += m * lush * 0.22;
    wHard += m * (1.0 - lush * 0.82);
  }
  {
    // Channel floors: washed down to gravel and bedrock, and darker than their banks.
    float f = channel * 0.70 * notSnowW;
    float k = 1.0 - f;
    float m = (wHard + wLoam + wRoot + wGrass) * f;
    wHard *= k; wLoam *= k; wRoot *= k; wGrass *= k;
    wLoose += m * 0.58;
    wRock += m * 0.42;
  }
  // --- landform position drives material, 60 m outward --------------------------
  // The one thing on a distant hillside that is BOTH a value structure and a form: what
  // grows where. Hollows hold water, litter and timber and read dark and green; spurs are
  // wind-scoured, thin-soiled and pale. It is the same field that perturbs the normal, so
  // wherever the sun does rake — every bearing but this one, and every hour that is not
  // mid morning — the colour and the light say the same thing about the same landform
  // instead of being two unrelated patterns laid over each other. And where the sun is
  // normal to the slope and can say nothing at all, this still reads.
  //
  // Sized against what the haze leaves. Measured on the signature bearing, aerial
  // perspective takes the ground from 7.3:1 to 2.8:1 in linear, so a material pair has to
  // be worth roughly twice on the surface what it needs to be worth on screen. Green over
  // pale mineral is the right pair for that: it separates by hue AND by value, and hue
  // survives haze far better than value does because the haze is itself a blue wash and
  // green is the one direction on the wheel it does not move.
  {
    float lfFar = smoothstep(60.0, 150.0, vWDist) * notSnowW;
    if (lfFar > 0.004) {
      float holl = clamp(-formPos, 0.0, 1.0) * lfFar * 0.70;
      float spur = clamp(formPos, 0.0, 1.0) * lfFar * 0.56;
      {
        float m = (wLoose + wHard) * holl + wRock * holl * 0.45;
        wLoose *= 1.0 - holl; wHard *= 1.0 - holl; wRock *= 1.0 - holl * 0.45;
        float lush = clamp(0.5 + reg * 0.80 - (alt - 0.42) * 1.8, 0.0, 1.0);
        wGrass += m * (0.40 + 0.34 * lush);
        wLoam += m * (0.60 - 0.34 * lush);
      }
      // The spur half goes ENTIRELY to loose, and the first pass of it sending a third to
      // rock is worth writing down as a mistake. The surface-weight dump says the far
      // hillside is already about 85% rock — the vertex classifier puts it there — so rock
      // is not a member this material can separate anything against out here; it is the
      // background. Adding more of it made the hillside more uniform and the measured
      // albedo contrast FELL from 2.25:1 to 1.94:1. Everything this block does now moves
      // weight AWAY from the majority member in both directions: green into the hollows,
      // pale mineral onto the ribs.
      {
        float m = (wGrass + wLoam + wRoot) * spur + wRock * spur * 0.55;
        wGrass *= 1.0 - spur; wLoam *= 1.0 - spur; wRoot *= 1.0 - spur;
        wRock *= 1.0 - spur * 0.55;
        wLoose += m * 0.80;
        wHard += m * 0.20;
      }
    }
  }

  // ===========================================================================
  // LANDFORM-SCALE MINERAL COVER — talus fans against bedrock, 45 m to the far plane.
  //
  // THE MATERIAL HALF OF THE CARRIED GAP: "hardpack, rock and scree do not separate out
  // there by value or hue at all." Dumped with __gripTerrainShading.view(1) — the
  // surface weights as false colour, scenario farhilldbg shot 7p0s — the baseline far
  // hillside is rock, hardpack and grass in blotches, with almost no LOOSE anywhere on it.
  // That is the entire failure in one image. The palette block four hundred lines below
  // spent two rounds spreading rock (mid luma 0.148, cold) against loose (0.259, warm) to
  // 1.75:1 specifically so the far field would have a value structure, and then the far
  // field never contained any loose: what it contained was rock at 0.148, hardpack at 0.18
  // and grass at 0.13, which is 1.4:1 with the pale member missing. A palette cannot
  // separate surfaces that are not there.
  //
  // Loose only ever arrived out there through the cliff-band apron, which is a member of a
  // contour section — so the one pale surface on the mountain was being delivered in
  // horizontal stripes. Hence "two painted patterns".
  //
  // This is the same operation the duff mat below uses and it is the entry point the
  // relief library was built around, run at landform scale instead of at mat scale:
  //
  //   PATCHES, NOT STRIPES. 78 m of ground, from the shared rlfCoverField, with an
  //   eighteen-metre fingered toe. A talus fan is a patch with a lobate snout; nothing
  //   about it is iso-elevation.
  //
  //   PLACED BY SLOPE, NOT BY NOISE ALONE. The bias goes on the LEVEL, which is the
  //   lesson the duff block learned the hard way — a cover faded out by a mask has no
  //   boundary left, and the boundary is the feature. Fans gather under steep ground and
  //   thin out on the benches and above the treeline.
  //
  //   AND IT HAS A METRE OF THICKNESS. That is what makes this different from every
  //   previous attempt at far-field material, all of which were weight swaps and therefore
  //   paint. A 0.95 m apron gives rlfBumpH a real shoulder along its upper edge and a real
  //   toe at its snout, and rlfCoverBlend hands back an occlusion band in the ground banked
  //   against that toe. So the fan is lit differently from the face it lies on, from a
  //   different direction, and it still reads in overcast where the value swap alone would
  //   not.
  //
  //   THE PAIR IS TWO-SIDED. Inside the fan, pale warm scree. Outside it, on ground steep
  //   enough that nothing could lie there, the face is stripped back to cold bedrock. One
  //   boundary, both members moving, which is twice the contrast of swapping one.
  // ===========================================================================
  float minW = 0.0;    // 0..1 how much of this pixel is talus fan
  float minH = 0.0;    // metres of apron standing above the face, for the normal
  float minOcc = 1.0;  // the ground banked against the toe, for the light
  {
    float minFar = smoothstep(45.0, 130.0, vWDist) * notSnowW * uDbgB.w;
    if (minFar > 0.004) {
      RlfCoverParams MP = rlfCoverDefaults();
      MP.level = 0.50
               - 0.11 * smoothstep(0.10, 0.36, slope)        // fans gather under the steeps
               + 0.10 * bench                                // and not out on a flat
               - 0.09 * formPos                              // and on the ribs, not in the timber
               + 0.07 * smoothstep(0.30, 0.72, alt)          // less soil to bury up high...
               - 0.06 * smoothstep(0.62, 0.95, alt);         // ...but the summits are rock
      MP.margin = 0.050;
      MP.breakFreq = 0.055;                                  // ~18 m lobes on the snout
      MP.breakAmp = 0.24;
      MP.depth = 1.25;
      MP.occ = 0.55;
      MP.grain = 0.55;
      // Plain wp.xz, not the warped domain: wpw is displaced by the 80 m macro field, and
      // at 78 m of patch that displacement is comparable to the patch itself — the fans
      // would follow the warp rather than the hill.
      float mFld = rlfCoverField(wp.xz + vec2(517.0, 233.0), 78.0, 5.9);
      RlfCover MC = rlfCoverBlend(mFld, wp.xz, L, MP);
      minW = MC.w * minFar;
      minH = MC.h * minFar;
      // The rim band gets the same treatment the duff mat's does and for the same reason:
      // fwidth on a hillside seen at a grazing angle is dominated by the near-vertical
      // screen axis, so an unfaded contact band comes out as swathes rather than as a
      // line. A 3 m feature is gone by roughly a kilometre, which is past the clipmap.
      minOcc = mix(1.0, MC.occ, minFar * rlfBandLod(L, 0.33));

      // Inside the fan: pale warm scree — but PARTIAL, which is the duff mat's hardest-won
      // lesson applied at forty times the size. The first pass took 88% and the capture
      // showed exactly what a total swap gives you at 400 m: a flat pale sticker, because
      // every detail layer loose owns is below Nyquist out there, so a pixel that is 100%
      // loose is the middle stop of one ramp and nothing else. At 0.52 the fan keeps half
      // of whatever hillside it is lying on, so it carries that hillside's own macro
      // variation, its oxide belt and its aspect tint across its span — and the thing that
      // makes it read as a fan rather than as a tint is its thickness, not its opacity.
      // A sixth of the take goes to hardpack rather than to loose for the same reason a
      // real apron is not one grade: the toe is finer than the head.
      float f = minW * 0.80;
      float m = (wLoam + wRoot + wGrass) * f + wRock * f * 0.86;
      wLoam *= 1.0 - f; wRoot *= 1.0 - f; wGrass *= 1.0 - f;
      wRock *= 1.0 - f * 0.86;
      // Split, not pure. A fan taken all the way to loose is one ramp's middle stop and
      // nothing else at 400 m, which is the flat pale sticker the first pass shipped; a
      // fan that is three parts talus to one part packed silt keeps hardpack's orange clay
      // in the mix and comes out warm tan rather than chalk. Hardpack is also left out of
      // the take above — the fan lies over the packed ground, it does not eat it.
      wLoose += m * 0.74;
      wHard += m * 0.26;
      // Outside it, and only where the ground is genuinely too steep to hold anything:
      // cold bedrock. Deliberately small — see the note in the landform block above about
      // rock already being the majority member out here — and gated hard on slope so a
      // forested bench does not turn to granite the moment it falls outside a fan. Its job
      // is to keep the fan's margin two-sided, not to add rock to a hillside that is
      // already made of it.
      float g = (1.0 - MC.w) * minFar * smoothstep(0.22, 0.52, slope) * 0.30;
      float m2 = (wLoose + wHard + wLoam + wRoot + wGrass) * g;
      wLoose *= 1.0 - g; wHard *= 1.0 - g; wLoam *= 1.0 - g;
      wRoot *= 1.0 - g; wGrass *= 1.0 - g;
      wRock += m2;
    }
  }

  // ===========================================================================
  // MINERAL LAG — the near field's SECOND MATERIAL on ground that is not steep.
  //
  // THE MEASUREMENT THAT FORCED THIS, and it is the one number in this round that changed
  // what got built. With the slope gates put into the right units the FAR band came back
  // with six classes (hardpack 77.9% loose 8.8% grass 7.9% loam 4.2% root 0.9% rock 0.4%)
  // and the NEAR band came back 99.9% hardpack — one class, on the vista frame a rider
  // spends the whole game looking at. Reading the frame explains it in one look: the
  // trailhead is a broad, gentle plateau. Its p50 slope is 12-15 degrees and its p90 is 26,
  // so no repose gate at any honest angle fires there, and the vertex classifier calls all
  // of it hardpack. Everything else this file owns inside 60 m is grain and clasts ON one
  // material: the cliff bands ramp in at 9 m and gate themselves off flat ground, the
  // landform hollow/spur split starts at 60 m, the talus fan starts at 45 m, and the duff
  // mat is organic cover which does not form on a dry alpine plateau. There was no material
  // term in the near field at all, and no amount of slope arithmetic can invent one.
  //
  // So this is the process that actually varies gentle open alpine ground, and it is not
  // slope: it is DEFLATION AND ARMOURING. Wind and sheet flow strip the fines off exposed,
  // convex, well-drained ground and leave the coarse fraction behind as a stone pavement;
  // the fines end up in the swales and the hollows, where they pack and hold what grows.
  // A fellfield is patches of gravel lag between compacted silt, and that pair — pale
  // mineral against warm pack — is exactly the contrast BAR asks a rider to read the
  // surface from. Ground.loose sits 0.154 of OKLab L above Ground.hardpack and
  // Ground.rock 0.128 below it: three rungs and two, against a 0.055 budget. Grass, the
  // class the duff mat used to manufacture here, is 0.036 as this file builds it.
  //
  // IT IS THE DUFF MAT'S MIRROR, deliberately, and shares its mechanism rather than
  // inventing one: the same rlfCoverBlend — an art-width feather, a margin broken into
  // fingers by an fBm that fades at its own Nyquist, a real depth in metres whose shoulder
  // catches the sun through rlfBumpH, and a contact band in the ground banked against its
  // toe. So the lag has FORM and CONTACT, not just a different colour, which is what makes
  // it survive overcast and a grazing view. The two covers are placed by opposite biases —
  // organic where it is low, damp, sheltered and already organic; mineral where it is high,
  // exposed, convex and already mineral — so they cannot both claim a pixel and the
  // mountain reads as one process rather than as two decals.
  //
  // NOT ON THE STEEPS. scree already owns everything past 22 degrees; the lag fades out
  // across the same band so the two never double up and a face does not get two pale
  // mineral terms stacked on it.
  // ===========================================================================
  float lagW = 0.0;    // 0..1 how much of this pixel is stone pavement
  float lagH = 0.0;    // metres of armour standing above the packed ground
  float lagOcc = 1.0;  // the silt banked against its edge, for the light
  {
    // Split in two on purpose. lagDist is a function of world position alone; lagFar
    // adds the terms that come off INTERPOLATED VERTEX DATA. Only the first may reach the
    // normal — see the note at lagH below.
    float lagDist = 1.0 - smoothstep(${gl(TerrainLag.far)}, ${gl(TerrainLag.gone)}, vWDist);
    float lagFar = lagDist * notSnowW * uDbgD.w
                 * (1.0 - smoothstep(${gl(slopeAt(TerrainRepose.screeOnsetDeg))},
                                     ${gl(slopeAt(TerrainRepose.screeFullDeg))}, slope));
    if (lagFar > 0.004) {
      RlfCoverParams LP = rlfCoverDefaults();
      // The bias goes on the LEVEL, never on the weight — the duff mat's hardest-won
      // lesson, in its own words: "a cover faded out by a mask has no boundary left, and
      // the boundary is the feature." Every term below is a real driver of where fines
      // survive, and each is worth about half a standard deviation of the cover field
      // (~0.085), so no single one can switch the layer off or leave it running everywhere.
      LP.level = ${gl(TerrainLag.level)}
               + 0.075 * bench                                  // fines settle on a flat
               + 0.070 * clamp(wLoam + wRoot + wGrass, 0.0, 1.0) // and under anything organic
               - 0.055 * clamp(wLoose + wRock, 0.0, 1.0)        // armour begets armour
               - 0.060 * smoothstep(0.34, 0.78, alt)            // less soil to lose up high
               - 0.045 * clamp(formPos, 0.0, 1.0);              // scoured on the ribs
      LP.margin = ${gl(TerrainLag.margin)};
      LP.breakFreq = ${gl(TerrainLag.breakFreq)};
      LP.breakAmp = 0.05;
      LP.depth = ${gl(TerrainLag.depth)};
      LP.occ = 0.50;
      LP.grain = 0.60;
      // Plain wp.xz for the same reason the talus fan uses it: wpw is displaced by the 80 m
      // macro field, and a 16 m patch riding on that displacement follows the warp rather
      // than the hill.
      float lFld = rlfCoverField(wp.xz + vec2(91.0, 407.0),
                                 ${gl(TerrainLag.patchM)}, ${gl(TerrainLag.seed)});
      RlfCover LC = rlfCoverBlend(lFld, wp.xz, L, LP);
      lagW = LC.w * lagFar;

      // A HEIGHT THAT REACHES rlfBumpH MAY NOT DEPEND ON AN INTERPOLATED VERTEX QUANTITY.
      //
      // This cost a capture and it is the sharpest thing this round found. The first
      // version was lagH = LC.h * lagFar, which is the obvious line and the one the duff
      // mat and the talus fan both use — and the frame came back with a hard dark LATTICE
      // across the whole near field, on the clipmap's own triangulation, ~2 m diagonals
      // with dotted lines along them. It is not aliasing and it is not the cover field.
      //
      // rlfBumpH takes SCREEN-SPACE DERIVATIVES of the height. slope is 1 - gN.y off the
      // interpolated normal and the w* weights come from the interpolated vertex colour:
      // both are (near enough) linear inside a triangle, so their gradients are CONSTANT
      // per triangle and JUMP at every edge. Differentiate a function with a kink and you
      // get a step; put that step in a normal and you have drawn the mesh. The albedo can
      // use those same quantities perfectly safely — a C0 function has no visible edge —
      // which is why lagW below still carries the full physical level.
      //
      // So the height is rebuilt from world-space fields only: the raw cover field, the
      // constant part of the level, and the distance ramp. The shoulder therefore sits a
      // little off the albedo boundary wherever the biases are strong; that is a real cost
      // and it is much cheaper than a wireframe. The same fault is latent in coverH and
      // minH — it is visible here first because a pale mineral surface at 6 m is where a
      // normal artefact shows — and it belongs in the report rather than in an edit to two
      // features this round did not otherwise touch.
      float hd = (lFld - ${gl(TerrainLag.level)}) / max(LP.margin, fwidth(lFld) * 1.5);
      lagH = LP.depth * smoothstep(-1.0, 1.0, hd) * lagDist;
      // Same fade policy as the duff mat's rim and for the same reason: fwidth on ground
      // seen at a grazing angle is dominated by the near-vertical screen axis, so an
      // unfaded contact band comes out as swathes rather than as a line.
      lagOcc = mix(1.0, LC.occ, lagFar * rlfBandLod(L, ${gl(TerrainLag.occFadeFreq)}));

      // PARTIAL, at 0.66. The duff mat found this the expensive way and the talus fan found
      // it again forty times larger: a total swap leaves a pixel that is 100% one surface,
      // i.e. the middle stop of one ramp and nothing else, which reads as a sticker. At
      // 0.66 the pavement keeps a third of the packed ground under it and therefore carries
      // its macro variation, its oxide belt and its aspect tint across its own span.
      float f = lagW * ${gl(TerrainLag.take)};
      float m = (wHard + wLoam) * f;
      wHard *= 1.0 - f; wLoam *= 1.0 - f;
      // Mostly gravel, with the odd slab where the pavement IS the bedrock. The rock share
      // is small on purpose: rock is the only cool ground in the palette and a fifth of a
      // near-field patch of it is a landmark, while half of it would be a grey mountain.
      wLoose += m * ${gl(1 - TerrainLag.toRock)};
      wRock += m * ${gl(TerrainLag.toRock)};
    }
  }

  // ===========================================================================
  // COVER PATCHES — duff, moss and ground mats lying ON the ground.
  //
  // THE CARRIED GAP, and the reason this round exists. What used to be here was
  //
  //     float pf = mv * 0.28 + macro.x * 0.72;
  //     float pw = max(fwidth(pf) * 1.15, 0.005);
  //     float coverP = smoothstep(0.47 - pw, 0.47 + pw, pf) * notSnowW * structFar;
  //     float f = coverP * 0.56;   // ...then swap 56% of wLoose/wHard into wGrass/wLoam
  //
  // and every fault the critic named follows from line two. pw is *defined* by the
  // pixel, so the boundary is a ~1 px step at every distance by construction: it can
  // never be a material edge, only a painted one. Nothing about it has a thickness, so
  // nothing beside it or under it knows the mat is there — no rim shoulder for the sun to
  // find, no darkening in the ground banked against it. And because grass and loam carry
  // a fraction of the clast weight that loose and hardpack do, the whole clast field died
  // on the line rather than pebbles poking through the duff. On a 40 m hillside that is a
  // flat khaki decal, and it is exactly what capture w3-terrain-shading-r0 shows.
  //
  // relief.ts's rlfCoverBlend is the entry point built for this, and all three structural
  // faults are fixed by construction rather than by amplitude:
  //
  //   AN ART WIDTH, NOT A PIXEL WIDTH. margin is a feather half-width in *field* units,
  //   floored at 1.5 px so it still antialiases but is never defined by the pixel. The
  //   cover field's gradient is around 0.07/m, so TerrainCover.margin = 0.030 is a
  //   boundary about 45 cm of real ground across — which is what the rim of a duff mat
  //   actually measures, and which stays 45 cm at 5 m and at 80 m.
  //
  //   A BROKEN MARGIN. The threshold is displaced by an fBm *before* it is crossed, so
  //   the boundary is fingers and tufts rather than a contour. The break fades at its own
  //   Nyquist limit, so a distant patch keeps its shape and loses its fringe instead of
  //   aliasing into a dotted line.
  //
  //   THICKNESS AND CONTACT. The mat has a depth in metres. That depth is a height field
  //   (fed to rlfBumpH at the bottom of this block) so its rim is a lit shoulder, and the
  //   band of ground just outside the rim gets an occlusion that goes on the LIGHT.
  //
  // And the fourth, which is ours rather than the library's: A MAT IS THINNER THAN A
  // STONE. gClast already returns each layer's height, in cell units; divided by that
  // layer's frequency it is metres, and any clast standing proud of the duff pushes the
  // cover weight back off itself. That is what turns "the clast field dies at the line"
  // into "pebbles poke through the duff", and it costs one smoothstep because every
  // height in it was computed 700 lines ago.
  //
  // Ramped in from 2 m rather than the structure block's 9 m: the margin *is* the
  // feature, and a margin you only ever see past 26 m is a margin nobody looks at.
  // ===========================================================================
  float coverW = 0.0;     // 0..1 how much of the mineral ground this mat has taken
  float coverH = 0.0;     // metres of duff standing above bare ground, for the normal
  float coverOcc = 1.0;   // rim contact, for the light
  float coverDuff = 0.0;  // coverW, near field only, for the mat's own colour
  {
    float lushP = clamp(0.5 + reg * 0.70 - (alt - 0.38) * 1.6, 0.0, 1.0);
    float coverNear = smoothstep(${gl(TerrainCover.near)}, ${gl(TerrainCover.far)}, vWDist);

    RlfCoverParams CP = rlfCoverDefaults();
    // A duff mat is not a coin flip spread evenly over the mountain. It collects on ground
    // that already holds organic soil and under canopy, and it does not form on live scree
    // or on a bedrock slab — and the first pass, which used a flat 0.50 everywhere, put a
    // weak half-strength wash over the whole world instead of a legible mat anywhere.
    //
    // The bias goes on the LEVEL, not on the weight, and that distinction is the whole
    // lesson of this block: a cover faded out by a mask has no boundary left at all, which
    // is the failure it started from. Moving the threshold instead means the mat is simply
    // absent on scree and nearly continuous in the forest, with its own margin intact at
    // both ends.
    //
    // WAVE 5 R2 — AND IT HAS TO KNOW ABOUT SLOPE, which is the half that was missing and
    // most of why the near field arrived as {hardpack, grass}. This block is the only term
    // in the file that MANUFACTURES grass: it takes 62% of the mineral weights inside its
    // mat and hands them to loam and grass, and with coverNear ramping 2-10 m it was
    // doing that over essentially the whole rideable foreground, on open alpine hillside
    // where there is no canopy to drop needles and no bench to hold them. Measured on the
    // finished frame the near band came back hardpack ~80% / grass ~15% and nothing else —
    // and hardpack against dry grass is 0.036 of OKLab L as this file builds it, against a
    // 0.055 budget. The wash was not a lighting failure; it was this block choosing the one
    // pair on the ladder that cannot separate.
    //
    // Duff lies on ground that will hold it. The slope term takes the level up by a full
    // field standard deviation (~0.085) across the same 22-32 degree band scree uses, so
    // the mat and the scree cannot both claim a face; the altitude term does the same above
    // the timber, where the needles come from nothing.
    CP.level = 0.50
             - 0.09 * clamp(wLoam + wRoot + wGrass * 0.6, 0.0, 1.0)
             + 0.09 * clamp(wLoose + wRock, 0.0, 1.0)
             + 0.085 * smoothstep(${gl(slopeAt(TerrainRepose.screeOnsetDeg))},
                                  ${gl(slopeAt(TerrainRepose.screeFullDeg))}, slope)
             + 0.055 * (1.0 - lushP);
    CP.margin = ${gl(TerrainCover.margin)};
    CP.breakFreq = ${gl(TerrainCover.breakFreq)};
    CP.breakAmp = ${gl(TerrainCover.breakAmp)};
    CP.depth = ${gl(TerrainCover.depthDry)} + ${gl(TerrainCover.depthLush)} * lushP;
    CP.occ = ${gl(TerrainCover.occ)};
    CP.grain = 0.60;

    // The shared field, not a private one. Nine consumers with no particular opinion
    // about where cover goes should produce cover that looks like one game — if
    // vegetation drifts its undergrowth on rlfCoverField too, the plants grow where the
    // duff is instead of on top of bare pack. Sampled in the warped domain so it inherits
    // the same macro displacement everything else here does.
    float coverFld = rlfCoverField(wpw.xz, ${gl(TerrainCover.patchM)}, ${gl(TerrainCover.seed)});
    RlfCover CV = rlfCoverBlend(coverFld, wpw.xz, L, CP);

    // Pebbles through the duff. Heights arrive in cell units; dividing by cells-per-metre
    // puts all three layers in metres so they can be compared against a depth in metres.
    float pokeM = bkD.w / F_BLOCK + stD.w / F_STONE + clD.w / F_CLOD;
    float poke = smoothstep(CP.depth * 0.35, CP.depth * 1.80, pokeM);
    float thin = 1.0 - ${gl(TerrainCover.poke)} * poke;

    float gate = notSnowW * coverNear * uDbgA.z;
    coverW = CV.w * thin * gate;
    coverH = CV.h * thin * gate;
    // The rim band sits just OUTSIDE the margin — it is the ground banked against the mat,
    // not the mat — so it is gated on distance and snow but not on the poke term. See
    // TerrainCover.occFadeFreq for why it also has to die at its own sampling limit.
    coverOcc = mix(1.0, CV.occ, gate * rlfBandLod(L, ${gl(TerrainCover.occFadeFreq)}));

    // PARTIAL, and that is the second half of the fix. At 0.62 the mat interior keeps 38%
    // of whatever mineral ground was under it, so its clasts are still in the recipe
    // rather than being replaced by a surface that has almost none; and thin takes the
    // take down to ~0.11 on a clast crown, which is the pebble poking through.
    float f = coverW * ${gl(TerrainCover.take)};
    float m = (wLoose + wHard) * f;
    wLoose *= 1.0 - f; wHard *= 1.0 - f;
    // WHAT the cover is made of has to change with distance, and getting that backwards is
    // how the previous version came out looking like lawn turf laid on dirt.
    //
    // Near, a forest cover is DUFF: needle litter and dark humus, which is loam — and loam
    // is the one recipe that carries a real litter fleck and a clod layer, so it keeps
    // texture at the range where you can see texture. Far, loam is half hardpack's value
    // and a patch of it reads as a hole punched in the hillside, so out there the cover
    // has to be grass, which separates from hardpack by *hue* at a similar value and
    // therefore survives haze. Same field, same mat, different material by range.
    float duffNear = 1.0 - smoothstep(8.0, 40.0, vWDist);
    float toLoam = clamp(0.14 + 0.22 * lushP + 0.34 * duffNear, 0.0, 0.78);
    wLoam += m * toLoam;
    wGrass += m * (1.0 - toLoam);
    coverDuff = coverW * duffNear;
    // ...and the complement strips the cover back off, so the boundary is a step in both
    // directions instead of a one-sided smear. Driven by CV.w rather than by coverW: bare
    // pack does not appear on top of a stone just because the stone is proud.
    float g = (1.0 - CV.w) * 0.34 * gate;
    float m2 = (wLoam + wGrass + wRoot) * g;
    wLoam *= 1.0 - g; wGrass *= 1.0 - g; wRoot *= 1.0 - g;
    wHard += m2 * 0.68;
    wLoose += m2 * 0.32;
  }

  // --- per-surface material recipes -------------------------------------------
  // Albedos are in the physical range for their material (dirt 0.10-0.25, granite
  // 0.30-0.35, dry grass 0.15-0.25, snow 0.80+) so the grade has something to work with.
  // Every soil recipe is built in the order the eye reads dirt in:
  //   body ramp (hue + value from the noise stack)
  //   -> the clasts lying in it, each its own colour
  //   -> litter and mineral flecks
  //   -> cavity and contact darkening (added once, after the blend).

  // ---------------------------------------------------------------------------
  // THE PALETTE. The single number that matters in each recipe below is the *middle*
  // stop of its ramp, because that is the colour the surface converges to once every
  // detail layer has faded — i.e. it is the entire mid distance. In R3 those middles
  // landed at luma 0.14 (hardpack), 0.14 (rock), 0.18 (loose), 0.07 (loam): rock and
  // hardpack were 4% apart, which is exactly why the mountain read as one brown wash
  // past 15 m. They are now spread over roughly 4:1 and given distinct hue families —
  //   loose  0.26  warm pale grey-tan     rock   0.15  cold dark grey
  //   hardpack 0.18 orange clay           grass  0.13  green (dry) / 0.07 (lush)
  //   root   0.09  warm dark              loam   0.07  near-neutral dark
  //
  // ROUND 3 widened it again, from 3.5:1 to 3.7:1, but the number that mattered was not
  // the span — it was that the two surfaces which between them cover the *entire far
  // alpine hillside*, rock and loose, sat 1.23:1 apart and so converged to one wash at
  // exactly the distance this piece keeps being sent back for. They are now 1.75:1 with
  // opposite hue temperature, which is a value structure legible before any detail
  // resolves, in fog, and in overcast.
  //
  // Talus lighter than the cliff it fell from is deliberate and it is the composition of
  // every mountain photograph ever taken: dark bedrock band, pale fan under it. The
  // first pass had rock as the brightest surface, which through half a kilometre of haze
  // came out as chalk and put the cliff bands and the snowline in the same value.
  // ---------------------------------------------------------------------------

  // Hardpack: buffed, compacted tread. The one saturated note in the palette — the
  // exposed-clay accent — pushed hard away from loam (near-neutral) and loose (pale
  // grey-tan) in hue as well as value, with clasts worn flush into it.
  float hpT = clamp(0.5 + mvC * 0.26 + fvC * 0.18 + mesC * 0.04 + micC * 0.12, 0.0, 1.0);
  vec3 cHard = gRamp3(${P.hard[0]}, ${P.hard[1]}, ${P.hard[2]}, hpT);
  cHard *= 1.0 - crack * 0.30;
  // WAVE 4 ROUND 3 — THE GRIT ON A CLAY LINE IS CLAY-COLOURED GRIT.
  //
  // These four tints and the mineral fleck below were NEUTRAL GREYS — (0.214, 0.196,
  // 0.172) and friends, HSV saturation 0.20 — mixed into the one saturated member of the
  // palette at 72 / 52 / 34 / 30 per cent. Hardpack's own ramp is 0.68 saturated; after
  // the grain mix the surface converged to about 0.33, so "the one saturated note in the
  // palette" shipped as one of the least saturated surfaces in the frame, and the carried
  // gap is exactly that: the ridden line separating from the ground DOWNWARD IN CHROMA,
  // at the same value, which is the signature of tarmac rather than of compacted clay.
  //
  // The physical reading is the fix. A dry hardpack line is not gravel lying on clay, it
  // is clay-dusted grit: every stone small enough to have been rolled on is filmed with
  // the same fines that make the surface orange, and only the big members keep their own
  // mineral face. So the tints now run warm-and-dusty at the grain end (sat 0.46) toward
  // mineral at the block end (sat 0.39), and each one holds its OLD LUMA to three decimal
  // places — this is a pure chroma change, so the value structure the far field is read
  // from, and every calibration in the palette block above, is untouched.
  //
  // gStoneTint still spreads each object between a cool and a warm pole, so the grit is a
  // population with cold members in it, not a single orange. What it no longer does is
  // pull the whole surface toward neutral.
  //
  // WAVE 5 — THE FOUR TINTS ARE NOW THE SURFACE'S OWN LADDER ENTRY. Round 3's argument was
  // right and its numbers were still hand-picked greys with a warm bias bolted on; the
  // four constants below are Ground.hardpack moved down one rung's worth in OKLab L and
  // nothing else, so the grit population is the SAME MATERIAL as the surface, spread in
  // value by gStoneTint. A clast layer can no longer pull hardpack toward any other rung,
  // which is half of why the near field converged.
  float gritW = uDbgD.x;
  vec3 gritCol = mix(${palAt(Ground.hardpack, -0.055, 0.42)}, ${P.gritHard}, gritW);
  cHard = mix(cHard, gStoneTint(gritCol, grId.xy) * (0.86 + 0.30 * grTop), grMask * 0.72);
  cHard = mix(cHard, gStoneTint(gritCol, clId.xy) * (0.86 + 0.30 * clTop), clMask * 0.52);
  cHard = mix(cHard, gStoneTint(gritCol, stId.xy) * (0.84 + 0.34 * stTop), stMask * 0.34);
  cHard = mix(cHard, gStoneTint(gritCol, bkId.xy) * (0.84 + 0.34 * bkTop), bkMask * 0.30);
  cHard = mix(cHard, cHard * 0.36 * litLit, fLit * 0.42);
  // The mineral fleck. Accent.feldspar is the authored glint — "value only, effectively
  // no hue" — and on a ridden line it is filmed with the same fines that make the surface
  // orange, so the shipped end of the A/B is that entry mixed a third of the way to
  // hardpack. It was the second largest desaturator on this surface at 38% coverage.
  cHard = mix(cHard, mix(${P.feldspar}, ${P.feldsparDusted}, gritW), fMin * 0.38);
  // Compacted, not polished. At 0.42 a hardpack clast flank picks up a specular ring
  // off a low sun and the whole clast field reads as wet plastic shells.
  float rHard = 0.60 + fv * 0.14 + crack * 0.12 - bench * 0.07 + micC * 0.09;

  // Loam: dark, damp, organic. Forest floor, so the litter layer does most of the work —
  // needles and bark over near-black soil — with clods of turned earth and the odd stone.
  float lmT = clamp(0.5 + mvC * 0.34 + fvC * 0.20 + mesC * 0.04 + micC * 0.12, 0.0, 1.0);
  vec3 cLoam = gRamp3(${P.loam[0]}, ${P.loam[1]}, ${P.loam[2]}, lmT);
  cLoam = mix(cLoam, gStoneTint(${P.clodLoam}, clId.xy) * (0.80 + 0.38 * clTop),
              clMask * 0.66);                                       // clods of turned earth
  cLoam = mix(cLoam, ${P.duff} * litLit, fLit * 0.52);                   // dry needles
  cLoam = mix(cLoam, ${P.moss}, fMin * 0.36);                            // moss fleck
  cLoam = mix(cLoam, gStoneTint(${P.stoneLoam}, stId.xy) * (0.80 + 0.38 * stTop),
              stMask * 0.42);
  cLoam = mix(cLoam, gStoneTint(${P.stoneLoam}, bkId.xy) * (0.78 + 0.40 * bkTop),
              bkMask * 0.38);                                       // mossy buried rock
  float rLoam = 0.86 + fv * 0.10 - fMin * 0.10;

  // Loose: scree over a dust matrix. The stones catch light and sit proud; the matrix
  // between them is fine, darker, and redder. The contrast between the two is the whole
  // reason this reads as loose rather than as sand — so the clasts get the widest
  // per-object spread and the heaviest coverage of any surface here.
  float lsT = clamp(0.48 + mvC * 0.28 + fvC * 0.19 + mesC * 0.04 + micC * 0.14, 0.0, 1.0);
  // Top stop pulled from 0.436 to 0.348. A sunlit talus face measured a peak of 246/255
  // with its 90th percentile at 217, which is chalk, and chalk on the pale member of a
  // cliff band is how the whole feature ends up looking applied. Real dry talus is around
  // 0.30; the mid stop is untouched so the far-field value separation documented in the
  // palette block is unchanged, and only the sunlit top of the ramp comes back down.
  vec3 cLoose = gRamp3(${P.loose[0]}, ${P.loose[1]}, ${P.loose[2]}, lsT);
  cLoose = mix(cLoose, gStoneTint(${P.stoneLoose}, grId.xy) * (0.78 + 0.44 * grTop),
               grMask * 0.90);
  cLoose = mix(cLoose, gStoneTint(${P.stoneLoose}, clId.xy) * (0.78 + 0.44 * clTop),
               clMask * 0.88);
  cLoose = mix(cLoose, gStoneTint(${P.stoneLoose}, stId.xy) * (0.76 + 0.46 * stTop),
               stMask * 0.88);
  cLoose = mix(cLoose, gStoneTint(${P.stoneLoose}, bkId.xy) * (0.74 + 0.48 * bkTop),
               bkMask * 0.80);
  cLoose = mix(cLoose, cLoose * 0.40 * litLit, fLit * 0.34);
  cLoose *= 1.0 - crack * 0.20;
  float rLoose = 0.88 - grMask * 0.14 - stMask * 0.22 + micC * 0.08;

  // Rock: stratified, cracked, iron-stained on the weathered bands, with pale lichen
  // where it has been standing long enough, and loose blocks spalled off the face.
  float rockShade = clamp(0.5 + (strat - 0.5) * 1.5 + fvC * 0.22 + mesC * 0.04 + micC * 0.11,
                          0.0, 1.0);
  // Cool mid grey. R3 had this ramp topping out below 0.15, which put bare rock at the
  // same value as hardpack dirt and cost the mountain its most useful landmark; the
  // first wave-2 pass overcorrected it to 0.29 and got chalk. 0.20 and cool is where a
  // cliff band reads as bedrock: darker than the talus below it, colder than the soil
  // beside it, and with enough range in the ramp that a sunlit face still opens up.
  // ROUND 3: mid 0.199 -> 0.148 luma, and colder. The wave-2 note under the palette block
  // claims "darker than the talus below it" and the numbers did not deliver it: rock at
  // 0.199 against loose at 0.245 is 1.23:1, which is nothing through half a kilometre of
  // haze, and the far hillside came out as one grey-lavender wash with the cliff bands
  // invisible inside it. At 0.148 against loose's 0.259 the ratio is 1.75:1 — a dark
  // bedrock band over a pale fan, which is the composition of every mountain photograph
  // and the one that survives distance. The top stop comes down with it so a sunlit face
  // opens up to stone rather than to chalk.
  vec3 cRock = gRamp3(${P.rock[0]}, ${P.rock[1]}, ${P.rock[2]}, rockShade);
  // Regional iron staining. Partly on the strata, but with a floor that does not depend
  // on them, or a cliff would revert to neutral concrete the moment the bedding planes
  // drop below the sampling limit. Accent.oxide is one of the palette's two sanctioned
  // accents and its area budget is PaletteLimits.accentAreaFraction — 6% of ground
  // pixels. The 0.46 ceiling below is what keeps this inside it on a banded face.
  cRock = mix(cRock, ${P.oxide},
              (0.34 + 0.66 * smoothstep(0.52, 0.94, strat)) * 0.46
              * clamp(0.5 + reg * 0.75 + mac * 0.25, 0.0, 1.0));
  cRock = mix(cRock, gStoneTint(${P.blockRock}, stId.xy) * (0.80 + 0.40 * stTop),
              stMask * 0.36);
  cRock = mix(cRock, gStoneTint(${P.blockRock}, bkId.xy) * (0.78 + 0.42 * bkTop),
              bkMask * 0.52);                                       // spalled blocks
  // Lichen colonises in patches the size of a dinner plate upward, so it has to be
  // driven by the metre-scale field. Hanging it off the pixel-scale grain turns a rock
  // face into camouflage.
  float lichen = smoothstep(0.60, 0.92, fv * 0.78 + mesS * 0.26 + micS * 0.14)
               * (1.0 - steepRock * 0.5) * clamp(0.5 + mac * 0.9, 0.0, 1.0);
  cRock = mix(cRock, ${P.lichen}, lichen * 0.42);
  cRock = mix(cRock, ${P.feldspar}, fMin * 0.48);   // feldspar glint
  cRock *= 1.0 - joint * 0.58 - crack * 0.20;
  float rRock = mix(0.34, 0.90, clamp(fv * 0.7 + joint * 0.6 + lichen * 0.5, 0.0, 1.0));

  // Root: dark soil crossed by pale, polished, slick strands, with the knuckles where
  // a root arches out of the ground and back into it. The knuckles are the clod layer
  // masked to the strand, so they only appear where a root actually runs.
  // THE PLAID. This network is what the wave-3 critique was looking at when it wrote "a
  // plaid of pure-albedo litter strokes on an otherwise flat dark plane", and the reading
  // is exact: a level set of a 1.8 m fBm, drawn as a 2.6:1 albedo step, with a comment
  // above it claiming the strands are 8 cm proud and a relief term that delivered
  // 0.09 * a masked gradient — call it a millimetre. Pale hard-edged lines at a fixed
  // spacing over a flat surface is a hatch, and a hatch is a drawing.
  //
  // Three changes, and only the first is really the fix: the strand is now a HEIGHT in
  // metres and goes through rlfBumpH with everything else, so a root arching out of the
  // ground is lit along its crown and shaded down its far side by whatever light there
  // is. Then it gets the contact its own comment always promised — soil banked into the
  // joint where the strand enters the ground, on the irradiance, so it survives overcast.
  // And the albedo step comes down by a quarter, because with the form doing the work the
  // paint no longer has to.
  float rootMask = 0.0;
  float rootRim = 0.0;
  if (wRoot > 0.004 && lodFine > 0.004) {
    vec3 rn = gFbmD(wpw.xz * 0.55 + 13.1, 2, 0.5);
    float u = rn.x * 2.0 - 1.0;
    float a = 1.0 - abs(u);
    rootMask = pow(a, 7.0) * lodFine;
    // The flanks of the strand, where it meets the soil: peaks at the half-height of the
    // profile and is zero both on the crown and out on open ground.
    rootRim = clamp(rootMask * (1.0 - rootMask) * 4.0, 0.0, 1.0);
  }
  vec3 cRoot = gRamp3(${P.root[0]}, ${P.root[1]}, ${P.root[2]},
                      clamp(0.5 + mvC * 0.34 + mesC * 0.04 + micC * 0.12, 0.0, 1.0));
  cRoot = mix(cRoot, ${P.duff} * litLit, fLit * 0.48);
  cRoot = mix(cRoot, gStoneTint(${P.clodRoot}, clId.xy) * (0.80 + 0.36 * clTop),
              clMask * 0.46);
  // The strand and its knuckles are wood, so they are Flora.deadwood — the authored
  // "palest warm-neutral in the forest so deadfall reads as structure on a dark floor",
  // which is exactly the job a root crown does on loam.
  cRoot = mix(cRoot, ${P.rootStrand}, rootMask);
  cRoot = mix(cRoot, ${P.rootKnuckle} * (0.76 + 0.42 * clTop),
              clMask * rootMask * 0.72);                            // root knuckles
  float rRoot = mix(0.90, 0.26, rootMask);

  // Grass: lush in the shaded, damp belts; bleached and dry on the exposed ones. Clumped
  // at the locked scale with dead thatch between the clumps, so it is not a green sheet,
  // and a few stones pushing through it.
  float dry = clamp(0.5 + mac * 0.70 + reg * 0.55 + (alt - 0.4) * 0.8, 0.0, 1.0);
  float gShade = clamp(0.5 + mvC * 0.40 + fvC * 0.24 + mesC * 0.05 + micC * 0.11, 0.0, 1.0);
  vec3 cGrass = mix(gRamp3(${P.grassLush[0]}, ${P.grassLush[1]}, ${P.grassLush[2]}, gShade),
                    gRamp3(${P.grassDryLo}, ${P.grassDryMid}, ${P.grassDryHi}, gShade), dry);
  cGrass = mix(cGrass, ${P.thatch} * litLit, fLit * 0.52);                 // dead thatch
  cGrass = mix(cGrass, gStoneTint(${P.stoneGrass}, stId.xy) * (0.82 + 0.34 * stTop),
               stMask * 0.24);
  cGrass = mix(cGrass, gStoneTint(${P.stoneGrass}, bkId.xy) * (0.80 + 0.36 * bkTop),
               bkMask * 0.34);
  float rGrass = 0.78 + tuft * 0.16;

  // Snow: wind-packed, blue in the hollows, glossy where it has crusted. Sastrugi from
  // the locked layer so it is not a flat white card at riding distance.
  float pack = clamp(0.5 + mvC * 0.44 + fvC * 0.28 + mesC * 0.10 + micC * 0.07, 0.0, 1.0);
  // Ground.snow is "faintly cool in albedo and no more — snow's blue shadows are a
  // lighting result, not a paint job, and painting them in doubles the effect". The old
  // ramp bottomed out at a saturated periwinkle (0.470, 0.548, 0.700) and that is the
  // paint job the palette entry's note names. Span widened 2.2x here and only here: snow
  // is the one surface with a 0.184 gap to its neighbour, so it can afford it.
  vec3 cSnow = gRamp3(${P.snow[0]}, ${P.snow[1]}, ${P.snow[2]}, pack);
  float rSnow = mix(0.18, 0.55, pack);

  // --- height blend ------------------------------------------------------------
  // Each surface competes with a procedural height; the tallest wins locally, so
  // boundaries interlock instead of cross-fading.
  float hHard = 0.34 + mv * 0.22 + fv * 0.10;
  float hLoam = 0.28 + mv * 0.42 + fv * 0.18;
  float hLoose = 0.40 + stD.x * 0.30 + clD.x * 0.20 + bkD.x * 0.26 + mv * 0.16;
  float hRock = 0.62 + strat * 0.48 + fv * 0.14;
  float hRoot = 0.40 + rootMask * 0.55;
  float hGrass = 0.36 + tuft * 0.50;
  float hSnow = 0.52 + mv * 0.55;

  float vHard = hHard + wHard * 2.0;
  float vLoam = hLoam + wLoam * 2.0;
  float vLoose = hLoose + wLoose * 2.0;
  float vRock = hRock + wRock * 2.0;
  float vRoot = hRoot + wRoot * 2.0;
  float vGrass = hGrass + wGrass * 2.0;
  float vSnow = hSnow + wSnow * 2.0;

  float top = max(max(max(vHard, vLoam), max(vLoose, vRock)), max(max(vRoot, vGrass), vSnow));
  float cut = top - 0.30;
  float bHard = max(vHard - cut, 0.0);
  float bLoam = max(vLoam - cut, 0.0);
  float bLoose = max(vLoose - cut, 0.0);
  float bRock = max(vRock - cut, 0.0);
  float bRoot = max(vRoot - cut, 0.0);
  float bGrass = max(vGrass - cut, 0.0);
  float bSnow = max(vSnow - cut, 0.0);
  float inv = 1.0 / max(bHard + bLoam + bLoose + bRock + bRoot + bGrass + bSnow, 1e-4);
  bHard *= inv; bLoam *= inv; bLoose *= inv; bRock *= inv;
  bRoot *= inv; bGrass *= inv; bSnow *= inv;

  vec3 albedo = cHard * bHard + cLoam * bLoam + cLoose * bLoose + cRock * bRock
              + cRoot * bRoot + cGrass * bGrass + cSnow * bSnow;
  float rough = rHard * bHard + rLoam * bLoam + rLoose * bLoose + rRock * bRock
              + rRoot * bRoot + rGrass * bGrass + rSnow * bSnow;

  // --- the cover mat's own material, near field ---------------------------------
  // The weight transfer above gives a patch its FAR reading, and it has to: grass
  // separates from hardpack by hue at a similar value and so survives half a kilometre of
  // haze, while loam is half hardpack's value and a patch of it at distance reads as a
  // hole punched in the hillside. Neither is what a cover looks like from the saddle. A
  // forest floor is needle litter, bark and moss lying over dark humus — warmer and
  // lighter than the humus, much darker and browner than the clay beside it — and without
  // its own material the mat reads as a patch of shade rather than as a thing lying on the
  // ground. Which is the failure this whole block exists to fix, arrived at from the
  // opposite direction.
  //
  // MIXED TOWARD A TARGET THAT KEEPS THE GRAIN. The pixel-scale grain and the litter fleck
  // are folded into the target colour, so the mat carries as much structure as the dirt
  // next to it. A flat target here is exactly how this became a khaki decal the first
  // time, and it would be one again at any strength.
  //
  // It inherits coverW, so the litter thins off the crown of every proud stone the same
  // way the weight transfer does — one interruption, applied to everything the mat owns.
  // Flora.duff is the authored forest-floor entry — "sits with loam by value and
  // separates from it by being drier and slightly warmer". Lush is that entry blended
  // toward Flora.understory, dry is it lifted and warmed. Both stay inside the flora
  // family, so a duff mat can never read as a hole punched in the hillside.
  vec3 duffCol = mix(${P.duffLush}, ${P.duffDry}, dry);
  duffCol *= 0.70 + 0.64 * micS + 0.34 * fLit * litLit;
  albedo = mix(albedo, duffCol, coverDuff * 0.55);
  rough = mix(rough, 0.96, coverDuff * 0.40);

  // How much of each clast layer's *relief* this surface is entitled to. Hardpack has
  // its stones worn flush; scree has them sitting on top; snow has none at all.
  //
  // HARDPACK'S SHARE DROPPED THIS ROUND, and it is the near half of the carried gap.
  // Hardpack is the built tread. TerrainGround.lumpHard is already 0.022 against loose's
  // 0.105 on exactly that argument, and the comment beside it says in as many words that
  // the compacted line must not acquire a case of the lumps — but these four numbers, the
  // ones that decide how many boulders lie on it, were 0.42/0.34/0.40/0.72 and had never
  // been revisited. See TerrainClast.tread*: the 8-95 cm members go to a seventh and a
  // fifth of what they were, grains keep everything, because grit is what a compacted
  // surface is made of and boulders are not.
  float bkRel = ${gl(TerrainClast.treadBlock)} * bHard + 0.70 * bLoam + 1.00 * bLoose + 0.85 * bRock
              + 0.50 * bRoot + 0.60 * bGrass;
  float stRel = ${gl(TerrainClast.treadStone)} * bHard + 0.62 * bLoam + 1.00 * bLoose + 0.52 * bRock
              + 0.55 * bRoot + 0.34 * bGrass;
  float clRel = ${gl(TerrainClast.treadClod)} * bHard + 0.78 * bLoam + 0.92 * bLoose + 0.30 * bRock
              + 0.80 * bRoot + 0.34 * bGrass;
  float grRel = ${gl(TerrainClast.treadGrain)} * bHard + 0.58 * bLoam + 0.95 * bLoose + 0.34 * bRock
              + 0.40 * bRoot + 0.22 * bGrass;
  // THE INVARIANT, in one line. Every cue an object owns is multiplied by its own layer's
  // entitlement from here on: the crease ring, the cast shadow, the crown, the body and
  // the dome normal all read relV, and none of them can be entitled differently from
  // another again. The three vec4s built at the top of the shade block exist so that this
  // dot product is possible at all — a scalar sum over four layers, which is what was
  // there before, cannot be entitled per surface after the fact.
  vec4 relV = vec4(bkRel, stRel, clRel, grRel);
  // ...AND THE ONE CUE THAT MUST NOT BE ENTITLED LINEARLY: THE RING.
  //
  // Round 2 made every cue read the same relV, which fixed the arithmetic — a ring can no
  // longer be at 100% behind a dome at 14%. It did not fix the READ, and the carried gap
  // says so in as many words: hollow rings are still shipping on the pale hardpack beside
  // the tread. The reason is geometric, not arithmetic. The crease is a RING 70-118% of
  // the way out from the centre; the body and the crown are the DISC. Entitle both at e
  // and their integrals stay matched, but the ring's amplitude is concentrated into a
  // two-pixel annulus while the disc's is spread over forty times the area, so at e = 0.16
  // the ring is still a visible dark outline and the solid inside it is 1% of a value
  // step. Matched means does not mean matched legibility.
  //
  // The physics says the same thing and says it more usefully: a contact crease is soil
  // BANKED AGAINST A PROUD BASE. A clast worn flush into a compacted line — which is what
  // an entitlement of 0.14 means — has no base standing out of the surface for anything to
  // bank against, so it should have no crease at all, while a grain (0.72 on that same
  // surface) still does. That is a super-linear relationship, not a proportional one.
  //
  // So the crease and the body — the mean-preserving PAIR — are entitled through
  // e * (RIM_FLUSH + (1 - RIM_FLUSH) * e): identity at e = 1, so every fully entitled
  // surface (scree, loose, the whole near field this round is not allowed to disturb) is
  // bit-for-bit unchanged, and 0.06 / 0.07 / 0.15 for hardpack's block / stone / clod
  // where it was 0.14 / 0.16 / 0.28. The pair stays balanced because BOTH halves take the
  // same factor, so the surface's mean does not move — the outline goes and nothing gets
  // darker or brighter. Grains keep 0.52 of theirs, because grit on a buffed line really
  // does sit in a little socket, and grit is what a compacted surface is made of.
  const float RIM_FLUSH = 0.35;
  vec4 relRim = relV * mix(vec4(1.0), RIM_FLUSH + (1.0 - RIM_FLUSH) * relV, uDbgD.y);
  // The remaining relief budget: the metre-scale fBm layers, which are landform, not
  // objects, and must stay restrained or a smooth bench turns into pond ripples.
  float bumpAmt = 0.20 * bHard + 0.55 * bLoam + 0.70 * bLoose + 0.90 * bRock
                + 0.55 * bRoot + 0.48 * bGrass + 0.30 * bSnow;
  float notSnow = 1.0 - bSnow;

  // --- THE MISSING BAND: 1-3 m ground form ---------------------------------------
  // Between the biggest clast this shader draws (a 95 cm knob) and the mesoform (6 m,
  // and deliberately held out of everything inside 10 m) there was NO RELIEF AT ALL.
  // That gap lands exactly on the ground a rider looks at from the saddle, and it is
  // most of why the shaded forest floor reads as a plane with marks on it: there was
  // nothing at the scale of a stride for either the sun or the sky to find.
  //
  // A forest floor at that scale is needle drift, buried branch, root swell and the
  // hollows between them; scree is drifts and runnels; a compacted tread is smooth,
  // which is why hardpack gets a fifth of what duff gets and the built line does not
  // acquire a case of the lumps. One 2-octave field at 1.6 m and 80 cm, faded on the
  // library's own Nyquist policy so it dies where 1.6 m stops resolving (~70 m) and the
  // mesoform has taken over anyway.
  //
  // This is NOT another octave of detail noise (docs/BAR.md's named failure mode). It is
  // 8 cm of form at a 1.6 m wavelength, below the clipmap's own 0.5 m tessellation in
  // frequency terms and far above it in wavelength — a shape, sized in metres, that
  // moves when the light moves and disappears when the light does.
  float lumpAmp = ${gl(TerrainGround.lumpHard)} * bHard + ${gl(TerrainGround.lumpLoam)} * bLoam
                + ${gl(TerrainGround.lumpLoose)} * bLoose + ${gl(TerrainGround.lumpRock)} * bRock
                + ${gl(TerrainGround.lumpRoot)} * bRoot + ${gl(TerrainGround.lumpGrass)} * bGrass
                + ${gl(TerrainGround.lumpSnow)} * bSnow;
  // Nyquist, far fade, motion and the master scale — but NOT the graze clamp, for the
  // reason the mesoform three hundred lines below already carries the same exemption:
  // the clamp exists because the long axis of a pixel footprint is undersampled at a
  // grazing angle, which is true of anything at the pixel scale and false of a 1.6 m
  // form at 5 m, which is two hundred pixels across. Measured with rlfReliefAmp in
  // (i.e. with graze), this whole layer ran at its 0.22 floor on exactly the ground a
  // rider looks at from the saddle — an 8 cm drift delivered as 1.8 cm — and the frame
  // did not move at all.
  // WAVE 5 R2: through gReliefAmpS, not rlfBandLod(L, ...). This layer's amplitude is
  // per-surface (0.022 m on hardpack, 0.105 m on loose) and it feeds bumpH, so the
  // triangle-edge staircase in L.foot was being multiplied by the tallest relief in the
  // near field — see the block at gBandLodS. The two are the same curve; only the
  // footprint's provenance changes.
  float lumpFade = gReliefAmpS(${gl(TerrainGround.lumpFreq * 2.0)});
  float groundLump = 0.0;   // metres, signed, zero mean
  if (lumpAmp * lumpFade > 0.002) {
    groundLump = rlfReliefField(wpw.xz + vec2(83.1, 27.9), ${gl(TerrainGround.lumpFreq)},
                                lumpAmp, 2).x * lumpFade;
  }

  // --- object shading: crown, crease, cast shadow -------------------------------
  // The three cues that a normal map alone cannot deliver. All three are applied after
  // the material blend so they read as one lighting event on one object, not as seven
  // surfaces each doing their own thing.
  float clastOn = clamp(dot(vec4(bkMask, stMask, clMask, grMask) * relV, vec4(1.0)),
                        0.0, 1.0);
  // THE FOUR CUES, ENTITLED TOGETHER. One dot product each, against the same relV, so
  // there is no longer any arithmetic path by which a ring can survive on a surface that
  // is not entitled to the solid inside it. That path is the carried gap.
  float crease = clamp(dot(clastRim * relRim, vec4(1.0)), 0.0, 1.0);
  float contact = clamp(dot(clastSh * relV, vec4(1.0)), 0.0, 1.0);
  float crown = clamp(dot(clastCrn * relV, vec4(1.0)), 0.0, 1.0);
  // The body coefficients are the crease coefficients times 0.58 = 0.463/0.80, the ratio
  // of the two profiles' integrals over the disc. That is what makes the pair mean-
  // preserving per layer rather than only on average across the mountain, so a hillside
  // that happens to be all scree cannot come out darker than one that is all hardpack.
  // relRim, not relV: this is the other half of the crease's mean-preserving pair, so it
  // has to take exactly the same entitlement or removing an outline from the tread would
  // leave an unbalanced brightening behind it.
  float body = clamp(dot(clastBody * relRim, vec4(0.46, 0.42, 0.29, 0.26)), 0.0, 1.0);
  // Crease: soil banked into the joint between clast and ground. Light-independent, so
  // it is what holds an object together under flat overcast. Kept modest — pushed hard
  // it turns into a drawn outline around every stone, which reads as a cartoon.
  //
  // SPLIT, and this is the pattern for every occlusion term in the block below. A crease
  // genuinely is darker in albedo — it is where the fines and the damp collect — but most
  // of why it is dark is that it cannot see the sky, and a multiply on diffuseColor
  // expresses none of that: it is the same number in full sun, in shadow and under
  // overcast, which is the definition of a paint mark. Roughly a third stays here and the
  // rest goes to gRlfOcc, which relief.ts's AO patch puts on indirectDiffuse in full and
  // on directDiffuse at 65%. The occ coefficient is ~1.4x the albedo one it replaces
  // precisely because of that 0.65, so under a clear sky the total is within a couple of
  // per cent of where R3 measured it and the near field does not move.
  albedo *= 1.0 - crease * 0.10 * notSnow;
  gRlfOcc *= 1.0 - crease * 0.23 * notSnow;
  // Cast shadow: hard, short, and only where the sun can actually reach. 0.52 rather than
  // R3's 0.42 for the same reason as the tint floor above — against a brighter matrix the
  // old depth turned every knob's shadow into a black dot at mid distance.
  albedo *= mix(1.0, 0.52, contact * notSnow);
  // Crown: the top of a dome sees the whole sky. Near, this is small on purpose — the
  // dome normal is already doing the direct-light half and doubling it reads as plastic.
  // Far, the crown term itself carries the extra block weight, so it lands as a legibly
  // brighter cap on the handful of boulders still out there.
  //
  // WAVE 3 ROUND 2: moved to the ambient channel — but only in the near field, and the
  // distance split is not a hedge, it is the whole argument.
  //
  // NEAR, "the top of a dome sees the whole sky" is a statement about irradiance being
  // written as a statement about paint: the same number in sun, in shade and at midnight,
  // which is why the interior of a shaded clast had nothing in it. There it belongs on
  // gTerrAmb, where it fills the ring the crease draws and dies with the light.
  //
  // FAR, that is wrong, and the first pass of this round proved it in one capture. At six
  // pixels a knob cannot show a shaded interior at all; what it shows is one light/dark
  // pair — a cap brighter than the matrix against its own cast shadow — and that pair was
  // calibrated in wave 2 against the far hillside coming out pocked with dark ellipses.
  // Ambient is a fifth of the light in sun, so moving the cap there cut it to a fifth,
  // left the cast shadow at full strength, and the ellipses came straight back. So the
  // albedo lift returns to its measured value exactly where it was measured, and the two
  // halves cross over on blockFar — the same 26-72 m ramp the rest of the layer uses.
  //
  // WAVE 4 ROUND 2 — THE CROWN NOW SHARES THE JOB WITH THE BODY, at the same total.
  // crown is the middle 37% of the object's radius; the crease is a ring at 70-118% of
  // it; and between them lies most of the object's visible area with nothing on it. That
  // annulus is the "featureless interior". body is the whole disc, weighted to the
  // flank, on the same channel, sized off the two profiles' integrals — see
  // TerrainAmbient.crown and .body for the arithmetic, including the check that the pair
  // moves no mean.
  albedo *= 1.0 + crown * mix(0.06, 0.18, blockFar) * notSnow;
  gTerrAmb *= 1.0 + (crown * mix(${gl(TerrainAmbient.crown)}, 0.30, blockFar)
                   + body * ${gl(TerrainAmbient.body)}) * notSnow;

  // --- cavity ------------------------------------------------------------------
  // Occlusion from the micro relief. It survives filtering (it loses contrast, it does
  // not shimmer), it does not depend on the light direction, and it is most of what makes
  // granular ground look granular in overcast light where a bump map gives you nothing at
  // all. Same split as the crease above: a little on albedo because a pit really does
  // hold darker fines, the rest on the irradiance because that is what a pit does.
  float soilFrac = bHard + bLoam + bLoose + bRoot;
  float cavity = clamp((1.0 - micS) * 0.34 + (1.0 - mesS) * 0.06 + crease * 0.45, 0.0, 1.0);
  albedo *= 1.0 - cavity * 0.06 * notSnow;
  gRlfOcc *= 1.0 - cavity * 0.15 * notSnow;

  // The root's own contact. A strand arching out of the ground has soil banked into the
  // joint on both sides of it, and that joint is the cue that says the root is IN the
  // ground rather than lying on a photograph of it. Light-independent, so it is what
  // holds the strand together in the overcast frame this round was sent back over.
  gRlfOcc *= 1.0 - rootRim * 0.20 * bRoot * notSnow;

  // --- the cover mat's own contact ---------------------------------------------
  // The ground banked against the rim of a duff mat, from rlfCoverBlend. On the light,
  // never on the albedo: this is the one cue that says the mat has a thickness, and if it
  // vanishes the moment the sun goes behind a cloud then it was never contact, it was a
  // dark outline drawn round a decal.
  gRlfOcc *= coverOcc;
  // ...and the ground banked against the toe of a talus fan, which is the same statement
  // at forty times the size. This is the term that stops a far-field material region being
  // a stain: a fan with a dark line under its snout is a thing lying on a hillside.
  gRlfOcc *= minOcc;
  // ...and the silt banked against the edge of a stone pavement, which is the same
  // statement at the scale a rider is actually looking at. Without it the lag is a pale
  // blotch; with it, it is 8 cm of armour lying on packed ground and you can see which is
  // on top of which.
  gRlfOcc *= lagOcc;

  // --- landform structure: the value steps the mid distance is read from -------
  // The shadow under a cliff lip is the most legible thing on a mountainside at 200 m:
  // hard-edged, and dark against the brightest surface in the palette. It goes on the
  // albedo rather than on the light so it survives flat overcast, which is precisely the
  // condition under which the direct term that ought to produce it does not exist.
  // Cool shadow, warm key — the art direction's own words, applied to the one place on
  // the mountain where there is a shadow big enough to read at distance. A grey
  // darkening under the lip is worth much less than the same darkening pushed blue: a
  // shadow outdoors is lit by sky and nothing else, and the hue shift is what tells the
  // eye it is a shadow rather than a darker rock.
  //
  // MEAN-PRESERVING, and that is the whole point of this round. R1 could only subtract:
  // lip down 0.54, riser down 0.16, nothing up. Integrated over a band that is a net
  // darkening of 0.12*0.54 + 0.12*0.16 = 8.4% applied in stripes, which is why the far
  // hillside measured +-5% modulation with the darks larger than the brights and read as
  // pencil ruling on a smooth dome rather than as benches and risers.
  //
  // The lift goes on the CREST — the narrow nose of the bench above the riser — not on
  // the bench itself, for the reason the previous pass got right: a mask covering the
  // majority of a surface can only ever be used to subtract, or its own gate becomes the
  // visible feature. Crest, lip and riser are all exactly BAND_W wide, so the lift that
  // makes the section integrate to zero is just K_LIP + K_RISER, with no width ratio to
  // get wrong. Every one of the three carries the identical bandLive * bandSteep gate,
  // so the cancellation is pointwise, not merely on average: wherever the gate closes,
  // all three vanish together and leave the local mean exactly where it was. No discs,
  // no rings, and the profile can now be run at an amplitude that actually reads.
  // Amplitude is set at the point where the step reads as a change of plane and stops
  // short of reading as a drawn line. The first pass at 0.42/0.16 was over it: stacked on
  // top of the lip's own cover change it made a near-black hairline on a pale face, which
  // is ink, not shadow. The cover change carries most of the value step now and this
  // carries the lighting event on top of it.
  // Pushed hard on purpose. Because this term is exactly balanced and the cover swaps
  // either side of it are only approximately balanced, raising it does not just raise the
  // contrast — it raises the *share* of the band's value structure that is balanced, and
  // so pulls the measured bright/dark ratio toward 1 as a side effect.
  // ROUND 3: cut to a third. These are now the *ambient* half of the step — an undercut
  // sees less sky, a crest sees all of it — with the directional half handed to the
  // shading normal above, where it belongs and where it dies gracefully. At 0.56 this term
  // was the step, and a step that does not move when the sun moves is a paint mark.
  // Measured on the far hillside, in a narrow column so sloping bands are not averaged
  // away: R2 ran bright +6.3% / dark -9.3% of local luminance, a ratio of 0.68 — the
  // dark-dominated profile the critic read as stripes. Cutting these to a third and
  // handing the step to the normal took the ratio to 0.88 at the same peak-to-peak, which
  // is the right *shape* at not quite enough amplitude: half a kilometre of aerial haze
  // multiplies whatever this produces by roughly 0.4, so a band that measures 50% p2p on
  // the albedo lands at 18% on screen. Back up a third, with the balance kept.
  // ...and then back off a notch, because the balanced lift lands on the crest, the crest
  // strips to pale cover, and 0.44 on top of that was starting to blow a sunlit bench nose
  // out to chalk — the one failure this palette has fallen into twice before.
  // WAVE 3: cut again, from 0.29/0.11 to 0.19/0.07, with what came off handed to
  // rlfCrease below. These three terms are a *partition of a band*, so they have to stay
  // exactly balanced against each other — hence cutting all three by the same factor and
  // leaving K_CREST as the sum — but the ambient step they are standing in for is not
  // really a property of the surface's colour at all. It is a half-plane of rock standing
  // two metres above the ground beside it, and relief.ts has the analytic form factor for
  // exactly that.
  const float K_LIP = 0.19;
  const float K_RISER = 0.07;
  const float K_CREST = K_LIP + K_RISER;   // widths are equal, so this is the balance
  float lipS = lipDark * notSnow;
  float riserS = riser * notSnow;
  float crestS = crestLit * notSnow;
  albedo *= 1.0 - lipS * K_LIP - riserS * K_RISER + crestS * K_CREST;
  // ...and the same section again on the sky term, with the same partition and therefore
  // the same exact balance. An undercut sees less sky and a crest sees all of it; that is
  // an irradiance statement and it stays true in overcast, in shade and under a sky whose
  // sun has gone, which is where a hillside painted in stripes stops being convincing.
  {
    const float A_LIP = ${gl(TerrainAmbient.bandLip)};
    const float A_RISER = ${gl(TerrainAmbient.bandRiser)};
    gTerrAmb *= 1.0 - lipS * A_LIP - riserS * A_RISER + crestS * (A_LIP + A_RISER);
  }

  // --- the undercut, as an occluder rather than as a band member ----------------
  // The ground below a riser does not stop being shaded at the edge of the lip member.
  // A 2.1 m scarp takes half the hemisphere away at its foot and is still worth a few per
  // cent five metres out, and the profile of that falloff is the thing that reads as a
  // wall rather than as a stripe — it is asymmetric, it is widest where the wall is
  // tallest, and it survives flat overcast because it never mentions the sun.
  // rlfCrease is that half-plane AO in one inversesqrt.
  //
  // bt runs uphill through the section (bench, apron, lip at 0.70-0.84, riser to 0.98,
  // crest across the seam), so (0.84 - bt) is the drop below the riser's foot in band
  // units; times the period and divided by the grade it is metres of ground. The window
  // stops it applying above the foot, where the riser's own normal tip already turns the
  // face away and a second darkening would double-count.
  {
    float btM = fract(bandT);
    float belowM = (0.84 - btM) * bandPer / max(grade, 0.10);
    float wallWin = (1.0 - smoothstep(0.84, 0.93, btM)) * bandLive * bandSteep * notSnow;
    gRlfOcc *= mix(1.0, rlfCrease(max(belowM, 0.0), bandPer * BAND_W, 0.62), wallWin);
  }
  // Warm key on the lit member against the cool sky-lit shadow on the dark one. That
  // pair is what tells the eye it is looking at a shadow rather than at a darker rock,
  // and it is the cheapest thing in this shader that makes a value step read as a form.
  //
  // BOTH TINTS ARE LUMA-NORMALISED, which is not fussiness. (0.82, 0.91, 1.16) has a
  // luminance of 0.909 and (1.12, 1.03, 0.86) has 1.037, so the "hue" pair was quietly
  // handing the lip another 5% of darkening and the crest only 1.6% of lift — enough on
  // its own to tip a balanced profile back to dark-dominated. Dividing each by its own
  // luminance leaves a pure chroma rotation and keeps every bit of the value structure in
  // the one place where it is provably balanced.
  // The warm side is worth more than the cool side at distance and gets the bigger mix.
  // Aerial perspective is itself a blue wash, so a cool shadow half a kilometre out is
  // being pushed the same direction the haze is already pushing it and contributes almost
  // nothing; a warm crest is the only thing on the mountain moving *against* the haze, and
  // hue contrast survives fog far better than value contrast does.
  albedo = mix(albedo, albedo * vec3(0.902, 1.001, 1.276), lipS * 0.50);
  albedo = mix(albedo, albedo * vec3(1.104, 0.990, 0.796), crestS * 0.62);
  float chS = channel * notSnow;
  // A gully floor is dark for two different reasons and they used to be one multiply:
  // it is wet gravel (albedo) and it is a slot that cannot see most of the sky
  // (irradiance). Split accordingly — the incision's own normal, further down, carries
  // the third reason.
  albedo *= 1.0 - chS * 0.24;
  gRlfOcc *= 1.0 - chS * 0.28;
  albedo = mix(albedo, albedo * vec3(0.84, 0.93, 1.14), chS * 0.55);

  // --- landform-scale variation that survives to the horizon -------------------
  // Cut from +-24% to +-12%. At the old amplitude this single term moved a surface
  // further than the difference between two *different* surfaces, so it did not read as
  // landform, it read as a stain over everything — and it was most of what made the mid
  // distance one brown. The structure block above replaces what it was doing.
  // ROUND 4 (wave 3): the macro half cut again, from +-8% to +-5.5%, with the mean held.
  // This term and the coarse form field above are the same field doing the same job by two
  // different means, and only one of them moves when the sun does. Every per cent taken
  // off here and put into relief is a per cent of the far hillside that stops being paint.
  albedo *= 0.925 + 0.11 * macro.x + 0.08 * region.x;
  albedo *= 1.0 - streak * 0.17 * notSnow;             // eroded runnels down the fall line

  // Iron-oxide belts. A real mountainside swings between grey-brown and red-brown over
  // a few hundred metres; without that, procedural soil reads as one bucket of paint.
  float oxide = clamp(0.5 + reg * 0.75 + mac * 0.30, 0.0, 1.0);
  albedo *= mix(vec3(1.0), mix(vec3(0.88, 0.94, 1.07), vec3(1.12, 0.97, 0.82), oxide), soilFrac);

  // Aspect: north faces (-Z) hold moisture, stay dark and mossy; south faces bake out.
  float north = clamp(-gN.z, 0.0, 1.0) * notSnow;
  albedo *= mix(1.0, 0.80, north * 0.6);
  albedo = mix(albedo, albedo * vec3(0.88, 1.07, 0.84), north * 0.35 * (1.0 - bRock));

  // Altitude: warm and rich in the valley, cold and dusty up top.
  albedo *= mix(vec3(1.07, 1.00, 0.90), vec3(0.96, 0.99, 1.07), alt);

  // --- water -------------------------------------------------------------------
  // It pools on the flats and in the low ground, and runs in lines down the fall line.
  float lowGround = 1.0 - smoothstep(0.10, 0.55, alt);
  float flatness = smoothstep(0.32, 0.04, slope);
  float damp = smoothstep(0.40, 0.80, macro.x * 0.62 + region.x * 0.58);
  float seep = pow(clamp(1.0 - abs(macro.x * 2.0 - 1.0), 0.0, 1.0), 5.0);
  // A drainage line is where the water is, by construction. Wiring the channel field
  // straight into the wetness is what makes rain read as a change in the *pattern* of
  // the mountain rather than as a global dimmer: the gullies fill in and go near-black
  // first, the benches go damp, the risers stay dry because nothing stands on them.
  // WHERE the water goes, independent of how much of it there is. Aspect-, form- and
  // drainage-driven: the gullies and the shaded undercuts fill first, the risers and the
  // wind-scoured crests never do.
  float wetPref = clamp(damp * (0.30 + 0.70 * flatness) * (0.35 + 0.65 * lowGround)
                      + seep * (0.35 + 0.45 * streak)
                      + channel * 0.80
                      + lipDark * 0.35
                      + benchBand * 0.10
                      // Duff is the dampest ground in a forest — it is a sponge, and it is
                      // still wet two days after the rock beside it has dried. Wiring the
                      // cover mask in here is what makes a wet day change *which* parts of
                      // the ground are dark rather than dimming all of it equally.
                      + coverW * 0.22
                      - riser * 0.35 - crestLit * 0.50, 0.0, 1.0);
  // HOW MUCH there is. This is the calibration the round was handed and the old form got
  // it badly wrong: wet was the preference field scaled by the global amount, so even a
  // full downpour only reached about 0.2 on ordinary ground and the rain preset came out
  // nine per cent darker than a clear day. Wet dirt is thirty to fifty per cent darker
  // than dry dirt; that is not a subtlety, it is the single most recognisable thing
  // weather does to a trail, and grip changes with it.
  //
  // So the amount now *interpolates the spatial field toward flat* as it rises. At a
  // trickle only the preferred places are damp, which is what keeps a clear day looking
  // like a clear day; in a downpour everything is wet and the pattern survives only as a
  // bias, which is what a hillside in the rain actually looks like.
  float wet = uWetness * mix(wetPref, 0.55 + 0.45 * wetPref, uWetness);
  wet *= notSnow;
  // Water collects in the low spots first, so the darkening follows the cavity field and
  // the clast creases — that is why a damp trail reads as mottled rather than as a
  // uniformly dark stripe, and why a wet stone stays bright while the dirt around its
  // base goes near-black.
  //
  // MEASURED AND RECALIBRATED, ROUND 3. Against the dry vista frame for frame, the rain
  // preset put the near ground at 0.225 against 0.293 — but the overcast sky is itself 9%
  // down, so only about 16% of that was the wetness, against the 30-50% that wet dirt
  // actually loses. The cause was this line, not the amount: a floor of 0.50 with 0.80 of
  // cavity on top means smooth ground — which is most ground — only ever gets half of the
  // wetness it was handed, and the clast exemption took another fifth off. The mottling is
  // worth keeping, so it stays as a *modulation* around a much higher floor rather than as
  // half the signal.
  float wetLocal = wet * (0.72 + 0.55 * cavity) * (1.0 - clastOn * 0.32);
  albedo *= mix(1.0, 0.37, clamp(wetLocal, 0.0, 1.0));
  rough = mix(rough, 0.11, clamp(wetLocal, 0.0, 1.0) * 0.90);

  // --- roughness variance ------------------------------------------------------
  // Nothing on a mountain has one roughness. Clast crowns buff up under traffic, the
  // matrix stays matte, and the grain moves it either side of that at the pixel scale.
  // Note the sign on the crown: a clast crown gets *rougher*, not smoother. Buffing it
  // is what turns a stone into a soap bubble, because a smooth dome under a directional
  // sun is a specular sphere and nothing else.
  rough += micC * 0.09 * soilFrac - cavity * 0.06 * soilFrac + crown * 0.10 * soilFrac;

  // --- bump --------------------------------------------------------------------
  // Each layer's gradient is scaled by the real relief that layer represents, so the
  // stack is self-similar and the near field does not turn into noise soup.
  //
  // The clast slopes arrive already dimensionless (dh/dx in metres per metre), which is
  // the whole point of the scale-free dome: a 20 cm stone that stands 6 cm proud has a
  // 50 degree flank, and that is what gets fed to the lighting instead of R2's 1.6 cm
  // pancake. Relief fades as lod^2 while the albedo mask fades as lod, because a strong
  // normal aliases into sparkle long before a colour does.
  vec3 grad = gradMid * 0.34 + gradFine * 0.10;
  grad += vec3(micro.y, 0.0, micro.z) * (0.024 / max(micA, 1e-3)) * bumpCut;
  grad += vec3(meso.y, 0.0, meso.z) * (0.016 / max(micA * 0.38, 1e-3)) * bumpCut;

  // The block layer's relief is deliberately *not* faded by graze: it is the only object
  // scale left at 60-150 m, which is exactly where the anisotropy is worst, and losing it
  // there is what put the brush strokes back. R2 also faded it to 45% by distance, on the
  // theory that a strong dome normal at 8 px reads as a pit — but the pit was the crease
  // ring and the below-matrix tint, not the normal, and cutting the normal only removed
  // the one cue that was still form rather than mark. It goes back to 78% at distance:
  // the sun-facing flank of a scattered boulder is exactly "form catching light", which is
  // what the whole mid distance is supposed to be made of.
  float relFade = graze * bumpCut;
  vec2 clastSlope = bkD.yz * (lodBlock * lodBlock * bkRel * mix(0.8, 1.0, graze) * bumpCut
                              * (0.78 + 0.22 * blockNear))
                  + (stD.yz * (lodStone * lodStone * stRel)
                   + clD.yz * (lodClod * lodClod * clRel)
                   + grD.yz * (lodGrain * lodGrain * grRel * 1.30)) * relFade;
  vec3 clastGrad = axU * clastSlope.x + axV * clastSlope.y;

  // The incised channels are a genuine height field, so they go through the same
  // tangential projection as everything else — but at full weight, not scaled by
  // bumpAmt: a gully is cut into whatever material happens to be lying in it.
  vec3 structGrad = vec3(chanG.x, 0.0, chanG.y);

  // Standing water fills in the texture; it does not fill in a fist-sized stone, and it
  // certainly does not fill in a gully.
  vec3 sg = grad - gN * dot(grad, gN);
  vec3 sgc = clastGrad - gN * dot(clastGrad, gN);
  vec3 sgs = structGrad - gN * dot(structGrad, gN);
  // structN, not gN: the cliff-band tips are a rotation of the base normal, not a bump
  // on top of it, so everything else is layered onto the already-tipped frame.
  vec3 nW = normalize(structN - sg * bumpAmt * 1.1 * (1.0 - wet * 0.45)
                              - sgc * (1.0 - wet * 0.22)
                              - sgs);

  // ===========================================================================
  // HEIGHTS, not slopes: the mid-scale form and the cover's own thickness.
  //
  // Everything above arrives as a gradient, because it was built from fields whose
  // derivative is known analytically. These two do not — a cover blend is a smoothstep of
  // a broken threshold and a mat's thickness is whatever came out of it — and hand-
  // differentiating either is how a bump ends up at the wrong strength. rlfBumpH takes
  // ANY scalar in metres and turns it into form with Mikkelsen's surface-gradient
  // construction: no UVs, no tangent frame, and a 4:1 slope cap so a mis-scaled field
  // cannot tip the normal past the horizon and light the ground from inside out.
  //
  // THE MID-SCALE FORM IS THE OLDER CARRIED GAP, and it was a scale problem rather than a
  // missing feature. gradMid * 0.34 * bumpAmt * 1.1 above works out to an RMS slope near
  // 0.017 — one degree, three centimetres of relief at a six-metre period. There was
  // simply no form past 30 m for the sun to find, which is why every structural cue out
  // there had to be an albedo mark. mv is that same six-metre field, already computed,
  // already Nyquist-faded, already domain-warped; multiplied by a relief in metres it is a
  // height field, and rlfBumpH does the rest.
  //
  // Ramped in past 10 m because inside that the clipmap tessellates at 0.5 m and the real
  // geometry, the clast layers and the tread are describing the ground perfectly well.
  //
  // NOT faded by graze, and this is the one place the file deliberately steps outside
  // rlfReliefAmp. The graze clamp exists because at a grazing angle the long axis of the
  // pixel footprint is genuinely undersampled — which is true of anything at the pixel
  // scale, and false of a six-metre lump at 300 m, which is twenty pixels across. It
  // bottoms out at its 0.22 floor on exactly the far hillside this term is for. Measured:
  // with graze in, an A/B against mesoRelief = 0 moved 6% of the vista frame and the far
  // hillside was untouched; the Nyquist and far-fade halves of the policy are kept, so the
  // layer still dies when the game says everything dies. The block clast layer three
  // hundred lines below already carries the same exemption for the same reason.
  float mesoLod = rlfBandLod(L, F_MID);
  float mesoAmt = smoothstep(${gl(TerrainShading.mesoNear)}, ${gl(TerrainShading.mesoFar)}, vWDist)
                * mesoLod * mesoLod
                * mix(1.0, RLF_FAR_FLOOR_RELIEF, L.farFade)
                * (1.0 - E.motion) * E.scale * uDbgB.x
                * (0.55 + 0.45 * bumpAmt);

  // The root strands, as the 6 cm of proudness their own comment always claimed. Faded a
  // second time on lodFine — rootMask already carries one factor, and relief has to go as
  // lod squared or a strong normal aliases into sparkle long before a colour does.
  float rootH = rootMask * bRoot * ${gl(TerrainGround.rootProud)} * lodFine;

  float bumpH = (mv - 0.5) * ${gl(TerrainShading.mesoRelief)} * mesoAmt
              + formH
              + ridgeH
              + massH
              + groundLump
              + rootH
              // Both covers go through gReliefAmpS for the reason in the block at
              // gBandLodS: a fade built from dFdx(wp) is a staircase on the triangulation
              // and bumpH differentiates it into a wireframe.
              + coverH * gReliefAmpS(1.4)
              // The stone pavement's own thickness, through the same helper as the duff
              // mat: both are covers a few centimetres deep lying on the ground a rider is
              // looking at, so both want the graze clamp and the Nyquist fade a
              // pixel-scale feature needs. A patch of lag whose shoulder catches the sun is
              // a material; one that does not is a stain, and this line is the difference.
              + lagH * gReliefAmpS(1.0)
              // The talus fan's own thickness. NOT through rlfReliefAmp: like the two form
              // fields it is a landform-scale feature and the graze clamp inside that
              // helper is a pixel-scale correction. minH already carries its own distance
              // ramp and its own Nyquist gate.
              + minH;
  gRlfNormalW = rlfBumpH(nW, wp, bumpH, 1.0);

  // ===========================================================================
  // THE LANDFORM LIGHT. Read the gTerrLand block at the top of TERRAIN_COMMON for the
  // argument; this is the four lines of arithmetic it describes.
  //
  // Note what is NOT in landSlope: the cliff bands (iso-elevation, and the pattern the
  // brief calls the symptom), the clast domes (object scale, and already carried by the
  // normal), the mesoform (6 m — below a pixel out here), the cover rims and the tread.
  // Three inputs only, all of them landform: the 24 m form field, the 90 m spur band and
  // the incised drainage. Each already carries its own distance ramp and its own Nyquist
  // fade, so where the far field has no shape this expression is identically 1.0 and the
  // compiler's own branch skips it.
  // ===========================================================================
  float landOn = smoothstep(${gl(TerrainLandform.near)}, ${gl(TerrainLandform.far)}, vWDist);
  if (landOn > 0.004) {
    // WHAT THIS IS MEASURED AGAINST, AND WHY IT IS NOT THE SHADING FIELDS.
    //
    // The first pass of this round drove the sigmoid off formG + ridgeG + chanG — this
    // file's own invented landform — and the A/B measured nothing, in five frames of one
    // page session, because those three fields are held out until 40-130 m and the whole
    // visible mountain in the current build is inside 200 m. The correct input was
    // sitting in the varying all along: the clipmap writes the terrain's EXACT ANALYTIC
    // normal, so gN already contains every gully, rib and spur the heightfield has. What
    // was missing was never the shape, it was the transfer.
    //
    // The pivot is the sun's own elevation, which is what a piece of ground lying flat
    // sees. So flat ground is exactly 1.0 whatever the hour, a flank turned toward the sun
    // lifts and one turned away drops, and neither the mountain's mean nor its exposure
    // moves — the term redistributes the light it was already given across the landform,
    // which is the whole difference between this and a contrast knob.
    // The normal is gN plus the three LANDFORM-scale gradients and nothing else — the
    // 24 m form field, the 90 m spur band and the incised drainage. Not gRlfNormalW: that
    // one carries the clast domes, the grain and the cover rims, and running a saturating
    // curve over pixel-scale relief at 30-150 m is how a procedural bump turns into
    // sparkle. Each of the three already carries its own Nyquist fade, so this normal is
    // band-limited by construction.
    vec2 landSlope = formG + ridgeG + massG + chanG * ${gl(TerrainLandform.channel)};
    vec3 nLand = rlfBumpXZ(gN, landSlope, 1.0);
    // Measured against the ground this landform stands on, not against the horizontal.
    // Pivoting on the sun's own elevation was the second pass of this round and it is
    // wrong for a reason worth recording: a hillside sits at N.sun ~ 0.85 while the sun
    // elevation is ~0.52, so the pivot was 0.6 of a unit away from the data, the sigmoid
    // ran saturated over the whole frame, and the ten degrees of landform on top of it had
    // no gain left to act on. Measured: +1.8% of frame standard deviation, i.e. nothing.
    // Subtracting the base makes the input the LANDFORM'S OWN contribution and nothing
    // else, which puts the data back in the steep part of the curve and makes the term
    // mean-preserving by construction — the input is odd about zero.
    float base = dot(gN, sunW);
    float s = (dot(nLand, sunW) - base) / max(abs(base), 0.20);
    // tanh, spelled with one inversesqrt: odd, monotone, and saturating, so a ten-degree
    // flank reads without having to pretend it is a sixty-degree one.
    float x = s * ${gl(TerrainLandform.sharp)};
    float sig = x * inversesqrt(1.0 + x * x);
    gTerrLand = clamp(1.0 + sig * ${gl(TerrainLandform.gain)} * landOn * sunVis * uDbgC.x,
                      0.22, 1.95);

  }

  // The same three fields again, NORMALISED rather than in metres: where this fragment
  // sits within its own relief, -1 in the bottom of a hollow and +1 on a crest. It has to
  // be built separately from bumpH because bumpH is a sum of layers whose amplitudes span
  // 4 cm to 2.6 m, and dividing that by any one characteristic height gives the far field
  // a term ten times the near field's. Each layer is divided by its own half-amplitude
  // and carries its own existing fade, so all three arrive at the same weight and each
  // one switches off exactly where it already switched off.
  //
  // This is the sky term's main input. See TerrainAmbient.height.
  float hAo = (mv - 0.5) * 2.0 * mesoAmt
            + formH * ${gl(2 / TerrainShading.formRelief)}
            + ridgeH * ${gl(2 / TerrainShading.ridgeRelief)}
            + massH * ${gl(2 / TerrainShading.massRelief)}
            + groundLump * ${gl(2 / TerrainGround.lumpLoam)};

  // ===========================================================================
  // THE SKY TERM. How much sky each piece of this relief can see, and which sky.
  //
  // Everything above answers "how much sun does this catch". This answers the other
  // question, and it has to be asked separately because the two have different answers
  // and only one of them was ever being asked. Read TerrainAmbient first — the argument
  // for why the probe's own normal response is not enough is written out there.
  //
  // It runs here, after gRlfNormalW, because every term is a function of the FINAL
  // perturbed normal: the clast domes, the cover rim, the six-metre mesoform and the
  // twenty-four-metre form field are all in it by then, each already carrying its own
  // LOD fade. So this block needs no distance policy of its own — where the relief has
  // faded to nothing there is nothing to shape, and the expression collapses to 1.0.
  // ===========================================================================
  {
    vec3 aN = gRlfNormalW;

    // 1. SKY VISIBILITY, as a ratio against the ground this relief is standing on.
    // (1 + N.y) * 0.5 is the cosine-weighted fraction of a uniform dome a facet sees;
    // dividing by the same quantity for the undisturbed surface is what makes the whole
    // term mean-preserving — flat ground is exactly 1.0 whatever its slope, and only the
    // deviation shapes anything. The extra gain on top is the occlusion the probe cannot
    // know about: a bump on a hillside has the hillside itself standing in the half of
    // the sky it is tilted away from.
    float invUp = 1.0 / max(1.0 + gN.y, 0.30);
    float cosT = clamp(dot(aN, gN), -1.0, 1.0);          // how far this facet leans
    // ...minus its own isotropic mean, or the term is an occlusion wearing a shaping
    // term's clothes and the whole hillside comes out darker. See TerrainAmbient.
    float iso = (1.0 - cosT) * max(gN.y, 0.0) * invUp;
    float dTilt = clamp((aN.y - gN.y) * invUp
                        + iso * ${gl(TerrainAmbient.tiltRestore)}, -1.0, 1.0);

    // 2. THE DIRECTIONAL LOBE. Shade is lit from somewhere: the bright circumsolar sky
    // and the warm bounce off whatever the sun is still hitting, both of them upward of
    // the sun's own azimuth. One dot product, taken against the part of the perturbed
    // normal that is PERPENDICULAR to the geometric one — the facet's lean, which
    // averages to zero over azimuth — so it tilts the light across the relief without
    // lifting or dropping the ground it stands on.
    vec3 keyDir = normalize(vec3(sunW.x, sunW.y + ${gl(TerrainAmbient.lobeLift)}, sunW.z));
    float dLobe = dot(aN - gN * cosT, keyDir);

    // 3. RELIEF POSITION — the term that carries this. A hollow does not lose its own
    // cosine, it loses the sky its neighbours are standing in, and for a zero-mean height
    // field that is the height itself: crests up, hollows down, mean untouched. See
    // TerrainAmbient.height for why the two slope terms above cannot do this job.
    float dHeight = clamp(hAo, -1.4, 1.4);

    float ambS = 1.0
               + dTilt * ${gl(TerrainAmbient.tilt)}
               + dLobe * ${gl(TerrainAmbient.lobe)}
               + dHeight * ${gl(TerrainAmbient.height)};

    // 4. PIXEL-SCALE GRAIN. A crown sees the sky, a pit does not, and neither of them is
    // resolvable as a normal at the range this matters — which is why this is a value
    // term rather than more bump. Signed around zero, so it is mean-preserving by
    // construction, and carried on the graze term like every other pixel-scale term here
    // so it cannot alias into a shimmer at a grazing angle.
    ambS *= 1.0 + (micC * ${gl(TerrainAmbient.grainMicro)}
                 + mesC * ${gl(TerrainAmbient.grainMeso)}) * soilFrac * graze;

    // 5. THE LITTER, given a near side and a far side. fLit * litter.z is a signed ramp
    // across each speck; spending a fifth of it on the sky term is the difference between
    // a needle lying on the duff and a needle printed on it.
    ambS *= 1.0 + fLit * litter.z * ${gl(TerrainAmbient.litter)};

    // 6. CHROMA. A shadow outdoors is lit by the sky and by what the sky bounced off, so
    // the up-facing side of a bump goes toward the sky's own colour and its underside
    // toward the ground bounce. Both directions are normalised to unit luminance, so this
    // is a pure hue rotation: it cannot move the value structure the rest of the file
    // spent three rounds calibrating, and it is the cheapest thing here that stops shaded
    // ground reading as ink rather than as dirt under a sky.
    vec3 skyC = E.sky / max(dot(E.sky, vec3(0.2126, 0.7152, 0.0722)), 1e-4);
    vec3 gndC = E.gnd / max(dot(E.gnd, vec3(0.2126, 0.7152, 0.0722)), 1e-4);
    float up = clamp(dHeight * 0.9 + dTilt * 2.2, -1.0, 1.0);
    vec3 tint = mix(vec3(1.0), skyC, max(up, 0.0) * ${gl(TerrainAmbient.chroma)})
              * mix(vec3(1.0), gndC, max(-up, 0.0) * ${gl(TerrainAmbient.chroma)});

    // The floor is deliberately well above gRlfOcc's 0.22: this is a shaping term, not an
    // occluder, and anything that reaches for black here is a bug in one of the five
    // terms above rather than a dark part of the ground.
    gTerrAmb *= clamp(ambS, 0.30, 2.10) * tint;
    gTerrAmb = clamp(gTerrAmb, vec3(0.22), vec3(2.3));
    gTerrAmb = mix(vec3(1.0), gTerrAmb, uAmbAmt);
  }

  // ===========================================================================
  // FAR-FIELD MATERIAL SEPARATION — now the palette's, because ours rotated hue.
  //
  // The INTENT was right and palette.ts says so in as many words (FarSeparation):
  // aerial perspective keeps about half the ground's value range, so send more separation
  // into the haze than you want to arrive with. What shipped was:
  //
  //     albedo += (albedo - vec3(0.185)) * (sep * 0.62);           // per channel
  //     albedo  = mix(vec3(luma), albedo, 1.0 + sep * 0.45);       // then a chroma gain
  //     albedo  = max(albedo, vec3(0.0));                          // then a clamp at zero
  //
  // Three separate mechanisms for a hue rotation stacked on one line each:
  //   - the expansion is PER CHANNEL about a SCALAR mid, so a channel below 0.185 is
  //     pushed down while a channel above it is pushed up. Rock's linear albedo
  //     (0.087, 0.107, 0.126) leaves at (0.026, 0.098, 0.158): blue/red goes 1.45:1 to
  //     6.1:1. That is not "rock stays cold", that is rock becoming ultramarine.
  //   - max(albedo, 0.0) is a channel clamp, and a channel clamp IS a hue shift — the
  //     comment two lines below it says so about the highlight and then does it anyway at
  //     the shadow end, which is where the dark surfaces all live.
  //   - the chroma gain then multiplies whatever hue survived by 1.45.
  // Measured on the 100-200 m band, the ground classes came out spread over 218 degrees
  // of hue. The critique calls it an orange / lavender / chartreuse camouflage patchwork;
  // it is the arithmetic above, and no re-tune of 0.62 and 0.45 fixes a rotation.
  //
  // palFarSeparate is the sanctioned replacement and it is the same idea done in OKLab:
  // lightness expands about the LADDER's mid (LADDER_MID_L, the real centre of mass of
  // the eight authored entries, not a guessed 0.185), chroma scales in (a,b) so hue is
  // literally untouched, the result is bounded to the ladder's own L range, capped at the
  // accent chroma ceiling, and CHROMA-clipped rather than channel-clamped when it leaves
  // gamut. It cannot reorder the ladder and it cannot rotate a hue, by construction.
  // Zero inside FarSeparation.near (40 m), so it is off on every pixel a rider reaches.
  //
  // uDbgC.y keeps the A/B rig alive: far2(land, 0) still removes the whole term.
  // ===========================================================================
  // --- THE INSTRUMENT'S KNOWN-BAD CASE, default off -------------------------------
  // __gripTerrainShading.degrade(collapse, legacyFar, holdS) drives uDbgE, and both
  // knobs SELF-EXPIRE on a timer rather than needing a reset in capture.mjs's per-scenario
  // block — see docs/AGENTS.md, "a debug knob that is not in capture.mjs's per-scenario
  // reset leaks into every scenario after it". A knob that cannot outlive its own hold
  // cannot leak.
  //
  // uDbgE.x forces every class onto ONE colour while the classifier still marks them
  // distinct. That is not a look, it is a reconstruction of the exact defect this round
  // exists to fix, and it is how terrainshading.nearClassSeparation* is shown to be able
  // to FAIL. An invariant that has never been seen to fail is a decoration.
  if (uDbgE.x > 0.002) albedo = mix(albedo, ${palAt(Ground.hardpack, -0.02, 0.5)}, uDbgE.x);

  {
    float sepAB = uDbgC.y;
    if (sepAB > 0.002) {
      vec3 sepC;
      if (uDbgE.y > 0.5) {
        // THE LEGACY PATH. Kept, unedited, as the far invariant's known-bad case: this is
        // the arithmetic that produced 218 degrees of hue spread at 100-200 m. Never on
        // by default; degrade(0, 1) turns it on for a few seconds so the instrument can
        // be watched reporting the defect it is supposed to catch.
        float sep = smoothstep(${gl(TerrainLandform.sepNear)}, ${gl(TerrainLandform.sepFar)}, vWDist);
        vec3 a2 = albedo + (albedo - vec3(${gl(TerrainLandform.farMid)}))
                * (sep * ${gl(TerrainLandform.farValue)});
        float sLum = dot(a2, vec3(0.2126, 0.7152, 0.0722));
        a2 = mix(vec3(sLum), a2, 1.0 + sep * ${gl(TerrainLandform.farChroma)});
        a2 = max(a2, vec3(0.0));
        float pk = max(a2.r, max(a2.g, a2.b));
        if (pk > 0.62) a2 *= (0.62 + (pk - 0.62) / (1.0 + (pk - 0.62) * 2.2)) / pk;
        sepC = a2;
      } else {
        sepC = palFarSeparate(albedo, vWDist);
      }
      albedo = mix(albedo, sepC, sepAB);
    }
  }

  gRlfRough = clamp(rough, 0.06, 1.0);
  gRlfOcc = clamp(gRlfOcc, 0.22, 1.0);
  diffuseColor.rgb = clamp(albedo, 0.0, 1.0);

  // --- false colour ------------------------------------------------------------
  // Four questions the finished frame cannot answer, because aerial perspective, ACES and
  // the post grade sit between this shader and the pixel. Each of these is the raw
  // quantity, written past the fog and past the tone curve, so what you read off the
  // capture is the number this file produced.
  //
  // Everything below is scaled by DBG_EXPOSE. The post chain still owns presentation — it
  // renders the scene to its own target and grades that — so a dump written at 1.0 comes
  // back clipped white and every value dump reads "flat" for a reason that has nothing to
  // do with the ground. 0.30 lands a unit quantity in the middle of the curve.
  const float DBG_EXPOSE = 0.30;
  gTerrDbg = vec3(0.0);
  if (uDbgB.y > 0.5) {
    int m = int(uDbgB.y + 0.5);
    if (m == 1) {
      // 1. DO THE MATERIALS SEPARATE AT ALL out there, before any of it is graded?
      //    rock cyan, loose white, hardpack orange, loam brown, grass green, root red,
      //    snow blue.
      gTerrDbg = (bRock * vec3(0.15, 0.55, 0.75) + bLoose * vec3(0.95, 0.92, 0.82)
               + bHard * vec3(0.95, 0.48, 0.10) + bLoam * vec3(0.30, 0.20, 0.10)
               + bGrass * vec3(0.20, 0.70, 0.18) + bRoot * vec3(0.75, 0.15, 0.25)
               + bSnow * vec3(0.55, 0.70, 1.00)) * (DBG_EXPOSE * 2.4);
    } else if (m == 2) {
      // 2. The albedo this file produced, ungraded and unfogged, x2 so a 0.15 dirt is
      //    actually visible on screen.
      gTerrDbg = clamp(albedo * 2.0, 0.0, 1.0) * (DBG_EXPOSE * 2.4);
    } else if (m == 3) {
      // 3. IS THERE ANY FORM OUT THERE FOR THE LIGHT TO FIND. N.sun off the final
      //    perturbed normal and nothing else: no albedo, no ambient, no haze. A flat
      //    grey field here means every structural cue in the frame is paint.
      gTerrDbg = vec3(clamp(dot(gRlfNormalW, sunW), 0.0, 1.0) * DBG_EXPOSE);
    } else if (m == 5) {
      // 5. HOW FAR THE NORMAL ACTUALLY MOVED, in degrees, 0..30 mapped over the dump.
      //    N.sun answers "is it lit differently" but folds in the base slope and the sun
      //    angle, so a small number there is ambiguous between "no relief" and "relief
      //    perpendicular to the light". This is the relief on its own.
      float tilt = degrees(acos(clamp(dot(gRlfNormalW, gN), -1.0, 1.0)));
      gTerrDbg = vec3(clamp(tilt / 30.0, 0.0, 1.0) * DBG_EXPOSE * 2.4);
    } else if (m == 6) {
      // 6. The three far-field amounts, one per channel: red = the 24 m form field,
      //    green = the 6 m mesoform, blue = the talus cover's weight. Black here means a
      //    layer is switched off by its own LOD policy rather than by its amplitude.
      gTerrDbg = vec3(formAmt + ridgeAmt + massAmt, mesoAmt, minW) * (DBG_EXPOSE * 2.4);
    } else if (m == 8) {
      // 8. THE HEIGHTFIELD'S OWN ANSWER TO THE SUN. dot(gN, sun) off the clipmap's exact
      //    analytic normal, before anything in this file touches it. This is the one
      //    measurement that separates "the material is not shading the landform" from
      //    "there is no landform in the heightfield to shade": if this dump is one flat
      //    colour, no shading model can put a gully on that hillside and the fix is
      //    upstream of this file.
      //
      //    BANDED, NOT CONTINUOUS, and that is the lesson of three waves of instrument
      //    bugs: a continuous ramp written through a post grade that another agent is
      //    editing this minute cannot be read, because a flat field and a clipped field
      //    look identical. Five hard bands cannot clip into each other. 0.10 wide each,
      //    so two adjacent bands are a 6 degree difference in the sun angle.
      float gc = clamp(dot(gN, sunW), 0.0, 1.0);
      gTerrDbg = gc < 0.45 ? vec3(0.36, 0.10, 0.10)
               : gc < 0.60 ? vec3(0.42, 0.24, 0.06)
               : gc < 0.72 ? vec3(0.30, 0.34, 0.08)
               : gc < 0.82 ? vec3(0.10, 0.34, 0.16)
               : gc < 0.90 ? vec3(0.10, 0.26, 0.40)
                           : vec3(0.34, 0.14, 0.40);
    } else if (m == 9) {
      // 9. THE OBJECT CHANNELS, which is the instrument the clast defect needed and did
      //    not have. RED is the crease ring, GREEN is the crown plus the body — the two
      //    cues that are supposed to fill it — and BLUE is the cast shadow. All three
      //    AFTER entitlement, so this is what the surface under them actually receives.
      //
      //    How to read it: a healthy clast is a green disc with a thin red edge and a blue
      //    crescent on its lee side. The defect this round exists to kill is a red ring
      //    with black inside it, and it is unmistakable in this dump at a glance — which
      //    is the point, because in the beauty frame it is a 4% value step behind aerial
      //    perspective and a post grade and cannot be attributed at all.
      gTerrDbg = vec3(crease, crown * 0.5 + body * 0.5, contact) * (DBG_EXPOSE * 2.4);
    } else if (m == 10) {
      // 10. THE ATTRIBUTION CHANNEL — not a picture, a data buffer, and the only mode in
      //     this list that is meant to be READ BY A MACHINE rather than looked at.
      //
      //     installTerrainAudit() renders this into a small offscreen tile through the
      //     SAME camera as the frame that has just been drawn to the canvas, so pixel
      //     (i, j) of this buffer is pixel (i, j) of the finished frame. That is the whole
      //     trick: it lets the separation invariant be stated on the DELIVERED colour
      //     while the class comes from the classifier, which is the only combination that
      //     can see the defect. Every previous metric in this area read the classifier for
      //     both halves and therefore reported perfect separation four rounds running.
      //
      //       R = ${gl(DBG_CLASS_BASE)} + dominant class index * ${gl(DBG_CLASS_STEP)}
      //       G = distance / ${gl(DBG_CLASS_DIST_M)} m, clamped
      //       B = 1 - R      <- the checksum. See the note in installTerrainAudit for why
      //                         a two-channel constraint is what makes non-terrain
      //                         geometry (which draws into this buffer normally) rejectable
      //                         without touching anyone else's objects.
      //
      //     Written past ACES and past the dithering chunk like every other dump here, so
      //     no tone curve can move a class code onto its neighbour's.
      float wBest = bHard; float iBest = 0.0;
      if (bLoam  > wBest) { wBest = bLoam;  iBest = 1.0; }
      if (bLoose > wBest) { wBest = bLoose; iBest = 2.0; }
      if (bRock  > wBest) { wBest = bRock;  iBest = 3.0; }
      if (bRoot  > wBest) { wBest = bRoot;  iBest = 4.0; }
      if (bGrass > wBest) { wBest = bGrass; iBest = 5.0; }
      if (bSnow  > wBest) { wBest = bSnow;  iBest = 6.0; }
      // A pixel whose top weight is a coin toss is not evidence about either class.
      float rr = wBest < ${gl(DBG_CLASS_MIN_W)}
               ? ${gl(DBG_CLASS_BASE - DBG_CLASS_STEP)}
               : ${gl(DBG_CLASS_BASE)} + iBest * ${gl(DBG_CLASS_STEP)};
      gTerrDbg = vec3(rr, clamp(vWDist * ${gl(1 / DBG_CLASS_DIST_M)}, 0.0, 1.0), 1.0 - rr);
    } else if (m == 7) {
      // 7. THE LANDFORM LIGHT ON ITS OWN. 0.22..1.95 mapped over the dump with 1.0 at
      //    mid grey, so a flat field here means the far field is still receiving one
      //    uniform light and the round failed. Nothing else is in it: no albedo, no
      //    ambient, no haze.
      gTerrDbg = vec3(clamp((gTerrLand - 0.22) / 1.73, 0.0, 1.0) * DBG_EXPOSE * 2.4);
    } else {
      // 4. Distance rings, so a claim about "100 to 500 m" can be located in the frame.
      float d = vWDist;
      gTerrDbg = d < 100.0 ? vec3(0.10, 0.10, 0.10)
               : d < 200.0 ? vec3(0.20, 0.35, 0.20)
               : d < 300.0 ? vec3(0.35, 0.30, 0.10)
               : d < 500.0 ? vec3(0.45, 0.15, 0.15)
                           : vec3(0.12, 0.12, 0.30);
    }
  }
}`;

// ===========================================================================
// THE INSTRUMENT.
//
// WHAT EVERY PREVIOUS METRIC IN THIS AREA GOT WRONG, and it is the same mistake four
// rounds running: it measured the CLASSIFIER. `view(1)` dumps the seven blend weights as
// seven saturated false colours and `view(2)` dumps the raw albedo before lighting, fog
// and the grade. Both separate beautifully — a classifier separating is the definition of
// a classifier — and this file's own header quotes them as evidence that "the surfaces out
// there ARE distinct". They were. The PIXEL was not, and the pixel is the product.
//
// So this instrument measures the two halves in the two places they actually live:
//   THE CLASS comes from the classifier (debug view 10, an attribution buffer, not a
//   picture), rendered through the SAME camera as the frame that has just been drawn;
//   THE COLOUR comes from the CANVAS — after lighting, after sky.ts's aerial perspective,
//   after ACES, after post.ts's grade. It is the same bytes a screenshot contains.
// Class sample (i, j) is canvas pixel (i*S + S/2, j*S + S/2) because the attribution pass
// uses the SAME camera and the SAME frustum, only rasterised at 1/S the linear resolution.
// No sub-rect, no view offset, no resampling — and therefore no registration error to make.
// The probe writes both buffers out as PNGs anyway (`sepImages()`), because a registration
// error is the one failure mode of this instrument that a number could not show.
//
// HOW IT IS SHOWN TO BE ABLE TO FAIL — the step docs/AGENTS.md asks for and the step two
// wave-4 instruments skipped. `__gripTerrainShading.degrade(1, 0)` forces every class onto
// one colour in the fragment shader while leaving the classifier untouched: the near-field
// separation must collapse toward zero. `degrade(0, 1)` restores the exact per-channel far
// expansion this round removed: the far hue spread must jump back to ~200 degrees. Both
// are driven from the probe in `captures/w5-ts-tools/`, in the same page session as the
// good build, so the comparison cannot be across two trees nine agents are editing.
//
// COST. One `readPixels` of a 512x384 rectangle of the canvas plus one 512x384 render of
// the scene into a byte target, every SEP_PERIOD_S seconds. Measured — see the report.
// ===========================================================================

/** A per-class mean, in the finished frame. */
export interface SepClassStat {
  name: string;
  pixels: number;
  /** OKLab, of the mean linear colour. */
  L: number;
  C: number;
  hueDeg: number;
}
export interface SepMeasurement {
  /** Pixels in the tile that decoded as terrain, and the tile's total. */
  terrainPx: number;
  tilePx: number;
  near: SepClassStat[];
  far: SepClassStat[];
  /** OKLab L between the two most common near-field classes. */
  nearDeltaL: number;
  nearDeltaHue: number;
  nearPair: string;
  /** Widest hue difference between any two qualifying classes at 100-200 m. */
  farHueSpread: number;
  farPair: string;
  /**
   * RAW per-class pixel counts in each band, before the evidence floor, indexed by
   * `SEP_CLASS_NAMES`.
   *
   * `near`/`far` above are filtered by `SEP_MIN_CLASS_FRAC`, which is right for a
   * statistic — a 40-pixel population is not evidence about a material's colour — and
   * wrong for the question this round is about. "Rock is 0.3% of the near field" and
   * "rock is not in the frame at all" are different worlds and the filtered arrays report
   * them identically, as absence. These two are the census, unfiltered, so a report can
   * say which of the two it is.
   */
  nearAll: number[];
  farAll: number[];
  /** How many classes cleared the evidence floor in each band. */
  nearClasses: number;
  farClasses: number;
  /** Milliseconds this instrument added, past the driver flush. */
  costMs: number;
  /** Milliseconds the one-texel read spent waiting for the frame's own rasterisation. */
  flushMs: number;
}

let lastSeparation: SepMeasurement | null = null;
/**
 * The two buffers the last measurement correlated, as PNG data URLs.
 *
 * This exists because the single most likely way for THIS instrument to lie is a
 * registration error — `setViewOffset` counts y down from the top and `readPixels` counts
 * it up from the bottom, and correlating the top of one buffer with the bottom of the
 * other produces "no separation" from a perfectly separated frame. A number cannot show
 * that; two images side by side can, in one look. BAR: if a number and a screenshot
 * disagree, believe the screenshot.
 */
let lastSepImages: { w: number; h: number; beauty: string; cls: string } | null = null;

const SEP_CLASS_NAMES = ['hardpack', 'loam', 'loose', 'rock', 'root', 'grass', 'snow'];
/**
 * The attribution pass samples every SEP_STRIDE-th canvas pixel in both axes.
 *
 * 8 gives 200x112 samples on a 1600x900 capture — 22,400 candidate points spread over the
 * WHOLE frame rather than 49,152 packed into a sixth of it, which is the difference
 * between a number and no number at all (see the note in `update`).
 */
const SEP_STRIDE = 5;
/** Seconds between measurements. One frame in ~240 at 60 fps. */
const SEP_PERIOD_S = 6.0;
/**
 * The near field, metres — the 52-80% of every frame the rider is actually looking at.
 */
const SEP_NEAR_M = 100;
/**
 * The mid distance. The critique states the camouflage defect at "100-200 m"; the band is
 * widened to 90-280 here because measured on this build a typical riding frame has only a
 * couple of hundred ground samples inside 100-200 m — the rest is timber and sky — and a
 * band that is usually empty is a metric that is usually silent. The widened band contains
 * the critique's band, so a number taken here is comparable to the one it is answering.
 */
const SEP_FAR_LO_M = 90;
const SEP_FAR_HI_M = 280;
/**
 * A class must hold this fraction of a band's attributed pixels, and this many samples
 * absolutely, to be evidence.
 *
 * Both came down this round, from 4% / 250. Measured on the shipping build, the near field
 * of a typical riding frame is 97% one class inside the terrain material's own pixels
 * (most of the rest of the ground the rider sees is the trails piece's tread mesh, which
 * is a different material and correctly excluded), so a 4% floor rejected the second class
 * and the invariant reported NOTHING. An invariant that goes quiet on a uniform frame is
 * useless for a defect whose name is "the frame is uniform".
 */
const SEP_MIN_CLASS_FRAC = 0.008;
const SEP_MIN_CLASS_PX = 70;
/** ...and this many before a HUE difference is claimed. See the note at `chromatic`. */
const SEP_MIN_HUE_PX = 150;

/** sRGB byte -> linear. Built once; the hot loop is 196k pixels. */
const SRGB_LUT = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    t[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }
  return t;
})();

/** Set by `sepImages()`; the next measurement fills `lastSepImages` and clears it. */
let wantImages = false;
/** Set by `sepNow()`; skips the duty cycle for one measurement. */
let forceMeasure = false;

/**
 * A GL-order byte buffer as a PNG data URL, point-sampled by `stride` and flipped upright.
 * Diagnostic only — it allocates a canvas and is never called on a shipped frame.
 */
function dumpPng(
  buf: Uint8Array, srcW: number, srcH: number, w: number, h: number, stride: number,
): string {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const c2 = cv.getContext('2d');
  if (!c2) return '';
  const img = c2.createImageData(w, h);
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const si = i * stride + (stride >> 1);
      const sj = j * stride + (stride >> 1);
      const so = (Math.min(sj, srcH - 1) * srcW + Math.min(si, srcW - 1)) * 4;
      const dOff = ((h - 1 - j) * w + i) * 4;   // GL rows are bottom-up
      img.data[dOff] = buf[so];
      img.data[dOff + 1] = buf[so + 1];
      img.data[dOff + 2] = buf[so + 2];
      img.data[dOff + 3] = 255;
    }
  }
  c2.putImageData(img, 0, 0);
  return cv.toDataURL('image/png');
}

function hueDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Register the separation measurement as a system.
 *
 * Order 300, so it runs after `post` (110) has drawn the finished frame to the canvas.
 * If anything in here throws — a context loss, a driver that will not read the drawing
 * buffer — it disables itself rather than spamming the console, and the invariant reports
 * "never observed", which the harness prints as a defect in its own right.
 */
function installTerrainAudit(dbg: Record<string, THREE.IUniform<THREE.Vector4>>): void {
  let tile: THREE.WebGLRenderTarget | null = null;
  let beautyBuf: Uint8Array | null = null;
  let classBuf: Uint8Array | null = null;
  let bufW = 0;
  let bufH = 0;
  let timer = 1.0;
  // `?nosepaudit=1` turns the instrument off for an A/B on its own cost. Read from the
  // URL rather than a hook so the switch is set before the first frame — a knob you can
  // only flip at t=1s cannot price the thing it flips.
  let failures = (typeof location !== 'undefined'
    && location.search.indexOf('nosepaudit') >= 0) ? 99 : 0;
  const size = new THREE.Vector2();

  // Per-class accumulators, two bands. Plain arrays; allocated once.
  const nR = new Float64Array(7); const nG = new Float64Array(7); const nB = new Float64Array(7);
  const nN = new Int32Array(7);
  const fR = new Float64Array(7); const fG = new Float64Array(7); const fB = new Float64Array(7);
  const fN = new Int32Array(7);
  const lab: RGB = [0, 0, 0];
  /** One texel, read first to force the driver's queue. See the note in `update`. */
  const oneTexel = new Uint8Array(4);

  const stats = (
    r: Float64Array, g: Float64Array, b: Float64Array, n: Int32Array, total: number,
  ): SepClassStat[] => {
    const out: SepClassStat[] = [];
    const floor = Math.max(SEP_MIN_CLASS_PX, total * SEP_MIN_CLASS_FRAC);
    for (let i = 0; i < 7; i++) {
      if (n[i] < floor) continue;
      const k = 1 / n[i];
      linearToOklab(r[i] * k, g[i] * k, b[i] * k, lab);
      let h = (Math.atan2(lab[2], lab[1]) * 180) / Math.PI;
      if (h < 0) h += 360;
      out.push({
        name: SEP_CLASS_NAMES[i], pixels: n[i], L: lab[0], C: Math.hypot(lab[1], lab[2]), hueDeg: h,
      });
    }
    out.sort((a, c) => c.pixels - a.pixels);
    return out;
  };

  registerSystem({
    id: 'terrain.sepaudit',
    order: 300,

    update(dt: number, ctx: SystemContext) {
      // The known-bad knobs count down here, so they cannot outlive their hold and leak
      // into a later scenario the way a wave-3 knob did.
      const e = dbg.uDbgE.value;
      if (e.z > 0 && Number.isFinite(e.z)) {
        e.z -= dt;
        if (e.z <= 0) { e.set(0, 0, 0, 0); }
      }

      if (failures >= 3) return;
      timer -= dt;
      if (timer > 0 && !forceMeasure) return;
      forceMeasure = false;
      timer = SEP_PERIOD_S;

      const t0 = performance.now();
      const renderer = ctx.renderer;
      const camera = ctx.camera;
      let restored = true;
      const prevMode = dbg.uDbgB.value.y;
      const prevShadowAuto = renderer.shadowMap.autoUpdate;
      const prevTarget = renderer.getRenderTarget();
      try {
        renderer.getDrawingBufferSize(size);
        const W = size.x | 0;
        const H = size.y | 0;
        if (W < 64 || H < 64) return;

        // THE WHOLE FRAME, AT A STRIDE — not a crop.
        //
        // The first version of this read a 512x384 rectangle out of the middle of the
        // frame through `camera.setViewOffset`. It measured, on `vista`, "near 21519 px /
        // 1 class, far 0 px / 0 classes": the crop happened to land entirely on one
        // surface inside 100 m and contained no ground at all at 100-200 m, so the
        // instrument had nothing to compare and reported NOTHING — which in SUMMARY.md
        // prints as "declared but never observed", i.e. indistinguishable from an owner
        // who never wired it up. A metric that is silent exactly when the frame is
        // uniform is the worst possible failure mode for this particular defect, because
        // "the frame is uniform" IS the defect.
        //
        // So the attribution pass now covers the SAME frustum as the frame — no view
        // offset, no sub-rect, and therefore no possibility of the y-up/y-down mix-up that
        // a crop invites — rasterised at 1/SEP_STRIDE the linear resolution. Class pixel
        // (i, j) is canvas pixel (i*S + S/2, j*S + S/2) by construction. The canvas read
        // is the full drawing buffer, which costs a memcpy and no extra rasterisation.
        const S = SEP_STRIDE;
        const cw = Math.max(8, (W / S) | 0);
        const ch = Math.max(8, (H / S) | 0);
        if (!tile || bufW !== W || bufH !== H) {
          tile?.dispose();
          tile = new THREE.WebGLRenderTarget(cw, ch, {
            format: THREE.RGBAFormat,
            type: THREE.UnsignedByteType,
            depthBuffer: true,
            stencilBuffer: false,
          });
          beautyBuf = new Uint8Array(W * H * 4);
          classBuf = new Uint8Array(cw * ch * 4);
          bufW = W; bufH = H;
        }
        const beauty = beautyBuf!;
        const cls = classBuf!;

        // 1. THE FINISHED FRAME. post owns the canvas and has already drawn to it this
        //    frame; binding the default framebuffer and reading it is the same bytes the
        //    harness's screenshot contains.
        renderer.setRenderTarget(null);
        const glc = renderer.getContext();

        // READING THIS INSTRUMENT'S OWN COST HONESTLY, which is the same lesson
        // docs/AGENTS.md teaches about `post`: in headless ANGLE/SwiftShader
        // `renderer.render()` only QUEUES GL work, and the first read of the frame is what
        // makes the driver do it. Timed naively, this measurement billed 1241 ms — and
        // essentially all of it was the frame's own rasterisation coming due, work that
        // would have happened at composite time regardless. Three pieces in wave 2 read
        // exactly that kind of undivided number as one system's price and concluded post
        // cost 20 ms.
        //
        // So: a one-texel read forces the flush, and the clock for `sepAuditMs` starts
        // AFTER it. What that invariant reports is this instrument's MARGINAL cost — the
        // big readback, the attribution render and the arithmetic — which is the only part
        // a budget can meaningfully be set on. The flush is recorded separately and
        // reported, never hidden.
        const tFlush0 = performance.now();
        glc.readPixels(0, 0, 1, 1, glc.RGBA, glc.UNSIGNED_BYTE, oneTexel);
        const flushMs = performance.now() - tFlush0;
        const tMarginal = performance.now();

        glc.readPixels(0, 0, W, H, glc.RGBA, glc.UNSIGNED_BYTE, beauty);

        // 2. THE ATTRIBUTION, through the identical sub-frustum.
        // AN INSTRUMENT MUST NOT MOVE THE NUMBERS IT SHARES. `renderer.info` is where
        // `drawCalls` and `triangles` come from in every piece's SUMMARY.md, and an extra
        // scene render lands in it. The counters are snapshotted and put back, and
        // autoReset is off for the duration, so a measurement frame reports exactly what
        // it would have reported without one. (The camera piece is being sent back this
        // wave for the same class of mistake — `camera.pixelProbeMs` at 112 ms.)
        restored = false;
        const info = renderer.info;
        const prevAutoReset = info.autoReset;
        const iCalls = info.render.calls;
        const iTris = info.render.triangles;
        const iPts = info.render.points;
        const iLines = info.render.lines;
        info.autoReset = false;
        dbg.uDbgB.value.y = 10;
        renderer.shadowMap.autoUpdate = false;
        renderer.setRenderTarget(tile);
        renderer.render(ctx.scene, camera);
        renderer.readRenderTargetPixels(tile, 0, 0, cw, ch, cls);
        renderer.setRenderTarget(prevTarget);
        renderer.shadowMap.autoUpdate = prevShadowAuto;
        dbg.uDbgB.value.y = prevMode;
        info.render.calls = iCalls;
        info.render.triangles = iTris;
        info.render.points = iPts;
        info.render.lines = iLines;
        info.autoReset = prevAutoReset;
        restored = true;

        // 3. Attribute and accumulate.
        nR.fill(0); nG.fill(0); nB.fill(0); nN.fill(0);
        fR.fill(0); fG.fill(0); fB.fill(0); fN.fill(0);
        let terrainPx = 0;
        let ambiguousPx = 0;
        let alienPx = 0;
        const codeTol = DBG_CLASS_STEP * 255 * 0.3;
        for (let cj = 0; cj < ch; cj++) {
          for (let ci = 0; ci < cw; ci++) {
          const o = (cj * cw + ci) * 4;
          const ob = ((cj * S + (S >> 1)) * W + (ci * S + (S >> 1))) * 4;
          const cr = cls[o];
          const cb = cls[o + 2];
          // The checksum. Terrain writes B = 1 - R exactly, past the tone curve and past
          // the colour-space chunk, so the pair survives 8-bit quantisation to +-1. Every
          // other object in the scene draws into this buffer normally — this is what makes
          // them rejectable without touching anyone else's meshes or the scene graph.
          if (cr + cb < 253 || cr + cb > 257) { alienPx++; continue; }
          const idx = Math.round((cr / 255 - DBG_CLASS_BASE) / DBG_CLASS_STEP);
          // The classifier's own "this pixel is a coin toss" code. Counted rather than
          // silently dropped: "the attribution found nothing" and "the attribution found
          // terrain everywhere and could not name it" are different bugs with the same
          // symptom, and the first version of this census could not tell them apart.
          if (idx < 0 || idx > 6) { ambiguousPx++; continue; }
          if (Math.abs(cr - (DBG_CLASS_BASE + idx * DBG_CLASS_STEP) * 255) > codeTol) {
            alienPx++; continue;
          }
          terrainPx++;
          const d = (cls[o + 1] / 255) * DBG_CLASS_DIST_M;
          const lr = SRGB_LUT[beauty[ob]];
          const lg = SRGB_LUT[beauty[ob + 1]];
          const lb = SRGB_LUT[beauty[ob + 2]];
          if (d < SEP_NEAR_M) {
            nR[idx] += lr; nG[idx] += lg; nB[idx] += lb; nN[idx]++;
          } else if (d >= SEP_FAR_LO_M && d < SEP_FAR_HI_M) {
            fR[idx] += lr; fG[idx] += lg; fB[idx] += lb; fN[idx]++;
          }
          }
        }

        let nearTot = 0; for (let i = 0; i < 7; i++) nearTot += nN[i];
        let farTot = 0; for (let i = 0; i < 7; i++) farTot += fN[i];
        const near = stats(nR, nG, nB, nN, nearTot);
        const far = stats(fR, fG, fB, fN, farTot);

        // THE CENSUS, and it is reported whether or not there is anything to compare.
        //
        // `census` is the unfiltered count, so "rock is 0.3% of the near field" and "rock
        // is not in the frame" can be told apart. It goes in every `where` string below,
        // because the number a critic needs when the separation is zero is not the
        // separation — it is WHICH MATERIALS ARE THERE.
        const census = (n: Int32Array, tot: number): string => {
          if (tot <= 0) return 'none';
          const parts: string[] = [];
          for (let i = 0; i < 7; i++) if (n[i] > 0) parts.push(`${SEP_CLASS_NAMES[i]} ${((100 * n[i]) / tot).toFixed(1)}%`);
          return parts.join(' ');
        };
        const nearCensus = census(nN, nearTot);

        // The class count. Declared as an invariant of its own because the two below
        // compare the TOP TWO classes and therefore say nothing at all about a frame that
        // contains one — which is the exact state the shipping build was in, and which
        // SUMMARY.md printed as "declared but never observed (owner has not wired it up)".
        if (nearTot >= SEP_MIN_CLASS_PX) {
          NEAR_CLASS_N.observeWhere(near.length, `${nearTot}px near: ${nearCensus}`);
        }

        let nearDeltaL = NaN; let nearDeltaHue = NaN; let nearPair = '';
        if (near.length >= 2) {
          const a = near[0]; const b = near[1];
          nearDeltaL = Math.abs(a.L - b.L);
          nearPair = `${a.name}/${b.name}`;
          NEAR_SEP_L.observeWhere(nearDeltaL, `${nearPair} ${a.pixels}+${b.pixels}px`);
          if (a.C >= SEP_CHROMA_GATE && b.C >= SEP_CHROMA_GATE
              && a.pixels >= SEP_MIN_HUE_PX && b.pixels >= SEP_MIN_HUE_PX) {
            nearDeltaHue = hueDiff(a.hueDeg, b.hueDeg);
            NEAR_SEP_H.observeWhere(nearDeltaHue, `${nearPair} dL ${nearDeltaL.toFixed(3)}`);
          }
        } else if (nearTot >= SEP_MIN_CLASS_PX) {
          // ONE CLASS IS ZERO SEPARATION, and it must be recorded as zero rather than as
          // silence. A `min` invariant that is never observed is indistinguishable in the
          // report from one that was never wired up; a `min` invariant observed at 0.000 is
          // the loudest line in SUMMARY.md. That difference is the whole reason the round-1
          // build could ship a mountain made of one material with a clean audit sheet.
          nearDeltaL = 0; nearDeltaHue = 0;
          nearPair = near.length === 1 ? `${near[0].name} only` : 'no class over the floor';
          NEAR_SEP_L.observeWhere(0, `${nearPair} — ${nearTot}px near: ${nearCensus}`);
          NEAR_SEP_H.observeWhere(0, `${nearPair} — ${nearTot}px near: ${nearCensus}`);
        }

        let farHueSpread = NaN; let farPair = '';
        // A HUE claim needs more evidence than a VALUE claim: hue is the noisier of the
        // two on a small population, and the far band of an ordinary riding frame is
        // mostly timber and sky. 215 samples across two classes is not a mountain reading
        // as camouflage, it is a handful of pixels on a ridge edge.
        const chromatic = far.filter((s) => s.C >= SEP_CHROMA_GATE && s.pixels >= SEP_MIN_HUE_PX);
        if (chromatic.length >= 2) {
          farHueSpread = 0;
          for (let i = 0; i < chromatic.length; i++) {
            for (let j = i + 1; j < chromatic.length; j++) {
              const d = hueDiff(chromatic[i].hueDeg, chromatic[j].hueDeg);
              if (d > farHueSpread) {
                farHueSpread = d;
                farPair = `${chromatic[i].name}/${chromatic[j].name}`;
              }
            }
          }
          FAR_SPREAD_H.observeWhere(farHueSpread, `${farPair} over ${farTot}px`);
        } else if (farTot >= SEP_MIN_CLASS_PX) {
          // The far band is a MAX invariant, so one class genuinely passes it — a mid
          // distance made of one material is not a camouflage patchwork. It is still worth
          // recording, with the census, so that "the far field is calm" and "the far field
          // is empty" do not print identically. Observing the real 0 keeps the invariant
          // out of the "never observed" list without claiming anything false.
          FAR_SPREAD_H.observeWhere(0, `${far.length} chromatic cls of ${farTot}px: ${census(fN, farTot)}`);
        }

        if (wantImages) {
          wantImages = false;
          lastSepImages = {
            w: cw,
            h: ch,
            // Both buffers are GL-order (row 0 at the bottom), so they are flipped
            // identically here and the pairing is preserved. The beauty is point-sampled
            // at exactly the taps the statistics used, not resized — what you look at is
            // what was measured.
            beauty: dumpPng(beauty, W, H, cw, ch, S),
            cls: dumpPng(cls, cw, ch, cw, ch, 1),
          };
        }

        const costMs = performance.now() - tMarginal;
        // The `where` carries the census, because "never observed" and "observed and
        // clean" print almost the same in SUMMARY.md and mean opposite things. If the
        // attribution finds nothing, or a band holds only one class, this line says so.
        SEP_COST.observeWhere(
          costMs,
          `${cw}x${ch} attrib + ${W}x${H} read; flush ${flushMs.toFixed(0)}ms;`
          + ` terrain ${terrainPx} amb ${ambiguousPx} other ${alienPx} of ${cw * ch}px;`
          + ` near ${nearTot}px/${near.length}cls [${nearCensus}];`
          + ` far ${farTot}px/${far.length}cls [${census(fN, farTot)}]`,
        );
        lastSeparation = {
          terrainPx,
          tilePx: cw * ch,
          near,
          far,
          nearDeltaL,
          nearDeltaHue,
          nearPair,
          farHueSpread,
          farPair,
          nearAll: Array.from(nN),
          farAll: Array.from(fN),
          nearClasses: near.length,
          farClasses: far.length,
          costMs,
          flushMs,
        };
      } catch (err) {
        failures++;
        if (failures === 1) console.warn('[terrain-shading] separation audit disabled:', err);
      } finally {
        if (!restored) {
          try {
                renderer.setRenderTarget(prevTarget);
          } catch { /* the context is gone; nothing to restore into */ }
          renderer.shadowMap.autoUpdate = prevShadowAuto;
          dbg.uDbgB.value.y = prevMode;
        }
      }
    },
  });
}

export function createTerrainMaterial(): TerrainMaterialHandles {
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.9,
    metalness: 0.0,
  });

  // The relief library's own per-frame update — the sun, the sky/ground radiance and the
  // camera-motion relief cut, written once into a shared vec4[4] that every consumer
  // reads. Idempotent, so it does not matter whether another piece already installed it,
  // and it is what makes `E.motion` below the same number the rest of the game uses
  // instead of the private copy this file used to keep.
  installReliefSystem();

  const uniforms: Record<string, THREE.IUniform<number>> = {
    uTime: { value: 0 },
    uWetness: { value: TerrainShading.wetnessBase },
    uRelief: { value: TerrainShading.reliefHeight },
    uAmbAmt: { value: 1 },
    // Metres of footprint per metre of distance. Set in `update` from the live camera and
    // drawing buffer; the default is a 55 deg vertical FOV at 900 px, so a frame drawn
    // before the first update is only slightly wrong rather than black.
    uPxPerM: { value: (2 * Math.tan((55 * Math.PI) / 360)) / 900 },
  };
  // The A/B rig. Separate from the record above because these are vec4s, not floats.
  const dbg: Record<string, THREE.IUniform<THREE.Vector4>> = {
    uDbgA: { value: new THREE.Vector4(1, 1, 1, 1) },
    uDbgB: { value: new THREE.Vector4(1, 0, 1, 1) },
    uDbgC: { value: new THREE.Vector4(1, 1, 1, 1) },
    uDbgD: { value: new THREE.Vector4(1, 1, 1, 1) },
    /** The instrument's known-bad knobs. x = class collapse, y = the legacy far
     *  expansion, z = seconds left on the hold. Both self-expire; see `degrade()`. */
    uDbgE: { value: new THREE.Vector4(0, 0, 0, 0) },
  };

  // The separation instrument. Registered here rather than at module scope so it only
  // exists when a terrain material does, and before `initSystems` runs (main.ts:46 vs 146).
  installTerrainAudit(dbg);

  // THE KNOWN-BAD BUILDS, SELECTED FROM THE URL.
  //
  // `capture.mjs --url 'http://127.0.0.1:PORT/?degrade=collapse'` runs the whole shared
  // harness — same scenarios, same SUMMARY.md, same audit block, same screenshots —
  // against a build in which the defect is reconstructed on purpose. That is how this
  // round shows its instrument can fail, and it does it through the harness everybody
  // else reads rather than through a private probe nobody will re-run.
  //
  // It CANNOT leak: the only way to set it is a query string, a normal run has none, and
  // the hold is only infinite on this path (the hook's `degrade()` always expires).
  //   ?degrade=collapse   every ground class forced onto one colour
  //   ?degrade=legacy     the per-channel far expansion this round replaced
  if (typeof location !== 'undefined') {
    const m = /[?&]degrade=(collapse|legacy|both)/.exec(location.search);
    if (m) {
      dbg.uDbgE.value.set(
        m[1] === 'legacy' ? 0 : 1,
        m[1] === 'collapse' ? 0 : 1,
        Number.POSITIVE_INFINITY,
        1,
      );
      console.info(`[terrain-shading] KNOWN-BAD BUILD: degrade=${m[1]} — not a shipping frame`);
    }
  }

  material.onBeforeCompile = (shader: THREE.WebGLProgramParametersWithUniforms) => {
    // The shared light block, by reference: one write per frame reaches every material in
    // the scene that merged it.
    mergeReliefUniforms(shader);
    // The palette's two shared uniforms. Shared INSTANCES — whoever calls
    // `updatePalette(env)` each frame (sky/post own the environment) updates every
    // material that merged them at once. If nobody calls it they hold their authored
    // defaults, which is what `palFarSeparate` needs and all this file reads.
    mergePaletteUniforms(shader);
    shader.uniforms.uTime = uniforms.uTime;
    shader.uniforms.uWetness = uniforms.uWetness;
    shader.uniforms.uRelief = uniforms.uRelief;
    shader.uniforms.uAmbAmt = uniforms.uAmbAmt;
    shader.uniforms.uPxPerM = uniforms.uPxPerM;
    shader.uniforms.uDbgA = dbg.uDbgA;
    shader.uniforms.uDbgB = dbg.uDbgB;
    shader.uniforms.uDbgC = dbg.uDbgC;
    shader.uniforms.uDbgD = dbg.uDbgD;
    shader.uniforms.uDbgE = dbg.uDbgE;

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vWPos;
varying vec3 vWNrm;
varying float vWDist;`,
      )
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
{
  vec4 gWorld = modelMatrix * vec4(transformed, 1.0);
  vWPos = gWorld.xyz;
  // The clipmap writes the analytic terrain normal, so this is exact, not a mesh average.
  vWNrm = normalize(mat3(modelMatrix) * objectNormal);
  vWDist = length(gWorld.xyz - cameraPosition);
}`,
      );

    shader.fragmentShader = shader.fragmentShader
      // The library, then the three channels its patches read back out, then our own
      // helpers — which now alias the library's hash and noise rather than duplicating
      // them, so the order matters. Varyings are off: we have our own, computed from the
      // clipmap's exact analytic normal, and terrain is neither instanced nor skinned so
      // there is nothing the library's version would buy.
      .replace(
        '#include <common>',
        // The palette authority first — `palFarSeparate` and the ladder defines are used
        // by TERRAIN_SHADE, and its two uniforms are shared instances merged below.
        `#include <common>\n${paletteGLSL()}\n${reliefGLSL('fragment', false)}\n`
        + `${RELIEF_STD_GLOBALS}\n${TERRAIN_COMMON}`,
      )
      .replace('#include <color_fragment>', TERRAIN_SHADE)
      .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = gRlfRough;')
      .replace(
        '#include <normal_fragment_maps>',
        // Our bump is built in world space; three shades in view space.
        'normal = normalize((viewMatrix * vec4(gRlfNormalW, 0.0)).xyz);',
      )
      // Occlusion on the irradiance, which is the whole point of having it at all:
      // indirectDiffuse in full, directDiffuse at 65% because a crease still sees the sun
      // until geometry actually blocks it. This chunk sits after <lights_fragment_end>,
      // so it is downstream of sky.ts's analytic terrain self-shadow and of the shadow
      // map, and it composes with both rather than replacing either.
      //
      // Then our own line, and it is one line for a reason: the sky term is the whole of
      // this round. relief.ts's patch is an occlusion — clamped at 1, and spent 65% on
      // the direct term as well — so it cannot express "this facet sees MORE sky than the
      // ground it stands on", which is half of what a shaded dome looks like. gTerrAmb is
      // signed, coloured, and lands on the ambient alone, which is what makes it
      // self-gating: a few per cent of a sunlit pixel, the entire read of a shaded one.
      .replace(
        '#include <aomap_fragment>',
        `${RELIEF_STD_AO_PATCH}
reflectedLight.indirectDiffuse *= gTerrAmb * mix( 1.0, gTerrLand, 0.35 );
reflectedLight.directDiffuse *= gTerrLand;`,
      )
      // The false-colour dump goes here, at the very last chunk, so it is downstream of
      // the lighting, of sky.ts's aerial perspective and of ACES — which is the whole
      // point: those three are exactly what stands between a claim about this shader's
      // output and what a capture shows. Costs one uniform compare on the shipped path.
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>
if ( uDbgB.y > 0.5 ) gl_FragColor = vec4( gTerrDbg, 1.0 );`,
      );
  };

  // Keep this stable so three does not think two terrain materials are different programs.
  material.customProgramCacheKey = () => 'grip-terrain-v7';

  // Authoring/diagnostic hook, same pattern as sky's and post's. The only thing behind it
  // is the A/B uniform above; it is what lets a capture measure this round's change
  // against its own baseline in one page session rather than across two builds of a tree
  // nine agents are editing.
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__gripTerrainShading = {
      ambient: (a: number) => { uniforms.uAmbAmt.value = Math.max(0, Math.min(1, a)); },
      /** Layer A/B, all 0..1, all default 1: block clasts, cliff bands, cover, drainage. */
      layers: (block = 1, bands = 1, cover = 1, drain = 1) => {
        dbg.uDbgA.value.set(block, bands, cover, drain);
      },
      /** Far form fields (meso + coarse + ridge), 0..1, default 1. */
      form: (a = 1) => { dbg.uDbgB.value.x = a; },
      /** Bedding-plane face gate, and the landform-scale talus cover. 0..1, default 1. */
      far: (strat = 1, mineral = 1) => {
        dbg.uDbgB.value.z = strat;
        dbg.uDbgB.value.w = mineral;
      },
      /** 0 off, 1 surface weights, 2 raw albedo, 3 N.sun, 4 distance rings,
       *  5 normal tilt deg, 6 far-layer amounts, 7 the landform light channel,
       *  8 the heightfield's own N.sun, 9 the clast object channels (r crease,
       *  g crown+body, b cast shadow). */
      view: (m = 0) => { dbg.uDbgB.value.y = m; },
      /**
       * THE WHOLE CLAST FIELD, and its ring on its own.
       *
       * `layers()` above can only switch the block layer, which is why three rounds of A/B
       * could not separate a block ring from a stone ring on the same piece of tread. This
       * switches all four layers at once (`all`) and, independently, only the crease rings
       * (`rim`) — so "is the mark I am looking at an object or the outline of one" is one
       * capture rather than one build.
       * @param all 0..1 every clast layer, every cue. Default 1.
       * @param rim 0..1 the base crease ring alone. Default 1.
       */
      clasts: (all = 1, rim = 1) => {
        dbg.uDbgC.value.z = all;
        dbg.uDbgC.value.w = rim;
      },
      /**
       * Round 8's two changes, A/B-able inside one page session.
       * @param land 0..1 the landform light channel (`gTerrLand`)
       * @param sep  0..1 the far-field material separation
       */
      far2: (land = 1, sep = 1) => { dbg.uDbgC.value.set(land, sep, 1, 1); },
      /**
       * Round 3's three changes, A/B-able inside one page session.
       * @param grit 0..1 hardpack's clay-dusted grit tints (0 = round 2's neutral greys)
       * @param ring 0..1 the crease ring's super-linear entitlement (0 = linear)
       * @param mass 0..1 the 300 m mass band
       */
      r3: (grit = 1, ring = 1, mass = 1) => { dbg.uDbgD.value.set(grit, ring, mass, dbg.uDbgD.value.w); },
      /**
       * WAVE 5 R2's near-field material: the mineral lag, on its own.
       *
       * It is the whole of this round's answer to "the near field is one class", so it has
       * to be switchable in one page session — a claim about a layer nobody can turn off
       * is an argument, not a measurement. 0..1, default 1.
       */
      lag: (a = 1) => { dbg.uDbgD.value.w = Math.max(0, Math.min(1, a)); },
      /**
       * THE INSTRUMENT'S KNOWN-BAD CASES. Both default off and both SELF-EXPIRE.
       *
       * `docs/AGENTS.md` records a wave-3 round in which a debug knob missing from
       * capture.mjs's per-scenario reset block leaked into every scenario after it and
       * false-colour dumps were filed as beauty frames. These two cannot do that: they
       * count down in `update()` and restore themselves, so the worst case is that a few
       * seconds of one scenario are degraded, not that the rest of the session is.
       *
       * @param collapse  0..1 — force every ground class onto ONE colour while the
       *                  classifier still separates them. Reconstructs the near-field
       *                  defect exactly, so `nearClassSeparation*` can be watched failing.
       * @param legacyFar 0/1 — restore the per-channel far expansion that produced 218
       *                  degrees of hue spread, so `farHueSpread` can be too.
       * @param holdS     seconds to hold, default 8, capped at 30.
       */
      degrade: (collapse = 0, legacyFar = 0, holdS = 8) => {
        dbg.uDbgE.value.set(
          Math.max(0, Math.min(1, collapse)),
          legacyFar > 0.5 ? 1 : 0,
          Math.max(0.5, Math.min(30, holdS)),
          0,
        );
      },
      /** The last separation measurement, or null if none has been taken yet. */
      separation: () => lastSeparation,
      /** Ask the next measurement to keep its two buffers; then read them here. The pair
       *  is how a registration error is caught by eye instead of by argument. */
      sepImages: (arm = true) => { if (arm) { wantImages = true; return null; } return lastSepImages; },
      /** Take a separation measurement on the NEXT drawn frame instead of waiting for the
       *  duty cycle. A probe that has to burn 44 frames per data point is a probe nobody
       *  runs twice, and this piece needs several rounds of measurement per session. */
      sepNow: () => { forceMeasure = true; },
    };
  }

  /** < 0 means "follow the weather"; >= 0 is an explicit override from setWetness(). */
  let wetOverride = -1;

  return {
    material,

    update(_dt: number, camera: THREE.Camera, time: number) {
      uniforms.uTime.value = time;

      // The smooth footprint scale: metres of texel per metre of view distance.
      //
      // 2*tan(fovY/2)/height is the standard pinhole result, and the only reason it is a
      // uniform rather than dFdx(wp) is the lattice — see the block at `gBandLodS`. It has
      // to be recomputed every frame because the camera piece opens the FOV with speed,
      // and a relief fade computed against last second's FOV is a fade against the wrong
      // resolution. The height is read off the canvas rather than assumed, so a resize
      // does not silently change how much relief the ground carries.
      const cam = camera as THREE.PerspectiveCamera;
      if (cam.isPerspectiveCamera) {
        const h = typeof window !== 'undefined'
          ? Math.max(1, (window.innerHeight || 900) * (window.devicePixelRatio || 1))
          : 900;
        uniforms.uPxPerM.value = (2 * Math.tan((cam.fov * Math.PI) / 360)) / h;
      }

      // Weather drives the ground. The mapping is base..1 rather than 0..1 so the clear
      // preset (Weather.wetness === 0) lands on the calibrated dry look above instead of
      // on bone dry, and full rain still reaches saturation.
      const src = wetOverride >= 0 ? wetOverride : Weather.wetness;
      const base = TerrainShading.wetnessBase;
      uniforms.uWetness.value = base + (1 - base) * Math.max(0, Math.min(1, src));

      // The camera-speed relief cut used to be tracked here, with its own prevCam, its own
      // smoothing and its own two constants. It is now `E.motion` out of the shared relief
      // block — same policy, same numbers (ReliefTuning.motionCut is 0.22 at 24 m/s,
      // lifted from what used to be here), computed once for the whole game by the system
      // installReliefSystem() registers.
    },

    setWetness(w: number) {
      wetOverride = Math.max(0, Math.min(1, w));
    },

    setAmbient(amount: number) {
      uniforms.uAmbAmt.value = Math.max(0, Math.min(1, amount));
    },

    clearWetness() {
      wetOverride = -1;
    },
  };
}
